import * as THREE from "three";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { AudioEngine, RailTimbre, RailVoiceInput, SimAudioSink, SUSTAIN_TIMBRES, Timbre, effectivePitchIndex, isKitTimbre, noteNameForPitchIndex, timbreIds, timbreIndexById, timbreIdByIndex, isPentatonic, snapToPentatonic, snapToScale, railTimbreIndexById, railTimbreIdByIndex } from "./audio";
import { ONSET_DIAG, summarizeOnsetDiag, resetOnsetDiag } from "./onsetDiag";
import { isTitleActive, attachTitleAudio, runTitleScreen, preloadTitleFont } from "./title";
import { shouldSuppressTitle, markSkipTitleOnce, consumeSkipTitleOnce } from "./titleGate";
import { showLanding, removeLanding, applyLandingLanguage, isLandingActive } from "./landing";
import { micStore, MicManifestEntry } from "./micStore";
import { performanceCycleSec } from "./clock";
import { exportLoopVideo } from "./videoExport";
import {
  BLUR_PAUSE_DELAY_MS,
  CAMERA_FIT_ASPECT_THRESHOLD,
  CAMERA_FIT_DETECT_NDC,
  DECAY_DEFAULT_S,
  DELAY_DEFAULT_S,
  ROUTER_HOLD_DEFAULT_S,
  DEFAULT_FIELD_FADE,
  ENVELOPE_ATTACK_MAX_S,
  ENVELOPE_DECAY_MAX_S,
  ENVELOPE_RELEASE_MAX_S,
  EMIT_RATE_MAX,
  EMIT_RATE_MIN,
  FIELD_K_MAX,
  FIELD_OMEGA_MAX,
  FIELD_RADIUS_MAX,
  FIELD_RADIUS_MIN,
  FIELD_STRENGTH_MAX,
  EMIT_DRAG_MAX,
  IMPACT_MAX,
  IMPACT_REF,
  MASS_MAX,
  MAX_OBJECTS,
  LAMP_DEFAULT_HUE,
  LAMP_DEFAULT_SAT,
  SELECTION_FRAME_COLOR,
  PITCH_INDEX_MIN,
  PITCH_INDEX_MAX,
  DEFAULT_PITCH_INDEX,
  KNOB_DEFAULT_VALUE,
  KNOB_MIN_DEFAULT,
  KNOB_MAX_DEFAULT,
  KNOB_INT_DEFAULT_MAX,
  TRANSPOSE_RANGE,
  GRAVITY_Y_MIN,
  CLOCK_BPM_MIN,
  CLOCK_BPM_MAX,
  WIRING_TARGET_COLOR,
} from "./constants";
import {
  createCollisionUniforms,
  CURVE_DEFAULT_CYCLE,
  defaultOrbitParams,
  defaultParams,
  defaultSeq,
  ObjectField,
  ObjectTemplate,
  ObjectType,
  OrbitParams,
  PlacedObject,
  ButtonMode,
  TimerMode,
  ValueOperator,
  snapKnobValue,
} from "./objects";
import { clampSeqNum, defaultEmitter, Emitter, EmitterField, EmitterParams, Meter, sequencerNums } from "./emitters";
import { emitterTarget, Gizmo, groupBoundingRadiusFromMesh, objectTarget } from "./gizmo";
import { ParticleSystem } from "./particles";
import {
  BloomMode,
  CameraPose,
  createBackgroundWall,
  createScene,
  DebugRenderState,
  RENDER_PHASE_BLOOM,
  RENDER_PHASE_POST,
  RENDER_PHASE_SCENE,
  ViewMode,
  WALL_DEFAULT_PARAMS,
  wallParamsToPreset,
  WallParams,
} from "./scene";
import { remapSceneIds } from "./sceneSerialize";
import { estimateVramBytes, formatVramMb } from "./gpuMemory";
import { StatsUI } from "./stats";
import {
  applySceneSettings,
  deleteSlot,
  FEATURE_CATEGORIES,
  FeatureCategory,
  hasSavedScene,
  loadConnections,
  loadEmitters,
  loadEnv,
  loadObjects,
  loadSettings,
  loadSlots,
  loadThumb,
  parseWallParams,
  saveConnections,
  saveEmitters,
  saveEnv,
  saveObjects,
  savePendingCamera,
  saveSettings,
  saveSlot,
  saveThumb,
  saveMicImport,
  loadMicImport,
  saveProvenance,
  loadProvenance,
  sceneSettings,
  roomSyncSettings,
  takePendingCamera,
  setBenchSceneOverride,
  SlotEntry,
} from "./settings";
import { BenchController, BenchSample } from "./bench";
import { EditSnap } from "./snap";
import { ConnectionGraph, PortId, pruneLegacyCountValueIn } from "./connections";
import { allowedPitchClasses, pitchByDegreeIndex } from "./pitchScale";
import { WidgetInput } from "./widgetInput";
import { SignalEvaluator } from "./signals";
import { buildGroupExpressions, ExprLine } from "./exprBuilder";
import { SfxManager } from "./sfx";
import { AppContext, NumericParam, Tool } from "./appContext";
import { FrameRequest, SelectionFrames } from "./selectionFrame";
import { ContextTarget, OPERATOR_LABELS, setupToolbar, UploadFieldSummary } from "./ui/controls";
import { createRadialMenu, RadialItem, RadialMenuApi } from "./ui/radialMenu";
import { setupTutorial } from "./ui/tutorial";
import { initPitchLabels } from "./ui/pitchLabels";
import { initPitchButtons } from "./ui/pitchButtons";
import { initPanelLayout } from "./ui/panelLayout";
import { initPorts } from "./ui/ports";
import { initTransformGraph } from "./transformGraph";
import { Clipboard, buildClipboard } from "./clipboard";
import { initEntityIndex } from "./entityIndex";
import { BulkKind, collectGroupBulkTypes, GroupBulkDeps, GroupBulkType } from "./groupBulkParams";
import { initSignalRuntime } from "./signalRuntime";
import { initUndoStack } from "./undoStack";
import { connectRoom, InterpTarget, SceneSerialized } from "./net/roomClient";
import { CounterSync } from "./net/counterSync";
import { ActivityKind, AD_TOKEN_STUB, DEFAULT_ROOM_CAPACITY, DirectoryList, EMOTES, KICK_MIN_STAY_MS, MAX_ROOM_CAPACITY, MIN_ROOM_CAPACITY, Op, PUBLIC_ROOM_CAP, RoomDescriptor, RoomSceneMeta, ROOM_COLORS, SnapshotMsg } from "./net/protocol";
import { anonymousIdSuffix, avatarFor } from "./avatar";
import { initActivityLog } from "./ui/activityLog";
import { FeedConsumer, FeedCoreApi, FeedCoreItem, FeedLoadResult, FeedSceneResult, openFeed } from "./ui/feedCore";
import { initSequencerPanel } from "./ui/sequencerPanel";
import { showActionModal, showUploadSuccessModal } from "./ui/modal";
import { initToolSpawn, isEmitterTool } from "./toolSpawn";
import { initTestPlaceApi } from "./testPlaceApi";
import { initNodeLabels } from "./ui/nodeLabels";
import { AvatarTarget, initPlayerAvatars } from "./ui/playerAvatars";
import { initPointer, TouchPlayRailInput } from "./pointer";
import { getLanguage, Language, setLanguage, t } from "./i18n";
import { AdaptiveQuality } from "./adaptive-quality";
import { addBookmark, addCategory, authStatus, AuthStatus, BookmarkCategory, BookmarkItem, bookmarkedSceneIds, deleteCategory, deleteScene, fetchScene, flushPlays, hasLiked, incrementPlay, likedSceneIds, queuePlay, likeScene, listBookmarks, listCategories, deleteMicAsset, listMicAssets, listMicHashes, listScenes, MicAsset, MicAssetInput, micPublicUrl, micStoragePath, r2Configured, removeBookmark, renameCategory, SceneQuery, myUid, myAccessToken, ensureRoomUid, isRealAccount, reportScene, SceneListItem, setBookmarkCategory, setDisplayName, applySceneTagOps, SceneTagOps, TagMutationResult, shareEnabled, signInWithGoogle, signOut, submitFeedback, unlikeScene, uploadMicAssets, uploadScene, UPLOAD_TIMEOUT_MS, UploadResult } from "./share";
import { MULTIPLAYER_ENABLED, NODE_GATED_TOOLS } from "./featureGate";
import { initTelemetry, track } from "./telemetry";


const ROOM_INTERP_TAU_S = 0.06;
const ROOM_RESET_FAILSAFE_MS = 1000;
const EMOTE_WINDOW_MS = 15000;
const EMOTE_WINDOW_MAX = 10;
const ACTIVITY_DEBOUNCE_MS = 600;
const KICK_EJECT_ICON = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 17l5-5-5-5M21 12H9M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/></svg>`;

declare global {
  interface Window {
    __room?: {
      uid(): string;
      claims(): Record<string, string>;
      colors(): Record<string, number>;
      selectedId(): string | null;
      foreignSelected(): boolean;
      dragStep(id: string, x: number, y: number, z: number): void;
      emitterDragStep(id: string, x: number, y: number, z: number): void;
      pokeKnob(id: string, value: number): void;
      paramValue(id: string, name: string): number | null;
      dragEnd(id: string): void;
      meshPos(id: string): [number, number, number] | null;
      colliderPos(id: string): [number, number, number] | null;
      emitterOriginPos(id: string): [number, number, number] | null;
      seqValues(id: string): number[] | null;
      button(id: string, phase: "press" | "release"): void;
      kick(targetUid: string): void;
      isHeld(id: string): boolean;
      clockTimeSec(): number;
      lampLevel(id: string): number | null;
      semiSelectedId(): string | null;
      countOwner(id: string): string | null;
      screenOf(id: string): [number, number] | null;
    };
  }
}

function main(): void {
  initTelemetry();
  const canvas = document.querySelector<HTMLCanvasElement>("#app");
  if (!canvas) {
    throw new Error("#app canvas not found");
  }

  document.addEventListener("contextmenu", (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target && target.closest('input[type="text"], .share-status, .account-name')) {
      return;
    }
    e.preventDefault();
  });

  document.addEventListener("gesturestart", (e: Event): void => {
    e.preventDefault();
  });

  const bloomParam = new URLSearchParams(location.search).get("bloom");
  const initialBloomMode: BloomMode =
    bloomParam === "off" || bloomParam === "quarter" || bloomParam === "half"
      ? bloomParam
      : "half";

  const { renderer, scene, rig, grid, resize, render, setFeedActive, setFeedRect, setDebugRender, setDprCap1, setBloomMode, setPhaseTimer, setShadowMapSize, setKeyLightDir } = createScene(canvas, initialBloomMode);
  const selectionFrames = new SelectionFrames();

  const css2dRenderer = new CSS2DRenderer();
  css2dRenderer.setSize(window.innerWidth, window.innerHeight);
  css2dRenderer.domElement.style.position = "fixed";
  css2dRenderer.domElement.style.top = "0";
  css2dRenderer.domElement.style.left = "0";
  css2dRenderer.domElement.style.pointerEvents = "none";
  document.body.appendChild(css2dRenderer.domElement);

  const emoteRadial = createRadialMenu({
    suspendControls: (): void => rig.suspendControls(),
    resumeControls: (): void => rig.resumeControls(),
    overlayClass: "radial-emote-menu",
    omitRootCancel: true,
    keepOpenOnSelect: true,
    itemsDisabled: (): boolean => emoteRateLimited(),
    limitProgress: (): number | null => emoteLimitProgress(),
  });
  emoteRadial.setRoot(
    EMOTES.map(
      (emoji: string): RadialItem => ({
        key: emoji,
        label: emoji,
        onSelect: (): void => sendEmote(emoji),
      }),
    ),
  );
  const kickRadial = createRadialMenu({
    suspendControls: (): void => rig.suspendControls(),
    resumeControls: (): void => rig.resumeControls(),
    overlayClass: "radial-kick-menu",
  });
  const playerAvatars = initPlayerAvatars(
    (clientX: number, clientY: number, pointerId?: number): void => {
      if (roomActive) {
        emoteRadial.open(clientX, clientY, pointerId);
      }
    },
    (uid: string, clientX: number, clientY: number): void => openKickRadial(uid, clientX, clientY),
  );
  const activityLog = initActivityLog();

  const stats = new StatsUI(renderer);
  renderer.info.autoReset = false;
  const collisionUniforms = createCollisionUniforms();
  const particles = new ParticleSystem(renderer, collisionUniforms);
  scene.add(particles.points);

  if (new URLSearchParams(location.search).has("debug")) {
    (window as unknown as { __gridAB: (steps?: number) => { grid: string; brute: string; match: boolean } }).__gridAB = (
      steps: number = 240,
    ): { grid: string; brute: string; match: boolean } => {
      particles.setUseGrid(true);
      const grid = particles.debugSimChecksum(steps);
      particles.setUseGrid(false);
      const brute = particles.debugSimChecksum(steps);
      particles.setUseGrid(true);
      return { grid, brute, match: grid === brute };
    };
  }

  if (ONSET_DIAG) {
    (window as unknown as { __onsetDiag: (minFires?: number) => unknown }).__onsetDiag = (
      minFires: number = 8,
    ): unknown => summarizeOnsetDiag(minFires);
    (window as unknown as { __onsetDiagReset: () => void }).__onsetDiagReset = (): void => resetOnsetDiag();
  }

  const field = new ObjectField(collisionUniforms);
  scene.add(field.group);

  const emitterField = new EmitterField();
  scene.add(emitterField.group);

  const gizmo = new Gizmo();
  scene.add(gizmo.group);
  selectionFrames.setResolution(window.innerWidth, window.innerHeight);
  gizmo.setResolution(window.innerWidth, window.innerHeight);

  const backgroundWall = createBackgroundWall();
  scene.add(backgroundWall.mesh);
  scene.add(backgroundWall.boundsMask);

  const audio = new AudioEngine();
  attachTitleAudio(audio);
  audio.setMicSource(micStore);
  void micStore
    .attach((data: ArrayBuffer): Promise<AudioBuffer> => audio.decodeAudio(data))
    .then(async (): Promise<void> => {
      const storedProv = loadProvenance();
      const prov = storedProv === null ? null : provenanceFromStored(storedProv);
      if (prov !== null) {
        setProvenance(prov);
      }
      await refreshMyUid();
      const imported = loadMicImport();
      if (imported !== null) {
        await applySceneMic(imported.manifest, imported.owner);
      }
    })
    .catch((e: unknown): void => {
      console.error("mic store init failed", e);
    });
  if (new URLSearchParams(location.search).get("worklet") !== "0") {
    void audio.enableSynthWorklet().catch((e: unknown): void => {
      console.error("synth worklet init failed; using native synth voices", e);
    });
  }

  /**
   * The engine node-driven global params write into: the realtime engine
   * normally, swapped to the offline engine for the duration of a loop-video
   * export so a node graph wired to e.g. `sustain piano` shapes the exported
   * audio instead of mutating the silent realtime engine.
   */
  let activeAudio: AudioEngine = audio;
  const sfx = new SfxManager();

  const graph = new ConnectionGraph();
  const evaluator = new SignalEvaluator();
  const widgets = new WidgetInput();
  /**
   * Pre-override base value of each currently driven `${entityId}|${paramName}`.
   * A Param-in edge overrides its target param live (the model field holds the
   * driven value so every consumer sees it), but the original is captured here on
   * the first driven frame and restored when the param stops being driven, and is
   * substituted back in for persistence (via withParamBase) — so a Param-in is a
   * temporary override, not a destructive write: disconnect / reload restore base.
   */
  const paramBase = new Map<string, number>();
  let serializingScene = false;
  let paramPanelDirty = false;
  let clearEmitInjection = (): void => {};

  const settings = loadSettings();
  setKeyLightDir(settings.wallLightX, settings.wallLightY);
  setShadowMapSize(settings.wallShadowRes);
  setLanguage(settings.language);
  document.documentElement.lang = settings.language;
  settings.features = { ...settings.features, physics: false, "3d": false };
  if (!settings.features["3d"] && settings.viewMode === "3d") {
    settings.viewMode = "2d";
  }
  if (settings.objectMaterial === "glass") {
    settings.objectMaterial = "glossy";
  }
  let roomNotify: () => void = (): void => {};
  let roomActive = false;
  let roomConnecting = false;
  let roomStopOnBlurBeforeJoin: boolean | null = null;
  let activeRoomId: string | null = null;
  let roomUidOverride: string | null = null;
  let roomClaimSend: (id: string) => void = (): void => {};
  let roomReleaseSend: (id: string) => void = (): void => {};
  let roomResetSend: () => void = (): void => {};
  let roomEmoteSend: (emoji: string) => void = (): void => {};
  let roomKickSend: (target: string) => void = (): void => {};
  let roomKickDoneSend: () => void = (): void => {};
  let roomActivitySend: (kind: ActivityKind, detail?: string) => void = (): void => {};
  let localRoomJoinAt = 0;
  let activitySettingsBaseline: { tempoBpm: number; gravityY: number; simpleMode: boolean; features: string; wall: string } | null = null;
  const activityTimers = new Map<ActivityKind, number>();
  const activityDetails = new Map<ActivityKind, string>();
  let suppressActivityEmit = false;
  let roomButtonSend: (id: string, phase: "press" | "release") => void = (): void => {};
  let roomCountSend: (id: string, value: number) => void = (): void => {};
  let roomCountOwnerSend: (id: string) => void = (): void => {};
  const emoteTimes: number[] = [];
  let emoteLockStart = 0;
  let emoteLockUntil = 0;
  let resetFailsafe: number | null = null;
  let roomClientClose: () => void = (): void => {};
  let surfReturnScene: Record<string, unknown> | null = null;
  let surfReturnProvenance: Provenance | null = null;
  let lastClaimedId: string | null = null;
  let semiSelectedId: string | null = null;
  const roomColors = new Map<string, number>();
  const roomClaims = new Map<string, string>();
  const roomNames = new Map<string, string>();
  let roomOwnerUid = "";
  let roomIsPublic = false;
  let roomNameCached: string | null = null;
  let roomCapacityCached: number | null = null;
  let roomSceneFromSnapshot = false;
  function forceRoomStopOnBlurOff(): void {
    if (roomStopOnBlurBeforeJoin !== null) {
      return;
    }
    roomStopOnBlurBeforeJoin = settings.stopOnBlur;
    settings.stopOnBlur = false;
  }
  function restoreRoomStopOnBlur(): void {
    if (roomStopOnBlurBeforeJoin === null) {
      return;
    }
    settings.stopOnBlur = roomStopOnBlurBeforeJoin;
    roomStopOnBlurBeforeJoin = null;
  }
  const counterSync = new CounterSync({
    myUid: (): string => myRoomUid(),
    sendCount: (id: string, value: number): void => roomCountSend(id, value),
    isPresent: (uid: string): boolean => roomColors.has(uid),
    now: (): number => performance.now(),
    onOwnershipChange: (): void => onCounterOwnershipChange(),
  });
  function onCounterOwnershipChange(): void {
    refreshSelectionFrames();
    paramPanelDirty = true;
  }
  const persist = (): void => {
    saveSettings(settings);
    roomNotify();
    noteLocalSettingsChange();
  };
  persist();
  const persistObjects = (): void => {
    saveObjects(withParamBase((): unknown => field.serialize()));
    roomNotify();
  };
  const persistConnections = (): void => {
    saveConnections(graph.serialize());
    syncEnvelopeEngine();
    roomNotify();
  };

  emitterField.restore(loadEmitters());

  function syncEmitters(): void {
    particles.setEmitters(emitterField.list());
    saveEmitters(withParamBase((): unknown => emitterField.serialize()));
    sequencerPanel?.rebuild();
    roomNotify();
  }

  const spawn: ObjectTemplate = {
    type: settings.shape,
    restitution: settings.bounce,
    friction: settings.friction,
    mass: settings.mass,
    massEnabled: false,
    killOnContact: false,
    collisionEnabled: true,
    rotation: new THREE.Quaternion(),
    pitchIndex: DEFAULT_PITCH_INDEX,
    timbreId: "bell",
    timbreIds: ["bell"],
    volume: 1,
    muted: false,
    fade: DEFAULT_FIELD_FADE,
    operator: "add",
    operand: 0,
    decayS: DECAY_DEFAULT_S,
    delayS: DELAY_DEFAULT_S,
    routerHoldS: ROUTER_HOLD_DEFAULT_S,
    routerHoldResetOnPulse: false,
    timerMode: "loop",
    knobValue: KNOB_DEFAULT_VALUE,
    knobMin: KNOB_MIN_DEFAULT,
    knobMax: KNOB_MAX_DEFAULT,
    knobInt: false,
    buttonMode: "push",
    buttonPulseOnReset: false,
    lampHue: LAMP_DEFAULT_HUE,
    lampSat: LAMP_DEFAULT_SAT,
    lampValue: 0,
    ...defaultSeq(settings.shape),
    seqCycleLength: CURVE_DEFAULT_CYCLE,
    params: defaultParams(settings.shape),
    scale: new THREE.Vector2(1, 1),
    arcDeg: 360,
    orbit: defaultOrbitParams(),
    railPoints: [],
    pitchIndexEnd: DEFAULT_PITCH_INDEX + 12,
    railTimbre: "sine",
  };

  const emitterSeed = emitterField.list()[0] ?? defaultEmitter();
  const emitterSpawn: EmitterParams = {
    rate: emitterSeed.rate,
    rotation: emitterSeed.rotation.clone(),
    coneDeg: emitterSeed.coneDeg,
    speedMin: emitterSeed.speedMin,
    speedMax: emitterSeed.speedMax,
    lifeMin: emitterSeed.lifeMin,
    lifeMax: emitterSeed.lifeMax,
    offset: emitterSeed.offset,
    bounce: emitterSeed.bounce,
    friction: emitterSeed.friction,
    drag: emitterSeed.drag,
    clockSync: emitterSeed.clockSync,
    seqMode: emitterSeed.seqMode,
    meter: emitterSeed.meter,
    num: emitterSeed.num,
    seqSteps: [...emitterSeed.seqSteps],
    kind: emitterSeed.kind,
    pulseSpeedFromSettings: emitterSeed.pulseSpeedFromSettings,
    gate: 1,
    name: "",
  };

  const ctx: AppContext = {
    scene,
    rig,
    selectionFrames,
    particles,
    field,
    emitterField,
    gizmo,
    audio,
    sfx,
    graph,
    evaluator,
    widgets,
    settings,
    spawn,
    emitterSpawn,
  };

  const pitchLabels = initPitchLabels(ctx);

  const undoApi = initUndoStack(ctx, {
    withParamBase,
    syncAudioFromObjects: syncTimbreEngine,
    captureSettings,
    applySettings,
    paramBase,
    objectById: (id: string): PlacedObject | null => objectById(id),
    hasClipboard: (): boolean => clipboard !== null,
    setUndoRedoEnabled: (canUndo: boolean, canRedo: boolean): void => toolbar.setUndoRedoEnabled(canUndo, canRedo),
    setCopyPasteEnabled: (canCopy: boolean, canPaste: boolean): void => toolbar.setCopyPasteEnabled(canCopy, canPaste),
    applyFeatureGates,
    reconcileOrbitClockSync,
    bindPanel,
    persistObjects,
    persistConnections,
    syncEmitters,
    clearEmitInjection: (): void => clearEmitInjection(),
    resetSignalState: (): void => evaluator.resetRuntimeState(),
    primeSignalState: (): void => primeSignalState(),
    getSimpleMode: (): boolean => settings.simpleMode,
    setSimpleMode: (on: boolean): void => {
      if (on && !settings.objectPlacement) {
        clearTouchPlay();
      }
      settings.simpleMode = on;
      toolbar.setSimpleChecked(on);
      persist();
      applySimpleMode();
    },
  });
  const {
    snapshot,
    syncCopyPasteButtons,
    pushUndo,
    undo,
    redo,
    discardLastUndo,
    recordBefore,
    runSuppressed,
    reset: resetUndo,
    resetEditGesture,
    beginEdit,
    editObject,
    pruneValuePorts,
    setNodeOperand,
    editEmitter,
    editEmitterSeqSteps,
  } = undoApi;

  /**
   * A save / export / share scene bundle with entity ids compacted to positional
   * `o*` / `e*` (connections re-pointed in step), the scene-setting subset, and
   * the current globals. The live fields keep their UUIDs — only the emitted
   * artifact is rewritten.
   */
  function sceneBundle(): Record<string, unknown> {
    const snap = snapshot();
    const remapped = remapSceneIds(snap.objects, snap.emitters, snap.connections);
    const bundle: Record<string, unknown> = {
      objects: remapped.objects,
      emitters: remapped.emitters,
      connections: remapped.connections,
      settings: sceneSettings(settings),
      env: { preset: wallParamsToPreset(backgroundWall.getParams()) },
      camera: rig.captureCamera(),
    };
    if (sceneUsesMic(snap.objects)) {
      const manifest = micStore.buildManifest();
      if (manifest.length > 0) {
        bundle.micManifest = manifest;
      }
    }
    return bundle;
  }

  const entities = initEntityIndex(ctx, { recordBefore: undoApi.recordBefore });
  const {
    objectById,
    emitterById,
    groupLeafIds,
    updateGroupBoxes,
    pruneEmptyGroups,
  } = entities;

  const signalRuntime = initSignalRuntime(ctx, {
    settableParams,
    emitterById,
    expandParamTargets: (id: string): string[] =>
      objectById(id)?.type === "group" ? groupLeafIds(id) : [id],
    bindPitchValue: (targetId: string, value: number): number => {
      const obj = objectById(targetId);
      if (!obj || (obj.type !== "box" && obj.type !== "sphere" && obj.type !== "funnel") || isKitTimbre(obj.timbreId)) {
        return value;
      }
      const classes = allowedPitchClasses(obj.timbreId, settings.simpleMode, field, graph);
      return classes === null ? value : pitchByDegreeIndex(value, classes);
    },
    paramBase,
    markParamPanelDirty: (): void => {
      paramPanelDirty = true;
    },
    resolveCountDisplay: (id: string, value: number, dt: number): number => counterSync.resolveDisplay(id, value, dt),
  });
  const { injExternal, injDriven, injSpeed, driveContactGlow, buildValueNodes, evaluateSignals } = signalRuntime;
  clearEmitInjection = signalRuntime.clearEmitInjection;
  const zeroSignalCounts = new Float32Array(MAX_OBJECTS);
  function primeSignalState(): void {
    evaluateSignals(field.packedColliders(), zeroSignalCounts, zeroSignalCounts, 0);
  }

  /**
   * The clock/performance reset: kill particles, rewind the clock, clear signal
   * runtime state and re-prime it, silence voices, then fire any pulse-on-reset
   * buttons. Shared by the Reset control and the Global node's reset pulse-in;
   * the latter defers here from the frame boundary (never inside evaluateSignals)
   * so primeSignalState's re-entrant evaluate cannot corrupt the in-flight frame.
   * Pulse-on-reset buttons inject AFTER primeSignalState, because primeSignalState
   * drains pending presses to clear runtime state — pulsing earlier would be
   * swallowed by that drain instead of reaching the next real frame.
   */
  function performClockReset(): void {
    particles.reset();
    evaluator.resetRuntimeState();
    clearEmitInjection();
    primeSignalState();
    audio.stopAllVoices();
    for (const o of field.list()) {
      if (o.type === "button" && o.buttonPulseOnReset) {
        widgets.pulse(o.id);
      }
    }
  }

  function clearResetFailsafe(): void {
    if (resetFailsafe !== null) {
      clearTimeout(resetFailsafe);
      resetFailsafe = null;
    }
  }

  /**
   * The Reset control while in a room: send the owner-only reset action and wait
   * for the DO's echo before resetting locally, so the presser resets on the same
   * round-trip as everyone else. A failsafe resets locally anyway if the echo never
   * arrives (dropped because a non-owner pressed, or a flaky connection), so Reset
   * is never silently dead. Solo presses reset immediately.
   */
  function requestClockReset(): void {
    if (!roomActive) {
      performClockReset();
      return;
    }
    if (!isRoomGuest()) {
      roomActivitySend("clockReset");
      noteActivity(myRoomUid(), "clockReset");
    }
    roomResetSend();
    clearResetFailsafe();
    resetFailsafe = window.setTimeout((): void => {
      resetFailsafe = null;
      performClockReset();
    }, ROOM_RESET_FAILSAFE_MS);
  }

  function onRoomReset(): void {
    clearResetFailsafe();
    performClockReset();
  }

  /** True while locked out: reaching EMOTE_WINDOW_MAX fires within EMOTE_WINDOW_MS starts a fresh EMOTE_WINDOW_MS lockout from that last fire. */
  function emoteRateLimited(): boolean {
    return emoteLockUntil > performance.now();
  }

  /** 0→1 progress across the lockout that began at the capping fire, or null when not locked out. */
  function emoteLimitProgress(): number | null {
    const now = performance.now();
    if (emoteLockUntil <= now) {
      return null;
    }
    return Math.min(1, (now - emoteLockStart) / EMOTE_WINDOW_MS);
  }

  /**
   * Fires an emote from the self avatar's radial: plays it locally at once (no
   * round-trip wait) and relays it so peers play it over this player's avatar.
   * Rapid repeats are allowed (the menu stays open); hitting EMOTE_WINDOW_MAX fires
   * inside EMOTE_WINDOW_MS locks emotes for a further EMOTE_WINDOW_MS measured from
   * that capping fire. The relayed emote never persists.
   */
  function sendEmote(emoji: string): void {
    if (!roomActive || emoteRateLimited()) {
      return;
    }
    const now = performance.now();
    while (emoteTimes.length > 0 && now - emoteTimes[0] > EMOTE_WINDOW_MS) {
      emoteTimes.shift();
    }
    emoteTimes.push(now);
    roomEmoteSend(emoji);
    playerAvatars.playEmote(myRoomUid(), emoji);
    if (emoteTimes.length >= EMOTE_WINDOW_MAX) {
      emoteLockStart = now;
      emoteLockUntil = now + EMOTE_WINDOW_MS;
      emoteTimes.length = 0;
    }
  }

  /** True when the kick radial may open over `uid`: any present participant other than myself (the owner included — the radial opens but its Kick wedge is disabled). */
  function canOpenKickRadial(uid: string): boolean {
    return roomActive && uid !== myRoomUid();
  }

  /** True when `uid` is the inviolable owner of an owned (private) room, so kicking them is never allowed. */
  function isOwnerTarget(uid: string): boolean {
    return roomOwnerUid !== "" && uid === roomOwnerUid;
  }

  /** True when `uid` is a valid kick target: a present participant other than myself and other than the inviolable owner. */
  function canKickTarget(uid: string): boolean {
    return canOpenKickRadial(uid) && !isOwnerTarget(uid);
  }

  /** True when this client is the private room's owner, whose single vote evicts instantly and is never time-gated. */
  function ownerCanInstaKick(): boolean {
    return roomActive && !roomIsPublic && roomOwnerUid !== "" && myRoomUid() === roomOwnerUid;
  }

  /**
   * True while this client's kick vote would carry zero weight: a non-owner whose
   * room stay has not yet reached KICK_MIN_STAY_MS. The radial's kick wedge is disabled
   * during this window (matching the server's attackPower=0), re-evaluated each time the
   * radial is reopened. The owner is never gated.
   */
  function kickGated(): boolean {
    return !ownerCanInstaKick() && (localRoomJoinAt === 0 || Date.now() - localRoomJoinAt < KICK_MIN_STAY_MS);
  }

  /**
   * Opens the kick radial over a peer (emote-style): the radial's built-in Close sits
   * top-centered at 12 o'clock and a single Kick wedge faces it. The Kick wedge is
   * disabled while {@link kickGated} or when the target is the inviolable owner — the
   * radial still opens so the gesture is consistent, but Kick cannot be pressed.
   * Choosing Kick opens the confirmation modal. A tap on myself opens nothing. Rebuilt
   * per open so the target and gate state are current.
   */
  function openKickRadial(targetUid: string, clientX: number, clientY: number): void {
    if (!canOpenKickRadial(targetUid)) {
      return;
    }
    kickRadial.setRoot([
      { key: "kick", label: t("multiplayer.kickAction"), iconSvg: KICK_EJECT_ICON, disabled: kickGated() || isOwnerTarget(targetUid), onSelect: (): void => openKickConfirm(targetUid) },
    ]);
    kickRadial.open(clientX, clientY);
  }

  /**
   * The kick confirmation modal reached from the radial's Kick wedge: the target's
   * display name directly under the title, then the explanation that a kick only
   * succeeds once several participants vote (no per-user time gate text — that gate
   * lives on the radial wedge). Confirm casts one server-authoritative kick vote.
   */
  function openKickConfirm(targetUid: string): void {
    if (!canKickTarget(targetUid)) {
      return;
    }
    const display = (roomNames.get(targetUid) ?? "") === "" ? `${t("multiplayer.anonymousName")}_${anonymousIdSuffix(targetUid)}` : roomNames.get(targetUid) ?? "";
    showActionModal<"kick" | "cancel">(
      t("multiplayer.kickTitle"),
      `${display}\n\n${t("multiplayer.kickConfirmBody")}`,
      [
        { label: t("multiplayer.close"), value: "cancel", subtle: true },
        { label: t("multiplayer.kickAction"), value: "kick" },
      ],
      (value: "kick" | "cancel"): void => {
        if (value === "kick") {
          roomKickSend(targetUid);
        }
      },
      { previewSrc: avatarFor(targetUid, colorCssForUid(targetUid), 96) },
    );
  }

  /** Localized label for a feature category, for the activity line naming a toggled feature. */
  function featureLabel(cat: string): string {
    return cat === "physics"
      ? t("feature.physics")
      : cat === "3d"
        ? t("feature.3d")
        : cat === "node"
          ? t("feature.node")
          : cat;
  }

  /**
   * Builds the activity-log sentence for `kind` by `display`, naming the specific
   * changed parameter and value carried in the language-neutral `detail` (tempo BPM,
   * `gravity|<value>`, `simple|<0|1>`, `feature|<cat>|<0|1>`, or `wallpaper`) so the
   * sentence is localized on this client regardless of the actor's language.
   */
  function activityText(display: string, kind: ActivityKind, detail: string | undefined): string {
    if (kind === "clockReset") {
      return t("multiplayer.activityClockReset", { name: display });
    }
    if (kind === "tempo") {
      return t("multiplayer.activityTempo", { name: display, value: detail ?? "" });
    }
    const parts = (detail ?? "").split("|");
    if (parts[0] === "gravity") {
      return t("multiplayer.activitySettingsValue", { name: display, param: t("param.gravity"), value: parts[1] ?? "" });
    }
    if (parts[0] === "wallpaper") {
      return t("multiplayer.activitySettingsParam", { name: display, param: t("multiplayer.activityParamWallpaper") });
    }
    if (parts[0] === "simple") {
      return t("multiplayer.activitySettingsToggle", { name: display, param: t("feature.simple"), state: t(parts[1] === "1" ? "multiplayer.stateOn" : "multiplayer.stateOff") });
    }
    if (parts[0] === "feature") {
      return t("multiplayer.activitySettingsToggle", { name: display, param: featureLabel(parts[1] ?? ""), state: t(parts[2] === "1" ? "multiplayer.stateOn" : "multiplayer.stateOff") });
    }
    return t("multiplayer.activitySettings", { name: display });
  }

  /**
   * Appends one activity-log line for `kind` performed by `uid`: the actor's avatar
   * icon (the same icon shown for a claimed object) plus a localized sentence naming
   * them and the changed parameter. A kickable actor's row doubles as the second kick
   * entry point (tap → kick UI); the self row and the owner row are not kickable.
   */
  function noteActivity(uid: string, kind: ActivityKind, detail?: string): void {
    const name = uid === myRoomUid() ? myNameCached ?? "" : roomNames.get(uid) ?? "";
    const display = name === "" ? `${t("multiplayer.anonymousName")}_${anonymousIdSuffix(uid)}` : name;
    activityLog.push({
      iconSrc: avatarFor(uid, colorCssForUid(uid), 48),
      text: activityText(display, kind, detail),
      onActivate: canOpenKickRadial(uid) ? (clientX: number, clientY: number): void => openKickRadial(uid, clientX, clientY) : undefined,
    });
  }

  /** The room-synced settings as field-level values, so a change can name the specific parameter (tempo BPM, gravity, simple mode, each feature, wallpaper) rather than a single opaque signature. */
  function settingsSnap(): { tempoBpm: number; gravityY: number; simpleMode: boolean; features: string; wall: string } {
    return {
      tempoBpm: Math.round(60 / settings.clockPeriod),
      gravityY: settings.gravityY,
      simpleMode: settings.simpleMode,
      features: JSON.stringify(settings.features),
      wall: JSON.stringify(wallParamsToPreset(backgroundWall.getParams())),
    };
  }

  function captureActivityBaseline(): void {
    activitySettingsBaseline = settingsSnap();
  }

  /** First feature category whose enabled state differs between two serialized feature maps, with its new state, or null when none. */
  function changedFeature(prevJson: string, curJson: string): { cat: string; on: boolean } | null {
    const prev = JSON.parse(prevJson) as Record<string, boolean>;
    const cur = JSON.parse(curJson) as Record<string, boolean>;
    for (const cat of FEATURE_CATEGORIES) {
      if (prev[cat] !== cur[cat]) {
        return { cat, on: cur[cat] === true };
      }
    }
    return null;
  }

  /**
   * Collapses a burst of writes for one activity kind (e.g. a slider drag firing
   * persist every frame) into a single trailing line carrying the settled `detail`:
   * each change re-arms the kind's timer and overwrites the pending detail, so only
   * the final value emits one line — broadcast to peers and shown locally at once.
   */
  function scheduleActivity(kind: ActivityKind, detail: string): void {
    activityDetails.set(kind, detail);
    const existing = activityTimers.get(kind);
    if (existing !== undefined) {
      clearTimeout(existing);
    }
    const timer = window.setTimeout((): void => {
      activityTimers.delete(kind);
      const settled = activityDetails.get(kind);
      activityDetails.delete(kind);
      roomActivitySend(kind, settled);
      noteActivity(myRoomUid(), kind, settled);
    }, ACTIVITY_DEBOUNCE_MS);
    activityTimers.set(kind, timer);
  }

  function clearActivityTimers(): void {
    for (const timer of activityTimers.values()) {
      clearTimeout(timer);
    }
    activityTimers.clear();
    activityDetails.clear();
  }

  /**
   * Called on every settings persist while in a room: diffs each synced field against
   * the last baseline and schedules an activity naming the specific changed parameter
   * and value. Remote applies (snapshot / incoming settings op) run under
   * suppressActivityEmit and only advance the baseline silently, so a peer's change is
   * never echoed back as this client's own activity. A private-room guest, whose
   * settings never propagate, also only advances the baseline.
   */
  function noteLocalSettingsChange(): void {
    if (!roomActive) {
      return;
    }
    const prev = activitySettingsBaseline;
    if (suppressActivityEmit || prev === null || isRoomGuest()) {
      activitySettingsBaseline = settingsSnap();
      return;
    }
    const cur = settingsSnap();
    activitySettingsBaseline = cur;
    if (cur.tempoBpm !== prev.tempoBpm) {
      scheduleActivity("tempo", String(cur.tempoBpm));
    }
    if (cur.gravityY !== prev.gravityY) {
      scheduleActivity("settings", `gravity|${(-cur.gravityY).toFixed(1)}`);
    }
    if (cur.simpleMode !== prev.simpleMode) {
      scheduleActivity("settings", `simple|${cur.simpleMode ? 1 : 0}`);
    }
    if (cur.features !== prev.features) {
      const changed = changedFeature(prev.features, cur.features);
      if (changed !== null) {
        scheduleActivity("settings", `feature|${changed.cat}|${changed.on ? 1 : 0}`);
      }
    }
    if (cur.wall !== prev.wall) {
      scheduleActivity("settings", "wallpaper");
    }
  }

  let activeTool: Tool | null = null;
  let tutorialDeleteLock = false;

  const tools = initToolSpawn(ctx, {
    getActiveTool: (): Tool | null => activeTool,
    setActiveToolState: (tool: Tool | null): void => {
      activeTool = tool;
    },
    highlightTool: (tool: Tool | null): void => toolbar.highlightTool(tool),
    persist,
    bindPanel,
    showArmedPreview: (): void => pointer.showArmedPreview(),
    hideArmedPreview: (): void => pointer.hideArmedPreview(),
  });
  const { setActiveTool } = tools;

  /**
   * Play mode is the inverse of object placement: with placement off the canvas
   * plays on touch instead of editing. Simple mode is a separate editing mode and
   * suppresses play, so play is active only when placement is off and Simple is
   * off. `objectPlacement` is a session-only top-bar toggle, never serialized.
   */
  function touchPlayActive(): boolean {
    return !settings.simpleMode && !settings.objectPlacement;
  }

  function setObjectPlacement(on: boolean): void {
    if (!on && settings.simpleMode) {
      settings.simpleMode = false;
      applySimpleMode();
      toolbar.setSimpleChecked(false);
    }
    settings.objectPlacement = on;
    persist();
    applyTouchPlayActive();
  }

  particles.setEmitters(emitterField.list());
  particles.setPointSize(settings.pointSize);
  field.setParticleRadius(settings.pointSize / 2);
  particles.setGravityY(settings.gravityY);
  particles.setFallKillDistance(settings.fallKillDistance);
  particles.setFallKillLifeDivisor(settings.fallKillLifeDivisor);
  particles.setClockPeriod(settings.clockPeriod);
  rig.jumpTo(settings.simpleMode ? "2d" : settings.viewMode);
  rig.setCameraEnabled(settings.cameraEnabled);
  const bootCamera = parseCameraPose(takePendingCamera());
  if (bootCamera !== null && !settings.simpleMode) {
    rig.restoreCamera(bootCamera);
  }
  particles.setZLock(rig.zLock());
  field.setColorSet("pitch");
  field.setObjectMaterial("matte");
  particles.setMaterial("matte");

  let benchController: BenchController | null = null;
  const benchFixture = new URLSearchParams(location.search).get("bench");
  const benchMode = benchFixture !== null;
  const perfInstrument = benchMode || new URLSearchParams(location.search).get("perfhooks") === "1";
  const params = new URLSearchParams(location.search);
  const readbackTestMode = parseReadbackTestMode(params.get("rbmode"));
  if (readbackTestMode === "noread" || readbackTestMode === "off") {
    particles.setDebugReadbackOff(true);
  }
  const runDownstream = readbackTestMode === "full" || readbackTestMode === "noread";
  const adaptive: AdaptiveQuality | null =
    benchMode || params.has("bloom") || params.has("dpr1")
      ? null
      : new AdaptiveQuality({
          setBloomMode,
          setDprCap1: (cap: boolean): void => {
            setDprCap1(cap);
            particles.updatePointScale();
          },
          onStep: (bloom: BloomMode, dprCap1: boolean): void => toolbar.syncDebugRender(bloom, dprCap1),
        });
  if (params.get("dpr1") === "1") {
    setDprCap1(true);
    particles.updatePointScale();
  }
  if (benchFixture !== null) {
    const durRaw = Number.parseInt(new URLSearchParams(location.search).get("dur") ?? "", 10);
    const durSec = Number.isFinite(durRaw) && durRaw > 0 ? durRaw : 10;
    const collisionEnabled = new URLSearchParams(location.search).get("collision") !== "0";
    if (new URLSearchParams(location.search).get("cam") !== "1") {
      rig.setCameraEnabled(false);
    }
    particles.setCollisionEnabled(collisionEnabled);
    const selectParam = new URLSearchParams(location.search).get("select");
    if (selectParam !== null && selectParam.length > 0) {
      const objs = field.list();
      const target =
        objs.find((o: PlacedObject): boolean => o.id === selectParam) ??
        objs.find((o: PlacedObject): boolean => o.type === selectParam) ??
        null;
      if (target !== null) {
        field.select(target);
        bindPanel();
      }
    }
    setPhaseTimer({
      begin: (name: string): void => stats.beginPhase(name),
      end: (name: string): void => stats.endPhase(name),
    });
    benchController = new BenchController(benchFixture, durSec, collisionEnabled);
  }
  if (import.meta.env.DEV && (params.has("notutorial") || params.has("bench"))) {
    initTestPlaceApi(ctx, { toolSpawn: tools, pushUndo, persistObjects, persistConnections, syncEmitters, bindPanel });
  }
  const perfSkip = parsePerfSkip(new URLSearchParams(location.search).get("noperf"));
  if (perfSkip.outline) {
    selectionFrames.setEnabled(false);
  }
  if (perfInstrument) {
    (window as unknown as { __perfToggle: (key: string, on: boolean) => void }).__perfToggle = (key: string, on: boolean): void => {
      if (key === "gizmo") {
        perfSkip.gizmo = !on;
        gizmo.group.visible = on && gizmo.hasTarget();
      } else if (key === "ports") {
        perfSkip.ports = !on;
      } else if (key === "labels") {
        perfSkip.labels = !on;
      } else if (key === "outline") {
        perfSkip.outline = !on;
        selectionFrames.setEnabled(on);
      } else if (key === "locks") {
        perfSkip.locks = !on;
      }
    };
    (window as unknown as { __benchSelect: (on: boolean) => void }).__benchSelect = (on: boolean): void => {
      if (on) {
        const box = field.list().find((o: PlacedObject): boolean => o.type === "box") ?? field.list()[0];
        if (box) {
          field.select(box);
        }
      } else {
        field.deselect();
      }
      bindPanel();
    };
  }
  const selPerf = { gizmo: 0, ports: 0, labels: 0, css2d: 0, frames: 0 };

  /**
   * Runs one select-time UI step, accumulating its per-frame CPU cost into
   * selPerf when in bench mode for attribution. The 100µs performance.now()
   * clamp averages out over the run's thousands of frames, so sub-clamp costs
   * still resolve. Outside bench mode it is a direct call with no timing
   * overhead; a skipped step (?noperf) is a no-op.
   */
  function runTimed(skip: boolean, fn: () => void): number {
    if (skip) {
      return 0;
    }
    if (!perfInstrument) {
      fn();
      return 0;
    }
    const t = performance.now();
    fn();
    return performance.now() - t;
  }
  audio.setMasterVolume(settings.volume);
  audio.setWoodDecay(settings.woodDecayS);
  audio.setBellTranspose(settings.bellTranspose);
  audio.setBellModRatio(settings.bellModRatio);
  audio.setBellModIndex(settings.bellModIndex);
  audio.setBellLevel(settings.bellLevel);
  audio.setDensityLow(settings.densityLow);
  audio.setDensityHigh(settings.densityHigh);
  audio.setDensityTransient(settings.densityTransient);
  audio.setDensityDetune(settings.densityDetune);
  audio.setDensityVoiceGain(settings.densityVoiceGain);
  audio.setDensityDuck(settings.densityDuck);
  audio.setReverbAmount(settings.densityReverb);

  let toolbar: {
    syncControls: (target: ContextTarget) => void;
    highlightTool: (tool: Tool | null) => void;
    setToolsEnabled: (enabled: boolean) => void;
    setGlobalPlaced: (placed: boolean) => void;
    applyLocks: (emitInDriven: boolean, driven: Map<string, number>) => void;
    applyGlobalParamLocks: (driven: Map<string, number>) => void;
    setUndoRedoEnabled: (canUndo: boolean, canRedo: boolean) => void;
    setCopyPasteEnabled: (canCopy: boolean, canPaste: boolean) => void;
    showPaletteHint: (tool: Tool) => void;
    nodeSubmenu: {
      toolAt: (x: number, y: number) => Tool | null;
      setHover: (tool: Tool | null) => void;
      close: () => void;
    };
    setSimpleChecked: (on: boolean) => void;
    setFeatureChecked: (cat: FeatureCategory, on: boolean) => void;
    setCameraState: (checked: boolean) => void;
    setTouchPlayToggleVisible: (visible: boolean) => void;
    setTouchPlayToggleActive: (on: boolean) => void;
    setDimensionToggleVisible: (visible: boolean) => void;
    setDimensionToggleActive: (on: boolean) => void;
    setViewMode: (mode: ViewMode) => void;
    setPlayModeHidden: (hidden: boolean) => void;
    syncSceneSettings: (clockPeriod: number, gravityY: number) => void;
    syncWall: (params: WallParams) => void;
    setOpenedScene: (meta: SceneListItem | null) => void;
    setRoomChip: (info: { name: string; ownerName: string | null; count: number; capacity: number | null } | null) => void;
    setForeignSceneLock: (blocked: boolean) => void;
    setCommunitySceneLoaded: (loaded: boolean) => void;
    setRoomGuestLock: (blocked: boolean) => void;
    setRoomModeLock: (locked: boolean) => void;
    setObjectPanelLocked: (locked: boolean) => void;
    setObjectPanelBottom: (bottom: boolean, clearancePx?: number) => void;
    setObjectPanelTopOffset: (px: number) => void;
    syncDebugRender: (bloom: BloomMode, dprCap1: boolean) => void;
    tutorialTargets: {
      sequencerBtn: () => HTMLElement | null;
      boxBtn: () => HTMLElement | null;
      scenePanelBtn: HTMLElement;
      sceneSettingsNavBtn: HTMLElement;
      featuresNavBtn: HTMLElement;
      communityNavBtn: () => HTMLElement | null;
      touchPlayBtn: HTMLElement;
      timbreRow: () => HTMLElement | null;
    };
    tutorialOpenScenePanelSection: (sectionTitle: string) => void;
    setHoverPanelsSuppressed: (suppressed: boolean) => void;
    setHoverLeaveDisabled: (disabled: boolean) => void;
    setMasterVolumeUnmuted: (v: number) => void;
    setRoomActive: (active: boolean) => void;
    isRadialMode: () => boolean;
    radial: RadialMenuApi;
    uiMin: boolean;
    uiView: boolean;
    uiNoSeq: boolean;
  };
  let showTutorial: () => void = (): void => {};

  let sequencerPanel: { rebuild: () => void; updatePlayhead: () => void; setPlayModeHidden: (hidden: boolean) => void } | null = null;

  function clampNum(v: number, lo: number, hi: number): number {
    return Math.min(hi, Math.max(lo, v));
  }

  /**
   * Runs `fn` with every driven param temporarily reset to its captured base, then
   * restores the live driven values. Used to persist / snapshot the base (so a
   * Param-in override never bakes into saved data) while consumers keep seeing the
   * live value the rest of the frame.
   */
  function withParamBase<T>(fn: () => T): T {
    const prevSerializing = serializingScene;
    serializingScene = true;
    try {
      const reapply: Array<() => void> = [];
      for (const [key, base] of paramBase) {
        const sep = key.indexOf("|");
        const entry = settableParams(key.slice(0, sep)).find((p: NumericParam): boolean => p.name === key.slice(sep + 1));
        if (!entry) {
          continue;
        }
        const live = entry.get();
        entry.set(base);
        reapply.push((): void => entry.set(live));
      }
      const result = fn();
      for (const r of reapply) {
        r();
      }
      return result;
    } finally {
      serializingScene = prevSerializing;
    }
  }

  /**
   * Pushes every timbre node's stored controls into the engine and display: it
   * first clears all timbres to their ungoverned defaults (volume 1, transpose
   * 0, sustain 1), then replays each placed timbre node's packed params so a
   * timbre with no node sounds unshifted. The single re-sync point after a
   * structural change (scene load, undo / redo) that field.serialize alone
   * cannot reflect, since the per-timbre engine state lives outside the objects.
   * Two timbre nodes on the same timbre resolve last-write-wins (unsupported).
   */
  function syncTimbreEngine(): void {
    for (const timbre of timbreIds()) {
      activeAudio.setTimbreVolume(timbre, 1);
      activeAudio.setTimbreTranspose(timbre, 0);
      field.setTimbreTranspose(timbre, 0);
      if (SUSTAIN_TIMBRES.includes(timbre)) {
        activeAudio.setSustainValue(timbre, 1);
      }
    }
    for (const obj of field.list()) {
      if (obj.type !== "timbre") {
        continue;
      }
      const volume = clampNum(obj.params.x, 0, 1);
      for (const timbre of obj.timbreIds) {
        activeAudio.setTimbreVolume(timbre, volume);
        activeAudio.setTimbreTranspose(timbre, obj.params.z);
        field.setTimbreTranspose(timbre, obj.params.z);
      }
      if (SUSTAIN_TIMBRES.includes(obj.timbreId)) {
        activeAudio.setSustainValue(obj.timbreId, clampNum(obj.params.y, 0, 1));
      }
    }
    syncEnvelopeEngine();
  }

  /**
   * Resolves each `envelopeOut → envelopeIn` edge into the engine: clears every
   * timbre's envelope (so a disconnect restores the recipe AD), then for each
   * timbre node with a wired envelope source maps that envelope's normalized
   * A/D/S/R knobs to seconds + a [0,1] sustain level and pushes them. A timbre
   * takes a single envelope input, so the first wired source wins. Cheap enough
   * to run on every structural / connection / knob change.
   */
  function syncEnvelopeEngine(): void {
    for (const timbre of timbreIds()) {
      activeAudio.setTimbreEnvelope(timbre, null);
    }
    for (const obj of field.list()) {
      if (obj.type !== "timbre") {
        continue;
      }
      const edges = graph.edgesTo(obj.id, "envelopeIn");
      if (edges.length === 0) {
        continue;
      }
      const src = objectById(edges[0].from);
      if (!src || src.type !== "envelope") {
        continue;
      }
      const adsr = {
        attackS: src.params.x * ENVELOPE_ATTACK_MAX_S,
        decayS: src.params.y * ENVELOPE_DECAY_MAX_S,
        sustainLevel: clampNum(src.params.z, 0, 1),
        releaseS: src.params.w * ENVELOPE_RELEASE_MAX_S,
      };
      for (const timbre of obj.timbreIds) {
        activeAudio.setTimbreEnvelope(timbre, adsr);
      }
    }
  }

  function captureSettings(): { clockPeriod: number; gravityY: number } {
    return { clockPeriod: settings.clockPeriod, gravityY: settings.gravityY };
  }

  function applySettings(s: { clockPeriod: number; gravityY: number }): void {
    particles.setClockPeriod(s.clockPeriod);
    settings.clockPeriod = s.clockPeriod;
    particles.setGravityY(s.gravityY);
    settings.gravityY = s.gravityY;
    toolbar.syncSceneSettings(s.clockPeriod, s.gravityY);
    persist();
    roomNotify();
  }

  /**
   * Applies a remote room settings op: merges the owner's synced scene-setting
   * subset onto the local settings (sanitized) and fires each changed key's runtime
   * reaction so the value actually takes effect — gravity/clock on the particle
   * system, the orbit clock reconcile, the feature gate, and simple mode. Only the
   * synced keys (ROOM_SYNCED_SETTING_KEYS) are copied; viewMode and every personal /
   * device-local setting (camera, object placement, language, snap, …) are left
   * untouched, so a remote settings change can never overwrite this device's own
   * view or controls. The heavy UI reactions (panel rebuild / feature gate / chrome)
   * fire only when features or simple mode actually flip, not on every gravity/tempo
   * transient during a remote drag. Runs under the room client's applyingRemote
   * guard, so the persist here does not echo back.
   */
  function applyRoomSettings(value: unknown): void {
    suppressActivityEmit = true;
    try {
    const merged = applySceneSettings(settings, value);
    const clockChanged = settings.clockPeriod !== merged.clockPeriod;
    const featuresChanged = JSON.stringify(settings.features) !== JSON.stringify(merged.features);
    const simpleChanged = settings.simpleMode !== merged.simpleMode;
    settings.gravityY = merged.gravityY;
    settings.clockPeriod = merged.clockPeriod;
    settings.features = merged.features;
    settings.simpleMode = merged.simpleMode;
    particles.setGravityY(settings.gravityY);
    particles.setClockPeriod(settings.clockPeriod);
    toolbar.syncSceneSettings(settings.clockPeriod, settings.gravityY);
    if (clockChanged) {
      reconcileOrbitClockSync();
    }
    if (featuresChanged) {
      for (const cat of FEATURE_CATEGORIES) {
        toolbar.setFeatureChecked(cat, settings.features[cat]);
      }
    }
    if (simpleChanged) {
      toolbar.setSimpleChecked(settings.simpleMode);
    }
    if (featuresChanged || simpleChanged) {
      applySimpleMode();
      applyTouchPlayActive();
    }
    applyRoomEnv(value);
    persist();
    } finally {
      suppressActivityEmit = false;
    }
  }

  /**
   * Applies the wallpaper (background wall) carried in a remote settings op. The
   * env rides inside the owner-gated `settings` op rather than a separate kind, so
   * a guest can never push a wall change. Rebaking the wall is expensive, so it
   * only regenerates when the incoming preset actually differs from the live one.
   */
  function applyRoomEnv(value: unknown): void {
    if (typeof value !== "object" || value === null) {
      return;
    }
    const incoming = parseWallParams((value as Record<string, unknown>).env);
    if (!incoming) {
      return;
    }
    if (JSON.stringify(wallParamsToPreset(incoming)) === JSON.stringify(wallParamsToPreset(backgroundWall.getParams()))) {
      return;
    }
    backgroundWall.regenerate(incoming);
    toolbar.syncWall(incoming);
    saveEnv(incoming);
  }

  /**
   * Loads a scene into the running engine without a page reload: rebuilds
   * objects/emitters/connections, applies the scene's setting subset and globals,
   * then a clock reset so the prior scene's particles, voices, timers and
   * signal-node runtime state cannot bleed into the new one. Never persists —
   * Community browsing must not overwrite the visitor's edited scene in
   * localStorage. The view mode + camera pose ride with the scene and are
   * reproduced (a legacy scene without them keeps the current view).
   * Object-placement and camera-control are session-only. Restoring the visitor's
   * own scene (`keepControls` true) keeps both on, while browse-style loads
   * (`keepControls` false) default to off unless this is a Community open
   * (`forCommunity`), where the per-user Community defaults apply. Returns an
   * error string for a malformed scene, null on success.
   */
  function applySceneInPlace(parsed: unknown, keepControls: boolean = false, forCommunity: boolean = false): string | null {
    const scene = validScene(parsed);
    if (scene === null) {
      return "invalid scene: missing objects / emitters / settings";
    }
    resetUndo();
    paramBase.clear();
    evaluator.resetRuntimeState();
    field.clear();
    field.restore(scene.objects);
    field.selectIndex(-1);
    emitterField.clear();
    emitterField.restore(scene.emitters);
    emitterField.selectIndex(-1);
    graph.restore(Array.isArray(scene.connections) ? scene.connections : []);
    pruneLegacyCountValueIn(graph, (id: string): boolean => objectById(id)?.type === "count");

    const merged = applySceneSettings(settings, scene.settings);
    settings.gravityY = merged.gravityY;
    settings.clockPeriod = merged.clockPeriod;
    settings.features = merged.features;
    settings.simpleMode = merged.simpleMode;
    if (forCommunity && !keepControls) {
      settings.cameraEnabled = settings.communitySceneCameraEnabled;
      settings.objectPlacement = settings.communitySceneObjectPlacement;
    } else {
      settings.cameraEnabled = keepControls;
      settings.objectPlacement = keepControls;
    }
    settings.viewMode = merged.viewMode;
    particles.setGravityY(settings.gravityY);
    particles.setClockPeriod(settings.clockPeriod);
    toolbar.syncSceneSettings(settings.clockPeriod, settings.gravityY);
    for (const cat of FEATURE_CATEGORIES) {
      toolbar.setFeatureChecked(cat, settings.features[cat]);
    }
    toolbar.setSimpleChecked(settings.simpleMode);
    toolbar.setViewMode(settings.viewMode);
    applySimpleMode();
    reconcileOrbitClockSync();
    applyTouchPlayActive();
    const cameraPose = parseCameraPose(scene.camera);
    if (cameraPose !== null && !settings.simpleMode) {
      rig.restoreCamera(cameraPose);
      particles.setZLock(rig.zLock());
    }
    syncTimbreEngine();

    const env = parseWallParams(scene.env) ?? { ...WALL_DEFAULT_PARAMS };
    backgroundWall.regenerate(env);
    toolbar.syncWall(env);
    saveEnv(env);

    performClockReset();
    particles.setEmitters(emitterField.list());
    return null;
  }

  let stashedScene: Record<string, unknown> | null = null;
  let stashedProvenance: Provenance | null = null;
  let currentProvenance: Provenance | null = null;
  let myUidCached: string | null = null;
  let myTokenCached: string | null = null;
  let myAdToken: string | null = null;
  let myNameCached: string | null = null;
  let myIsRealCached: boolean = false;
  let focusEpoch = 0;
  let feedViewportEl: HTMLElement | null = null;

  /**
   * Records (or clears) the author of the scene now loaded, and keeps the
   * affordances that depend on it in sync. A foreign scene (author ≠ this device's
   * uid) locks both save-slot and upload — keeping someone else's scene under your
   * name is an attribution violation, and retention is meant to go through
   * bookmarks (a foreign scene saved to a slot also loses its author and goes
   * silent) — and pins the info chip so it cannot be dismissed (the only signal
   * you are in someone else's scene). Your own scene shows a dismissible chip;
   * null hides it. The chip is suppressed while the Community feed is open (the
   * feed is its own context) — it appears only once a card is committed
   * full-screen (keep / exit-restore / boot). Runtime only — persistence is done
   * explicitly at the points that commit a scene to localStorage (keep / import),
   * so transient in-place browsing never leaves a stale persisted author behind.
   */
  function setProvenance(p: Provenance | null): void {
    currentProvenance = p;
    applyForeignLock();
    renderTopChips();
  }

  /**
   * The foreign-scene lock (save / upload / export / scene-settings) keys off a
   * single fact — "the loaded scene is not mine" — so it stays orthogonal to room
   * role. A room guest never authored the live room scene, so it is always
   * foreign-locked regardless of its (stale) local provenance; the owner is locked
   * only when the room was seeded from someone else's Community scene.
   */
  function applyForeignLock(): void {
    const ownScene = currentProvenance === null || currentProvenance.meta.owner === myUidCached;
    toolbar.setForeignSceneLock(isRoomGuest() || !ownScene);
    toolbar.setCommunitySceneLoaded(!ownScene);
  }

  /**
   * Repaints both top-bar chips from current state. The scene chip shows the
   * loaded scene's Community provenance — but never for a room guest, who sees the
   * room's scene whose author it does not know (showing the guest's stale
   * provenance was the original stale-chip bug). The room chip shows once presence
   * has arrived (occupancy ≥ 1). While the feed is open both are hidden.
   */
  function renderTopChips(): void {
    if (document.body.classList.contains("feed-open")) {
      toolbar.setOpenedScene(null);
      toolbar.setRoomChip(null);
      return;
    }
    if (currentProvenance !== null && (!isRoomGuest() || roomSceneFromSnapshot)) {
      toolbar.setOpenedScene(currentProvenance.meta);
    } else {
      toolbar.setOpenedScene(null);
    }
    if (roomActive && roomColors.size > 0) {
      toolbar.setRoomChip({
        name: roomNameCached ?? t("multiplayer.untitledRoom"),
        ownerName: roomOwnerUid === "" ? null : (roomNames.get(roomOwnerUid) ?? null),
        count: roomColors.size,
        capacity: roomCapacityCached,
      });
    } else {
      toolbar.setRoomChip(null);
    }
  }

  /**
   * Refreshes the cached own uid (anonymous users have one too) and re-syncs the
   * foreign-scene guards, since "is this scene mine" depends on it. Called at boot
   * and after any auth transition so a guard never decides against a stale uid.
   */
  async function refreshMyUid(): Promise<string | null> {
    myUidCached = await myUid();
    myTokenCached = await myAccessToken();
    myIsRealCached = await isRealAccount();
    if (currentProvenance !== null) {
      setProvenance(currentProvenance);
    }
    return myUidCached;
  }
  let feedCapture: { id: string; resolve: (dataUrl: string | null) => void } | null = null;

  /**
   * The numeric parameters an entity exposes on its Param-in port — each clamps to
   * its UI range on write. This per-type registry is the single source of truth the
   * Param-in dropdown and the per-frame overwrite both read, so a new type lights up
   * by adding its list here. Geometry/size params are excluded (they need a mesh
   * rebuild, not a per-frame scalar write); pitch routes through field.setPitch so
   * the mesh recolors. The same params.z slot is `strength` (Lorentz) or `omega`
   * (Drag) depending on force mode, exposed once as `strength`.
   */
  function settableParams(id: string): NumericParam[] {
    const em = emitterById(id);
    if (em) {
      return [
        ...(em.kind === "full"
          ? [{ name: "gate", get: (): number => em.gate, set: (v: number): void => { em.gate = clampNum(v, 0, 1); } }]
          : []),
        { name: "rate", get: (): number => em.rate, set: (v: number): void => { em.rate = clampNum(v, EMIT_RATE_MIN, EMIT_RATE_MAX); } },
        { name: "spread", get: (): number => em.coneDeg, set: (v: number): void => { em.coneDeg = clampNum(v, 0, 90); } },
        { name: "speed", get: (): number => (em.speedMin + em.speedMax) / 2, set: (v: number): void => {
          const half = (em.speedMax - em.speedMin) / 2;
          const c = clampNum(v, 0, 30);
          em.speedMin = Math.max(0, c - half);
          em.speedMax = c + half;
        } },
        { name: "spd range", get: (): number => em.speedMax - em.speedMin, set: (v: number): void => {
          const c = (em.speedMin + em.speedMax) / 2;
          const half = clampNum(v, 0, 30) / 2;
          em.speedMin = Math.max(0, c - half);
          em.speedMax = c + half;
        } },
        { name: "life", get: (): number => (em.lifeMin + em.lifeMax) / 2, set: (v: number): void => {
          const half = (em.lifeMax - em.lifeMin) / 2;
          const c = clampNum(v, 0.2, 12);
          em.lifeMin = Math.max(0.2, c - half);
          em.lifeMax = c + half;
        } },
        { name: "life range", get: (): number => em.lifeMax - em.lifeMin, set: (v: number): void => {
          const c = (em.lifeMin + em.lifeMax) / 2;
          const half = clampNum(v, 0, 12) / 2;
          em.lifeMin = Math.max(0.2, c - half);
          em.lifeMax = c + half;
        } },
        { name: "offset", get: (): number => em.offset, set: (v: number): void => { em.offset = clampNum(v, 0, 1); } },
        { name: "bounce", get: (): number => em.bounce, set: (v: number): void => { em.bounce = clampNum(v, 0, 1); } },
        { name: "friction", get: (): number => em.friction, set: (v: number): void => { em.friction = clampNum(v, 0, 1); } },
        { name: "drag", get: (): number => em.drag, set: (v: number): void => { em.drag = clampNum(v, 0, EMIT_DRAG_MAX); } },
      ];
    }
    const obj = objectById(id);
    if (!obj) {
      return [];
    }
    if (obj.type === "force") {
      const strengthMax = Math.max(FIELD_STRENGTH_MAX, FIELD_OMEGA_MAX);
      return [
        { name: "radius", get: (): number => obj.params.x, set: (v: number): void => { obj.params.x = clampNum(v, FIELD_RADIUS_MIN, FIELD_RADIUS_MAX); } },
        { name: "strength", get: (): number => obj.params.z, set: (v: number): void => { obj.params.z = clampNum(v, -strengthMax, strengthMax); } },
        { name: "k", get: (): number => obj.params.w, set: (v: number): void => { obj.params.w = clampNum(v, 0, FIELD_K_MAX); } },
        { name: "fade", get: (): number => obj.fade, set: (v: number): void => { obj.fade = clampNum(v, 0, 1); } },
      ];
    }
    if (obj.type === "orbit") {
      const o = obj.orbit;
      return [
        { name: "rot x", get: (): number => o.rotSpeed.x, set: (v: number): void => { o.rotSpeed.x = clampNum(v, -1000, 1000); } },
        { name: "rot y", get: (): number => o.rotSpeed.y, set: (v: number): void => { o.rotSpeed.y = clampNum(v, -1000, 1000); } },
        { name: "rot z", get: (): number => o.rotSpeed.z, set: (v: number): void => { o.rotSpeed.z = clampNum(v, -1000, 1000); } },
        { name: "amp x", get: (): number => o.amp.x, set: (v: number): void => { o.amp.x = clampNum(v, 0, 50); } },
        { name: "amp y", get: (): number => o.amp.y, set: (v: number): void => { o.amp.y = clampNum(v, 0, 50); } },
        { name: "amp z", get: (): number => o.amp.z, set: (v: number): void => { o.amp.z = clampNum(v, 0, 50); } },
        { name: "period x", get: (): number => o.periodPct.x, set: (v: number): void => { o.periodPct.x = clampNum(v, 0.001, 1000); } },
        { name: "period y", get: (): number => o.periodPct.y, set: (v: number): void => { o.periodPct.y = clampNum(v, 0.001, 1000); } },
        { name: "period z", get: (): number => o.periodPct.z, set: (v: number): void => { o.periodPct.z = clampNum(v, 0.001, 1000); } },
        { name: "phase x", get: (): number => o.phasePct.x, set: (v: number): void => { o.phasePct.x = clampNum(v, -10, 10); } },
        { name: "phase y", get: (): number => o.phasePct.y, set: (v: number): void => { o.phasePct.y = clampNum(v, -10, 10); } },
        { name: "phase z", get: (): number => o.phasePct.z, set: (v: number): void => { o.phasePct.z = clampNum(v, -10, 10); } },
        { name: "play", get: (): number => o.play ? 1 : 0, set: (v: number): void => { o.play = v >= 0.5; } },
      ];
    }
    if (obj.type === "box" || obj.type === "sphere" || obj.type === "funnel") {
      return [
        { name: "bounce", get: (): number => obj.restitution, set: (v: number): void => { obj.restitution = clampNum(v, 0, 1); } },
        { name: "friction", get: (): number => obj.friction, set: (v: number): void => { obj.friction = clampNum(v, 0, 1); } },
        { name: "mass", get: (): number => obj.mass, set: (v: number): void => { obj.mass = clampNum(v, -MASS_MAX, MASS_MAX); } },
        { name: "pitch", get: (): number => obj.pitchIndex, set: (v: number): void => {
          const idx = Math.round(clampNum(v, PITCH_INDEX_MIN, PITCH_INDEX_MAX));
          const classes = isKitTimbre(obj.timbreId) ? null : allowedPitchClasses(obj.timbreId, settings.simpleMode, field, graph);
          field.setPitchIndex(obj.id, classes === null ? idx : clampNum(snapToScale(idx, classes), PITCH_INDEX_MIN, PITCH_INDEX_MAX));
        } },
        { name: "timbre", get: (): number => timbreIndexById(obj.timbreId), set: (v: number): void => { field.setTimbreById(obj.id, timbreIdByIndex(v)); } },
        { name: "mute", get: (): number => obj.muted ? 1 : 0, set: (v: number): void => { obj.muted = v >= 0.5; } },
        { name: "kill", get: (): number => obj.killOnContact ? 1 : 0, set: (v: number): void => { obj.killOnContact = v >= 0.5; } },
        { name: "collision", get: (): number => obj.collisionEnabled ? 1 : 0, set: (v: number): void => { obj.collisionEnabled = v >= 0.5; } },
      ];
    }
    if (obj.type === "rail") {
      return [
        { name: "timbre", get: (): number => railTimbreIndexById(obj.railTimbre), set: (v: number): void => { field.setRailTimbreById(obj.id, railTimbreIdByIndex(v)); } },
      ];
    }
    if (obj.type === "lamp") {
      return [
        { name: "value", get: (): number => obj.lampValue, set: (v: number): void => { obj.lampValue = clampNum(v, 0, 1); } },
        { name: "hue", get: (): number => obj.lampHue, set: (v: number): void => {
          obj.lampHue = v - Math.floor(v);
          field.setLampColor(obj.id);
        } },
        { name: "sat", get: (): number => obj.lampSat, set: (v: number): void => {
          obj.lampSat = clampNum(v, 0, 1);
          field.setLampColor(obj.id);
        } },
      ];
    }
    if (obj.type === "timbre") {
      const params: NumericParam[] = [
        { name: "volume", get: (): number => obj.params.x, set: (v: number): void => {
          obj.params.x = clampNum(v, 0, 1);
          for (const timbre of obj.timbreIds) {
            activeAudio.setTimbreVolume(timbre, obj.params.x);
          }
        } },
        { name: "transpose", get: (): number => obj.params.z, set: (v: number): void => {
          obj.params.z = Math.round(clampNum(v, -TRANSPOSE_RANGE, TRANSPOSE_RANGE));
          for (const timbre of obj.timbreIds) {
            activeAudio.setTimbreTranspose(timbre, obj.params.z);
            field.setTimbreTranspose(timbre, obj.params.z);
          }
        } },
      ];
      return params;
    }
    if (obj.type === "knob") {
      return [
        { name: "value", get: (): number => obj.knobValue, set: (v: number): void => {
          const clamped = clampNum(v, 0, 1);
          obj.knobValue = obj.knobInt ? snapKnobValue(clamped, obj.knobMin, obj.knobMax) : clamped;
          field.setKnobIndicator(obj.id, "value", obj.knobValue);
        } },
      ];
    }
    if (obj.type === "envelope") {
      const envParam = (name: string, read: () => number, write: (v: number) => void): NumericParam => ({
        name,
        get: read,
        set: (v: number): void => {
          const c = clampNum(v, 0, 1);
          write(c);
          field.setKnobIndicator(obj.id, name, c);
          syncEnvelopeEngine();
        },
      });
      return [
        envParam("attack", (): number => obj.params.x, (v: number): void => { obj.params.x = v; }),
        envParam("decay", (): number => obj.params.y, (v: number): void => { obj.params.y = v; }),
        envParam("sustain", (): number => obj.params.z, (v: number): void => { obj.params.z = v; }),
        envParam("release", (): number => obj.params.w, (v: number): void => { obj.params.w = v; }),
      ];
    }
    if (obj.type === "global") {
      /**
       * The global node's simple/feature param setters mirror a scene-synced
       * setting, so a discrete toggle persists + room-syncs like the panel
       * handlers. Two guards keep that safe under the param-write machinery:
       * `serializingScene` (set while withParamBase resets driven params to base
       * for serialization) suppresses every reaction so serialization only moves
       * the value — without it persist() → serialize → withParamBase → set() →
       * persist() is an infinite re-entrant loop that freezes the tab. The
       * paramBase check skips the room commit while the param is wire-driven: a
       * continuous signal cannot cleanly room-sync (serialize captures the base,
       * not the live driven value), so a wired toggle stays local-only.
       */
      const featureParam = (cat: FeatureCategory): NumericParam => ({
        name: cat,
        get: (): number => settings.features[cat] ? 1 : 0,
        set: (v: number): void => {
          const on = v >= 0.5;
          if (on === settings.features[cat]) {
            return;
          }
          settings.features = { ...settings.features, [cat]: on };
          if (serializingScene) {
            return;
          }
          if (!paramBase.has(`${obj.id}|${cat}`)) {
            persist();
          }
          toolbar.setFeatureChecked(cat, on);
          applyFeatureGates();
          bindPanel();
        },
      });
      return [
        { name: "gravity", get: (): number => -settings.gravityY, set: (v: number): void => {
          const stored = -clampNum(v, 0, -GRAVITY_Y_MIN);
          particles.setGravityY(stored);
          settings.gravityY = stored;
        } },
        { name: "tempo", get: (): number => 60 / settings.clockPeriod, set: (v: number): void => {
          const period = 60 / clampNum(v, CLOCK_BPM_MIN, CLOCK_BPM_MAX);
          particles.setClockPeriod(period);
          settings.clockPeriod = period;
        } },
        { name: "simple", get: (): number => settings.simpleMode ? 1 : 0, set: (v: number): void => {
          const on = v >= 0.5;
          if (on === settings.simpleMode) {
            return;
          }
          settings.simpleMode = on;
          if (serializingScene) {
            return;
          }
          if (on && !settings.objectPlacement) {
            clearTouchPlay();
          }
          if (!paramBase.has(`${obj.id}|simple`)) {
            persist();
          }
          toolbar.setSimpleChecked(on);
          applySimpleMode();
        } },
        featureParam("node"),
      ];
    }
    if (obj.type === "group") {
      return groupSettableParams(id);
    }
    return [];
  }

  /**
   * A group's Param-in registry is the union of its leaf descendants' settable
   * param names (nested groups expanded): each aggregate entry reads the first
   * leaf carrying that name and writes every leaf carrying it, so a value wired to
   * the group fans out to the whole subtree. Leaves lacking the param are skipped,
   * so a mixed-type group only drives the members that own the param. The per-frame
   * overwrite keys its base per leaf (see applyParamWrites) — this aggregate setter
   * is the UI / panel path, not the driven-write path.
   */
  function groupSettableParams(groupId: string): NumericParam[] {
    const names: string[] = [];
    const seen = new Set<string>();
    for (const leafId of groupLeafIds(groupId)) {
      for (const p of settableParams(leafId)) {
        if (!seen.has(p.name)) {
          seen.add(p.name);
          names.push(p.name);
        }
      }
    }
    return names.map((name: string): NumericParam => ({
      name,
      get: (): number => {
        for (const leafId of groupLeafIds(groupId)) {
          const entry = settableParams(leafId).find((p: NumericParam): boolean => p.name === name);
          if (entry) {
            return entry.get();
          }
        }
        return 0;
      },
      set: (v: number): void => {
        for (const leafId of groupLeafIds(groupId)) {
          settableParams(leafId).find((p: NumericParam): boolean => p.name === name)?.set(v);
        }
      },
    }));
  }

  /**
   * Param names offered in the Param-in dropdown for an entity: the subset of
   * settableParams whose manual UI is currently exposed (feature toggles,
   * emitter kind). Constrains new wiring choices only — an edge already bound
   * to a filtered-out param keeps driving and keeps displaying its param
   * (settableParams stays the full list for applyParamWrites).
   *
   * The global node's simple/node mode params are never offered: a mode toggle
   * is owner-state, not a continuously drivable value, and a signal driving it
   * cannot cleanly room-sync (the room mirrors the un-driven base). They stay
   * settable only by the owner's direct controls (the scene-settings panel and
   * the node's own toggle). gravity/tempo remain wirable — those are real
   * continuous values.
   */
  function offeredParams(id: string): string[] {
    const drop = new Set<string>(["offset"]);
    if (objectById(id)?.type === "global") {
      drop.add("simple");
      drop.add("node");
    }
    const em = emitterById(id);
    if (em && em.kind === "sync") {
      drop.add("rate");
      drop.add("spd range");
      drop.add("life range");
    }
    if (!settings.features.physics) {
      drop.add("mass");
    }
    if (!settings.features["3d"]) {
      drop.add("rot x");
      drop.add("rot y");
      drop.add("amp z");
      drop.add("period z");
      drop.add("phase z");
    }
    return settableParams(id)
      .map((p: NumericParam): string => p.name)
      .filter((name: string): boolean => !drop.has(name));
  }

  /**
   * The bulk-edit bucket an entity falls into: a placed object contributes its
   * ObjectType, an emitter its kind as Stream (full) / Sequencer (sync), and
   * anything unresolved is excluded. Emitters and objects share `settableParams`
   * accessors, so only this bucketing differs between them.
   */
  function bulkKindOf(id: string): BulkKind | null {
    const o = objectById(id);
    if (o) {
      return o.type;
    }
    const em = emitterById(id);
    if (em) {
      return em.kind === "sync" ? "sequencer" : "stream";
    }
    return null;
  }

  function groupBulkDeps(): GroupBulkDeps {
    return {
      leafIds: groupLeafIds,
      typeOf: bulkKindOf,
      paramNames: (id: string): string[] => offeredParams(id),
      paramValue: (id: string, name: string): number =>
        settableParams(id).find((p: NumericParam): boolean => p.name === name)?.get() ?? 0,
      isBound: (id: string, name: string): boolean =>
        graph.edgesTo(id, "setParamIn").some((e): boolean => e.param === name),
    };
  }

  function groupBulkModel(groupId: string): GroupBulkType[] {
    return collectGroupBulkTypes(groupId, groupBulkDeps());
  }

  /**
   * Writes one settable param to every same-kind leaf of the selected group,
   * routed through the per-type accessor so pitch / timbre still recolor their
   * mesh. Coalesced into one undo step per drag gesture (pointerup ends it), the
   * same way single-entity slider edits are. Objects persist; emitters resync to
   * the particle system — a mixed group does both.
   */
  function editGroupBulkParam(type: BulkKind, name: string, value: number): void {
    const sel = field.selectedObject();
    if (!sel || sel.type !== "group") {
      return;
    }
    beginEdit(`groupBulk:${sel.id}:${type}:${name}`);
    let touchedObject = false;
    let touchedEmitter = false;
    for (const id of groupLeafIds(sel.id)) {
      if (bulkKindOf(id) !== type) {
        continue;
      }
      const isEmitter = emitterById(id) !== null;
      recordBefore(isEmitter ? "emitter" : "obj", id);
      settableParams(id).find((p: NumericParam): boolean => p.name === name)?.set(value);
      if (isEmitter) {
        touchedEmitter = true;
      } else {
        touchedObject = true;
      }
    }
    bindPanel();
    if (touchedObject) {
      persistObjects();
    }
    if (touchedEmitter) {
      syncEmitters();
    }
  }

  /**
   * Paints the wiring-mode frame: a non-null target gets a distinct WIRING_TARGET_COLOR
   * OBB frame, while null restores the white selection-color frame on the selected entity.
   * The frame set is exclusive, so the target frame replaces the selection frame for the
   * duration of a pending wire.
   */
  function highlightWireTarget(id: string | null): void {
    if (id !== null) {
      const mesh = field.meshById(id) ?? emitterField.meshById(id);
      selectionFrames.apply(mesh ? [{ mesh, color: WIRING_TARGET_COLOR }] : []);
      return;
    }
    const mesh = field.selectedMesh() ?? emitterField.selectedMesh();
    selectionFrames.apply(mesh ? [{ mesh, color: SELECTION_FRAME_COLOR }] : []);
  }

  function currentSelectedId(): string | null {
    return field.selectedObject()?.id ?? emitterField.selectedEmitter()?.id ?? null;
  }

  function liveCountIds(): string[] {
    const ids: string[] = [];
    for (const o of field.list()) {
      if (o.type === "count") {
        ids.push(o.id);
      }
    }
    return ids;
  }

  function myRoomUid(): string {
    return roomUidOverride ?? myUidCached ?? "";
  }

  function isForeignClaimed(id: string): boolean {
    const holder = roomClaims.get(id);
    return holder !== undefined && holder !== myRoomUid();
  }

  function colorForUid(uid: string): THREE.Color {
    const index = roomColors.get(uid) ?? 0;
    return new THREE.Color(ROOM_COLORS[index % ROOM_COLORS.length]);
  }

  function colorCssForUid(uid: string): string {
    return `#${colorForUid(uid).getHexString()}`;
  }

  /**
   * Paints the selection frames: the local selection in white (suppressed when
   * that object is claimed by another player, so its claim color shows through),
   * plus one frame per remotely-claimed object in its holder's room color.
   */
  function refreshSelectionFrames(): void {
    const frames: FrameRequest[] = [];
    if (!ports.isPending()) {
      const id = currentSelectedId();
      const mesh = settings.simpleMode
        ? field.selectedMesh() ?? emitterField.selectedMesh()
        : field.selectedObject()
          ? field.selectedMesh()
          : emitterField.selectedEmitter()
            ? emitterField.selectedMesh()
            : null;
      if (mesh && (id === null || !isForeignClaimed(id))) {
        frames.push({ mesh, color: SELECTION_FRAME_COLOR });
      }
    }
    for (const [objId, holder] of roomClaims) {
      if (holder === myRoomUid()) {
        continue;
      }
      const mesh = field.meshById(objId) ?? emitterField.meshById(objId);
      if (mesh) {
        frames.push({ mesh, color: colorForUid(holder) });
      }
    }
    if (roomActive && semiSelectedId !== null) {
      const mesh = field.meshById(semiSelectedId) ?? emitterField.meshById(semiSelectedId);
      if (mesh) {
        frames.push({ mesh, color: colorForUid(myRoomUid()) });
      }
    }
    if (roomActive) {
      const selectedId = currentSelectedId();
      for (const id of liveCountIds()) {
        if (id === selectedId) {
          continue;
        }
        const owner = counterSync.ownerOf(id);
        if (owner === null || !roomColors.has(owner)) {
          continue;
        }
        const mesh = field.meshById(id);
        if (mesh) {
          frames.push({ mesh, color: colorForUid(owner) });
        }
      }
    }
    selectionFrames.apply(frames);
  }

  /** Locks the object panel when the selected object is claimed by another player. */
  function applyClaimEnforcement(): void {
    const id = currentSelectedId();
    toolbar.setObjectPanelLocked(id !== null && isForeignClaimed(id));
  }

  /**
   * Mirrors what the local player is currently "on" into a single room edit claim
   * (exclusive at the DO, 1 per player): the normal selection while in placement
   * mode, else the touchplay semi-selection. Switching targets releases the prior
   * claim and claims the new one; a normal selection supersedes any semi-selection.
   * The holder is shown to other players and gives them a locked panel / widget on
   * that object.
   */
  function updateActiveClaim(): void {
    if (!roomActive) {
      return;
    }
    let target: string | null;
    if (touchPlayActive()) {
      target = semiSelectedId;
    } else {
      if (currentSelectedId() !== null) {
        semiSelectedId = null;
      }
      target = currentSelectedId();
    }
    if (target === lastClaimedId) {
      return;
    }
    if (lastClaimedId !== null) {
      roomReleaseSend(lastClaimedId);
    }
    if (target !== null) {
      roomClaimSend(target);
    }
    lastClaimedId = target;
  }

  /**
   * Marks the root transform cluster of a UI node the local player is operating in
   * touchplay as semi-selected: a selection frame in the player's own presence color
   * is drawn for it (and, once claimed, for every other viewer), but no panel opens —
   * unlike a normal selection. One per player, following whatever UI node they last
   * touched. A stale normal selection carried in from placement mode is cleared so the
   * local frame/gizmo follow the semi-selection rather than lingering on the old
   * object (the claim already moved, so remote viewers were correct). No-op outside a room.
   */
  function onSemiSelect(rootId: string): void {
    if (!roomActive || semiSelectedId === rootId) {
      return;
    }
    semiSelectedId = rootId;
    if (field.selectedObject() !== null || emitterField.selectedEmitter() !== null) {
      field.deselect();
      emitterField.deselect();
      bindPanel();
    }
    updateActiveClaim();
    reassignCounterOwners(rootId);
    refreshSelectionFrames();
  }

  /**
   * Hands every `count` descendant of the just-semi-selected root cluster to the
   * local player: the operator of a cluster becomes the authority for its counters
   * (owner is sticky, so it stays after moving on). Each newly-taken counter is
   * announced on the discrete owner channel so every client repaints and the DO seeds
   * late joiners. A non-group target (the UI node itself) has no descendants, so a
   * standalone counter is never picked up and stays unowned — each client counts it
   * locally, as intended.
   */
  function reassignCounterOwners(rootId: string): void {
    const ids = new Set<string>();
    entities.collectAllDescendants(rootId, ids, new Set<string>());
    const me = myRoomUid();
    let changed = false;
    for (const id of ids) {
      if (objectById(id)?.type !== "count") {
        continue;
      }
      if (counterSync.setOwner(id, me)) {
        roomCountOwnerSend(id);
        changed = true;
      }
    }
    if (changed) {
      onCounterOwnershipChange();
    }
  }

  function onRoomPresence(colors: Record<string, number>, claims: Record<string, string>, owner: string, names: Record<string, string>, isPublic: boolean): void {
    roomColors.clear();
    for (const [uid, color] of Object.entries(colors)) {
      roomColors.set(uid, color);
    }
    roomClaims.clear();
    for (const [objId, holder] of Object.entries(claims)) {
      roomClaims.set(objId, holder);
    }
    roomNames.clear();
    for (const [uid, name] of Object.entries(names)) {
      roomNames.set(uid, name);
    }
    roomOwnerUid = owner;
    roomIsPublic = isPublic;
    applyRoomPublicState();
    applyRoomGuestLock();
    refreshSelectionFrames();
    applyClaimEnforcement();
    refreshGizmo();
  }

  /**
   * A guest is anyone in an owned (private) room whose uid is not the authoritative
   * owner uid from presence — including the window before the owner is known (owner
   * ""), which fails closed. An ownerless public room (the shared sandbox) has no
   * guests: every participant is unlocked, so `roomIsPublic` short-circuits to false
   * before the owner-"" fail-closed branch (which would otherwise lock everyone).
   * Solo (non-room) users are never guests. The owner uid is compared against
   * myRoomUid so the DEV uid override used for two-context testing resolves the same
   * identity the hello sent.
   */
  function isRoomGuest(): boolean {
    return roomActive && !roomIsPublic && (roomOwnerUid === "" || myRoomUid() !== roomOwnerUid);
  }

  /**
   * Pins the public room's fixed mode and locks its toggles. An ownerless public
   * room is always SimpleMode OFF + Node ON so every participant shares the same
   * full-feature sandbox; the values are forced only when they actually differ (so a
   * repeat presence does no work) and the SimpleMode / Node toggles are disabled via
   * the mode lock. A private room or solo session leaves both alone and releases the
   * lock.
   */
  function applyRoomPublicState(): void {
    if (roomIsPublic) {
      if (settings.simpleMode) {
        settings.simpleMode = false;
        toolbar.setSimpleChecked(false);
        applySimpleMode();
      }
      if (!settings.features.node) {
        setFeatureEnabled("node", true);
        toolbar.setFeatureChecked("node", true);
      }
    }
    toolbar.setRoomModeLock(roomIsPublic);
  }

  /**
   * Locks the owner-only affordances (scene reset / scene settings) for room
   * guests, and re-derives the foreign lock + top chips since guest status feeds
   * both (a guest is always foreign-locked, and its scene chip is suppressed).
   */
  function applyRoomGuestLock(): void {
    toolbar.setRoomGuestLock(isRoomGuest());
    applyForeignLock();
    renderTopChips();
  }

  /**
   * Simple mode keeps the focus feedback (halo + gizmo) but never opens the
   * right object panel — pitch is tapped, move is a direct drag, delete is an
   * edge drop, so the panel controls have no role in that layer.
   */
  function bindPanel(): void {
    if (settings.simpleMode) {
      toolbar.syncControls({ kind: "none" });
      updateActiveClaim();
      refreshSelectionFrames();
      applyClaimEnforcement();
      refreshGizmo();
      sequencerPanel?.rebuild();
      return;
    }
    if (activeTool === "global" && field.list().some((o: PlacedObject): boolean => o.type === "global")) {
      setActiveTool(null);
      return;
    }
    const obj = field.selectedObject();
    const em = emitterField.selectedEmitter();
    if (obj) {
      toolbar.syncControls({ kind: "object", template: obj, selected: true });
    } else if (em) {
      toolbar.syncControls({ kind: "emitter", params: em, selected: true });
    } else if (activeTool && isEmitterTool(activeTool)) {
      toolbar.syncControls({ kind: "emitter", params: emitterSpawn, selected: false });
    } else if (activeTool) {
      toolbar.syncControls({ kind: "object", template: spawn, selected: false });
    } else {
      toolbar.syncControls({ kind: "none" });
    }
    updateObjectPanelSide();
    updateActiveClaim();
    refreshSelectionFrames();
    applyClaimEnforcement();
    refreshParamLocks();
    refreshGizmo();
    ports.renderMainPorts();
    sequencerPanel?.rebuild();
    syncCopyPasteButtons();
    toolbar.setGlobalPlaced(field.list().some((o: PlacedObject): boolean => o.type === "global"));
  }

  /**
   * Drops the selection panel to bottom-right when the selected entity sits in
   * the top-right quadrant (where the panel's default home would occlude it).
   * Run on selection change and every frame so it keeps up while the object is
   * dragged or otherwise moving.
   */
  function updateObjectPanelSide(): void {
    const sel = field.selectedObject() ?? emitterField.selectedEmitter();
    if (sel === null) {
      toolbar.setObjectPanelTopOffset(0);
      toolbar.setObjectPanelBottom(false);
      return;
    }
    const ndc = sel.position.clone().project(rig.activeCamera);
    const sx = (ndc.x * 0.5 + 0.5) * window.innerWidth;
    const sy = (-ndc.y * 0.5 + 0.5) * window.innerHeight;
    const atBottom = sx > window.innerWidth * 0.5 && sy < window.innerHeight * 0.5;
    if (!atBottom) {
      const menu = document.querySelector(".top-controls");
      const menuH = toolbar.isRadialMode() && menu instanceof HTMLElement ? Math.round(menu.getBoundingClientRect().height) + 4 : 0;
      toolbar.setObjectPanelTopOffset(menuH);
      toolbar.setObjectPanelBottom(false);
      return;
    }
    toolbar.setObjectPanelTopOffset(0);
    const seq = document.querySelector(".seq-panel");
    const seqShown = seq instanceof HTMLElement && getComputedStyle(seq).display !== "none";
    const clearance = seqShown ? Math.round(seq.getBoundingClientRect().height) + 24 : 12;
    toolbar.setObjectPanelBottom(true, clearance);
  }

  /**
   * Updates the selected entity's slider locks from the Param-in edges driving it.
   * Run on selection change and every frame, so a locked slider tracks the live
   * (overwritten) value the way a meter would.
   */
  let prevGlobalDriven = new Set<string>();

  function refreshParamLocks(): void {
    const obj = field.selectedObject();
    const em = emitterField.selectedEmitter();
    const id = obj ? obj.id : em ? em.id : null;
    const driven = new Map<string, number>();
    if (id && settings.features.node) {
      const params = settableParams(id);
      for (const e of graph.edgesTo(id, "setParamIn")) {
        if (e.param === "") {
          continue;
        }
        const entry = params.find((p: { name: string }): boolean => p.name === e.param);
        if (entry) {
          driven.set(e.param, entry.get());
        }
      }
    }
    const emitInDriven = settings.features.node && em !== null && graph.edgesTo(em.id, "emitIn").length > 0;
    toolbar.applyLocks(emitInDriven, driven);
    refreshGlobalParamLocks();
  }

  /**
   * Mirrors the object-panel Param-in lock onto the scene controls a global node
   * drives: each driven scene param locks + follows its live override value, and a
   * released wire refreshes the freed control to the restored base (applyParamWrites
   * has already written the base back to settings by the time this runs).
   */
  function refreshGlobalParamLocks(): void {
    const globalObj = field.list().find((o: PlacedObject): boolean => o.type === "global");
    const driven = new Map<string, number>();
    if (globalObj) {
      const params = settableParams(globalObj.id);
      for (const e of graph.edgesTo(globalObj.id, "setParamIn")) {
        if (e.param === "") {
          continue;
        }
        const entry = params.find((p: { name: string }): boolean => p.name === e.param);
        if (entry) {
          driven.set(e.param, entry.get());
        }
      }
    }
    for (const name of prevGlobalDriven) {
      if (driven.has(name)) {
        continue;
      }
      if (name === "gravity" || name === "tempo") {
        toolbar.syncSceneSettings(settings.clockPeriod, settings.gravityY);
      } else if (name === "simple") {
        toolbar.setSimpleChecked(settings.simpleMode);
      } else if (name === "node") {
        toolbar.setFeatureChecked("node", settings.features.node);
      }
    }
    prevGlobalDriven = new Set(driven.keys());
    toolbar.applyGlobalParamLocks(driven);
  }

  function refreshGizmo(): void {
    const selId = currentSelectedId();
    if (selId !== null && isForeignClaimed(selId)) {
      gizmo.detach();
      gizmoIs2D = rig.canPlace();
      return;
    }
    const obj = field.selectedObject();
    const em = emitterField.selectedEmitter();
    if (obj) {
      const groupBr = obj.type === "group"
        ? (): number => {
          const groupMesh: THREE.Mesh | null = field.meshById(obj.id);
          return groupBoundingRadiusFromMesh(obj.position, groupMesh);
        }
        : undefined;
      gizmo.attach(objectTarget(obj, groupBr), !rig.canPlace());
    } else if (em) {
      gizmo.attach(emitterTarget(em), !rig.canPlace());
    } else {
      gizmo.detach();
    }
    gizmoIs2D = rig.canPlace();
  }

  let gizmoIs2D = true;

  function syncGizmoMode(): void {
    if (!gizmo.hasTarget()) {
      return;
    }
    if (rig.canPlace() !== gizmoIs2D) {
      refreshGizmo();
    }
    gizmo.sync();
    gizmo.orient();
  }

  let objectPanelEl: HTMLElement | null = null;
  let mainPanelEl: HTMLElement | null = null;
  let panelLockTimer: ReturnType<typeof setTimeout> | null = null;

  function lockPanelOnTouch(ev: PointerEvent): void {
    if (ev.pointerType !== "touch") return;
    if (panelLockTimer !== null) clearTimeout(panelLockTimer);
    objectPanelEl?.classList.add("select-locked");
    panelLockTimer = setTimeout((): void => {
      objectPanelEl?.classList.remove("select-locked");
      panelLockTimer = null;
    }, 350);
  }

  let clipboard: Clipboard | null = null;

  /**
   * Pushes the feature-category gates into the simulation: objects of a
   * disabled type hide, leave the SDF/force pack and the pick pass, and
   * a selected object of a disabled type is deselected so the panel never
   * targets an invisible entity. Colliders (force/funnel) are never disabled —
   * a placed scene keeps its physics/3D behavior when those features turn off;
   * only their palette/panel operation surface is gated elsewhere.
   */
  function applyFeatureGates(): void {
    const disabled: ObjectType[] = [];
    if (toolbar.uiView) disabled.push("timbre", "pitchQuantize", "global");
    field.setDisabledTypes(disabled);
    const sel = field.selectedObject();
    if (sel && disabled.includes(sel.type)) field.deselect();
  }

  /**
   * Migrates every orbit (placed and the spawn template) to clock-synced: orbit
   * motion always tracks the room period now (rotSpeed in rev/period, period as
   * a fraction of the room period). A free-run orbit (deg/s, absolute seconds)
   * loaded from an older scene is converted against the current clock period so
   * its visible motion stays continuous across the migration — only that it now
   * follows tempo changes. Synced orbits are left untouched.
   */
  function reconcileOrbitClockSync(): void {
    const T = settings.clockPeriod;
    let changed = false;
    const convert = (o: OrbitParams): void => {
      if (o.clockSync) {
        return;
      }
      o.rotSpeed.multiplyScalar(T / 360);
      o.periodPct.multiplyScalar(1 / T);
      o.clockSync = true;
      changed = true;
    };
    for (const o of field.list()) {
      if (o.type === "orbit") {
        convert(o.orbit);
      }
    }
    convert(spawn.orbit);
    if (changed) {
      persistObjects();
    }
  }

  /**
   * Whether a pitched body sounds a pitch outside the pentatonic set: a sounding
   * box/sphere/funnel (kit timbres excepted — their pitchIndex selects a kit
   * piece, not an audible pitch) or either end of a rail glissando. Drives whether
   * entering simple mode needs the snap-confirm dialog.
   */
  function hasNonPentatonicPitched(): boolean {
    return field.list().some((o: PlacedObject): boolean => {
      if ((o.type === "box" || o.type === "sphere" || o.type === "funnel") && !isKitTimbre(o.timbreId)) {
        return !isPentatonic(o.pitchIndex);
      }
      if (o.type === "rail") {
        return !isPentatonic(o.pitchIndex) || !isPentatonic(o.pitchIndexEnd);
      }
      return false;
    });
  }

  /**
   * Snaps every pitched body whose timbre is governed by a connected
   * PitchQuantize mask onto the nearest allowed semitone, so wiring a scale (or
   * editing its mask) re-evaluates the scene's pitches immediately rather than
   * only on the next manual step. Kit timbres (piece index, not a pitch) and
   * rails (own RailTimbre catalog, no Timbre node governs them) are left
   * untouched; an ungoverned / chromatic timbre resolves to null and is skipped.
   * Caller owns the undo snapshot; this only mutates + persists when something
   * actually moved.
   */
  function pitchMaskSnapTargets(): { id: string; snapped: number }[] {
    const targets: { id: string; snapped: number }[] = [];
    for (const o of field.list()) {
      if ((o.type !== "box" && o.type !== "sphere" && o.type !== "funnel") || isKitTimbre(o.timbreId)) {
        continue;
      }
      const classes = allowedPitchClasses(o.timbreId, settings.simpleMode, field, graph);
      if (classes === null) {
        continue;
      }
      const snapped = snapToScale(o.pitchIndex, classes);
      if (snapped !== o.pitchIndex) {
        targets.push({ id: o.id, snapped });
      }
    }
    return targets;
  }

  function applyPitchMaskSnap(targets: { id: string; snapped: number }[]): void {
    if (targets.length === 0) {
      return;
    }
    for (const target of targets) {
      field.setPitchIndex(target.id, target.snapped);
    }
    persistObjects();
  }

  function requantizePitchToMasks(): void {
    applyPitchMaskSnap(pitchMaskSnapTargets());
  }

  /** Snaps every pitched body's pitch (and rail end pitch) to the nearest pentatonic semitone. Irreversible except by the paired undo step. */
  function snapAllPitchedToPentatonic(): void {
    for (const o of field.list()) {
      if ((o.type === "box" || o.type === "sphere" || o.type === "funnel") && !isKitTimbre(o.timbreId)) {
        field.setPitchIndex(o.id, snapToPentatonic(o.pitchIndex));
      } else if (o.type === "rail") {
        field.setPitchIndex(o.id, snapToPentatonic(o.pitchIndex));
        field.setPitchIndexEnd(o.id, snapToPentatonic(o.pitchIndexEnd));
      }
    }
    persistObjects();
  }

  /**
   * Applies the current simpleMode to the editor: entering simple disarms any
   * armed tool so the advanced placement path cannot fire, and the panel
   * rebind re-evaluates the selection UI for the new layer.
   */
  function applySimpleMode(): void {
    audio.setSceneTimbreOverride(settings.simpleMode ? "bell" : null);
    if (settings.simpleMode) {
      setActiveTool(null);
      rig.setMode("2d");
    } else {
      rig.setMode(settings.viewMode);
    }
    applyFeatureGates();
    bindPanel();
  }

  /**
   * Leaves play mode by forcing object placement back on, unwinding the play-mode
   * side effects (release multi-drags, restore camera/chrome). Simple and play
   * mode are mutually exclusive, so entering Simple clears play through here. Does
   * not persist — the caller persists after settling both flags.
   */
  function clearTouchPlay(): void {
    settings.objectPlacement = true;
    applyTouchPlayActive();
  }

  /**
   * Reflects the play-mode (active TouchPlay) state onto the editing chrome: the
   * object-placement palette and the sequencer panel UI are hidden while playing
   * (the top-left mode menu stays so the user can leave), and any selection /
   * gizmo is cleared so a performance touch never grabs an editing handle. Hiding
   * the sequencer is UI-only — its emitter sequencing keeps running.
   */
  function applyPlayModeChrome(): void {
    const playing = touchPlayActive();
    toolbar.setPlayModeHidden(playing);
    sequencerPanel?.setPlayModeHidden(playing);
    if (playing) {
      field.deselect();
      emitterField.deselect();
      bindPanel();
    }
  }

  /**
   * Re-applies the effective TouchPlay state (feature ∧ toggle) across every
   * consumer in one place: the camera lock + its checkbox, the pointer's play
   * state, the editing chrome, and the top-bar toggle button. Called from the
   * feature change, the toggle click, and scene restore so the camera / chrome /
   * pointer can never end up half-applied when the effective state flips.
   */
  function applyTouchPlayActive(): void {
    if (!touchPlayActive()) {
      pointer.clearTouchPlayState();
      if (semiSelectedId !== null) {
        semiSelectedId = null;
        updateActiveClaim();
        refreshSelectionFrames();
      }
    }
    rig.setCameraEnabled(settings.cameraEnabled);
    toolbar.setCameraState(settings.cameraEnabled);
    applyPlayModeChrome();
    toolbar.setTouchPlayToggleActive(settings.objectPlacement);
    toolbar.setDimensionToggleVisible(!settings.simpleMode && settings.features["3d"]);
    toolbar.setDimensionToggleActive(settings.viewMode === "3d");
  }

  function setFeatureEnabled(category: FeatureCategory, on: boolean): void {
    settings.features = { ...settings.features, [category]: on };
    persist();
    applyFeatureGates();
    if (!on) {
      const disabledTools: readonly Tool[] =
        category === "node" ? NODE_GATED_TOOLS
          : category === "physics" ? ["force"]
          : category === "3d" ? ["funnel"]
          : [];
      if (activeTool !== null && disabledTools.includes(activeTool)) {
        setActiveTool(null);
      }
      if (category === "node") {
        ports.cancelPending();
      }
    }
    bindPanel();
  }

  /**
   * Display name of an entity: the user-set name, else a type-derived fallback —
   * pitched bodies read as `BOX[C4]` (the note name, square brackets so
   * count's call parens stay unambiguous), values as their operator symbol,
   * emitters as their kind.
   */
  function entityExprLabel(id: string): string {
    const obj = field.list().find((o: PlacedObject): boolean => o.id === id);
    if (obj) {
      if (obj.name) {
        return obj.name;
      }
      const notation = getLanguage() === "ja" ? "ja" : "en";
      if (obj.type === "box" || obj.type === "sphere" || obj.type === "funnel") {
        return `${obj.type.toUpperCase()}[${noteNameForPitchIndex(effectivePitchIndex(obj.pitchIndex, obj.timbreId, field.timbreTransposeFor(obj.timbreId)), true, notation)}]`;
      }
      if (obj.type === "rail") {
        return `RAIL[${noteNameForPitchIndex(obj.pitchIndex, true, notation)}→${noteNameForPitchIndex(obj.pitchIndexEnd, true, notation)}]`;
      }
      if (obj.type === "calc") {
        return OPERATOR_LABELS[obj.operator];
      }
      return obj.type;
    }
    const em = emitterField.list().find((e: Emitter): boolean => e.id === id);
    if (em) {
      return em.name || (em.kind === "sync" ? "Rhythm" : "Stream");
    }
    return "";
  }

  const applyPitch = (pitchIndex: number): void =>
    editObject("pitch", { pitchIndex }, (): void => {
      spawn.pitchIndex = pitchIndex;
    });
  const applyPitchEnd = (pitchIndex: number): void =>
    editObject("pitchEnd", { pitchIndexEnd: pitchIndex }, (): void => {
      spawn.pitchIndexEnd = pitchIndex;
    });

  function resetSceneToBlankInPlace(): void {
    const blank = { ...sceneBundle(), objects: [], emitters: [], connections: [] };
    applySceneInPlace(blank, true);
    persistScene(blank);
    saveMicImport(null);
    saveProvenance(null);
    setProvenance(null);
    focusEpoch++;
    micLoading = false;
    void applySceneMic(null, null);
  }

  function resetSceneForGuestJoin(): void {
    const blank = { ...sceneBundle(), objects: [], emitters: [], connections: [] };
    applySceneInPlace(blank, true);
    setProvenance(null);
    toolbar.setForeignSceneLock(false);
  }

  toolbar = setupToolbar(settings, emitterSpawn, {
    onViewMode: (mode: ViewMode): void => {
      rig.setMode(mode);
      settings.viewMode = mode;
      toolbar.setDimensionToggleActive(mode === "3d");
      persist();
    },
    onUndo: (): void => undo(),
    onRedo: (): void => redo(),
    onBounce: (value: number): void =>
      editObject("bounce", { restitution: value }, (): void => {
        spawn.restitution = value;
        settings.bounce = value;
        persist();
      }),
    onFriction: (value: number): void =>
      editObject("friction", { friction: value }, (): void => {
        spawn.friction = value;
        settings.friction = value;
        persist();
      }),
    onMass: (value: number): void =>
      editObject("mass", { mass: value }, (): void => {
        spawn.mass = value;
        settings.mass = value;
        persist();
      }),
    onMassEnabled: (enabled: boolean): void =>
      editObject("massEnabled", { massEnabled: enabled }, (): void => {
        spawn.massEnabled = enabled;
      }),
    onKillOnContact: (enabled: boolean): void =>
      editObject("killOnContact", { killOnContact: enabled }, (): void => {
        spawn.killOnContact = enabled;
      }),
    onFade: (value: number): void =>
      editObject("fade", { fade: value }, (): void => {
        spawn.fade = value;
      }),
    onCollisionEnabled: (enabled: boolean): void =>
      editObject("collisionEnabled", { collisionEnabled: enabled }, (): void => {
        spawn.collisionEnabled = enabled;
      }),
    onValueOperator: (operator: ValueOperator): void => {
      const sel = field.selectedObject();
      editObject("operator", { operator }, (): void => {
        spawn.operator = operator;
      });
      if (sel && sel.type === "calc") {
        pruneValuePorts(sel);
      }
    },
    onValueOperand: (value: number): void =>
      editObject("operand", { operand: value }, (): void => {
        spawn.operand = value;
      }),
    valueOperand2Wired: (id: string): boolean => graph.edgesTo(id, "operand2In").length > 0,
    onDecayS: (seconds: number): void =>
      editObject("decayS", { decayS: seconds }, (): void => {
        spawn.decayS = seconds;
      }),
    onDelayS: (seconds: number): void =>
      editObject("delayS", { delayS: seconds }, (): void => {
        spawn.delayS = seconds;
      }),
    onRouterHoldS: (seconds: number): void =>
      editObject("routerHoldS", { routerHoldS: seconds }, (): void => {
        spawn.routerHoldS = seconds;
      }),
    onRouterHoldResetOnPulse: (enabled: boolean): void =>
      editObject("routerHoldResetOnPulse", { routerHoldResetOnPulse: enabled }, (): void => {
        spawn.routerHoldResetOnPulse = enabled;
      }),
    onSetSeqParam: (id: string, key: "seqMin" | "seqMax" | "seqLen" | "seqCycleLength", value: number): void => {
      const obj = field.list().find((o): boolean => o.id === id);
      if (!obj) {
        return;
      }
      field.select(obj);
      const patch: Partial<ObjectTemplate> = key === "seqMin" ? { seqMin: value }
        : key === "seqMax" ? { seqMax: value }
        : key === "seqLen" ? { seqLen: value }
        : { seqCycleLength: value };
      editObject(`seq:${id}:${key}`, patch, (): void => {
        if (key === "seqMin") {
          spawn.seqMin = value;
        } else if (key === "seqMax") {
          spawn.seqMax = value;
        } else if (key === "seqLen") {
          spawn.seqLen = value;
        }
      });
    },
    onTimerMode: (mode: TimerMode): void =>
      editObject("timerMode", { timerMode: mode }, (): void => {
        spawn.timerMode = mode;
      }),
    onButtonMode: (mode: ButtonMode): void => {
      editObject("buttonMode", { buttonMode: mode }, (): void => {
        spawn.buttonMode = mode;
      });
    },
    onButtonPulseOnReset: (enabled: boolean): void =>
      editObject("buttonPulseOnReset", { buttonPulseOnReset: enabled }, (): void => {
        spawn.buttonPulseOnReset = enabled;
      }),
    onLampValue: (value: number): void =>
      editObject("lampValue", { lampValue: value }, (): void => {
        spawn.lampValue = value;
      }),
    onLampHue: (hue: number): void =>
      editObject("lampHue", { lampHue: hue }, (): void => {
        spawn.lampHue = hue;
      }),
    onLampSat: (sat: number): void =>
      editObject("lampSat", { lampSat: sat }, (): void => {
        spawn.lampSat = sat;
      }),
    onKnobMin: (value: number): void =>
      editObject("knobMin", { knobMin: value }, (): void => {
        spawn.knobMin = value;
      }),
    onKnobMax: (value: number): void =>
      editObject("knobMax", { knobMax: value }, (): void => {
        spawn.knobMax = value;
      }),
    onKnobInt: (enabled: boolean): void =>
      editObject("knobInt", { knobInt: enabled }, (): void => {
        spawn.knobInt = enabled;
        if (enabled) {
          if (spawn.knobMax === KNOB_MAX_DEFAULT) {
            spawn.knobMax = KNOB_INT_DEFAULT_MAX;
          }
          spawn.knobMin = Math.round(spawn.knobMin);
          spawn.knobMax = Math.round(spawn.knobMax);
        }
      }),
    onTimbreSet: (timbres: Timbre[]): void => {
      editObject("timbreSet", { timbreIds: timbres }, (): void => {
        spawn.timbreIds = [...timbres];
        if (timbres.length > 0) {
          spawn.timbreId = timbres[0];
        }
      });
      const sel = field.selectedObject();
      if (sel && sel.type === "timbre") {
        syncTimbreEngine();
        requantizePitchToMasks();
      }
    },
    onTimbreParam: (name: string, value: number): void => {
      const sel = field.selectedObject();
      if (!sel || sel.type !== "timbre") {
        return;
      }
      const param = settableParams(sel.id).find((p: NumericParam): boolean => p.name === name);
      if (!param || param.get() === value) {
        return;
      }
      beginEdit(`timbreParam:${name}`);
      recordBefore("obj", sel.id);
      param.set(value);
      persistObjects();
    },
    onSize: (params: THREE.Vector4): void =>
      editObject("size", { params }, (): void => {
        spawn.params.copy(params);
      }),
    onArc: (arcDeg: number): void =>
      editObject("arc", { arcDeg }, (): void => {
        spawn.arcDeg = arcDeg;
      }),
    onOrbit: (params: OrbitParams): void =>
      editObject("orbit", { orbit: params }, (): void => {
        spawn.orbit.rotSpeed.copy(params.rotSpeed);
        spawn.orbit.amp.copy(params.amp);
        spawn.orbit.periodPct.copy(params.periodPct);
        spawn.orbit.phasePct.copy(params.phasePct);
        spawn.orbit.clockSync = params.clockSync;
        spawn.orbit.play = params.play;
      }),
    orbitTargetMode: (id: string): boolean => graph.edgesTo(id, "target").length > 0,
    onPiece: (pieceIndex: number): void =>
      editObject("piece", { pitchIndex: pieceIndex }, (): void => {
        spawn.pitchIndex = pieceIndex;
      }),
    onRailTimbre: (railTimbre: RailTimbre): void =>
      editObject("railTimbre", { railTimbre }, (): void => {
        spawn.railTimbre = railTimbre;
      }),
    onTimbre: (timbre: Timbre): void =>
      editObject("timbre", { timbreId: timbre }, (): void => {
        spawn.timbreId = timbre;
      }),
    onObjectVolume: (value: number): void =>
      editObject("objVolume", { volume: value }, (): void => {
        spawn.volume = value;
      }),
    onObjectMuted: (enabled: boolean): void =>
      editObject("muted", { muted: enabled }, (): void => {
        spawn.muted = enabled;
      }),
    onName: (name: string): void => {
      editObject("name", { name }, (): void => {
        editEmitter("name", { name }, (): void => {});
      });
    },
    groupBulkModel: (id: string): GroupBulkType[] => groupBulkModel(id),
    onGroupBulkParam: (type: BulkKind, name: string, value: number): void =>
      editGroupBulkParam(type, name, value),
    buildGroupExpressions: (id: string): ExprLine[] =>
      buildGroupExpressions(id, graph, field.list(), entityExprLabel),
    entityLabel: entityExprLabel,
    countOwner: (id: string): { name: string; color: string | null } | null => {
      if (!roomActive) {
        return null;
      }
      const owner = counterSync.ownerOf(id);
      if (owner === null) {
        return { name: t("count.ownerNone"), color: null };
      }
      const name = (owner === myRoomUid() ? myNameCached : roomNames.get(owner)) || `${t("multiplayer.anonymousName")}_${anonymousIdSuffix(owner)}`;
      return { name, color: colorCssForUid(owner) };
    },
    onVolume: (value: number): void => {
      audio.setMasterVolume(value);
      settings.volume = value;
      persist();
    },
    onWoodDecay: (seconds: number): void => {
      audio.setWoodDecay(seconds);
      settings.woodDecayS = seconds;
      persist();
    },
    onBellTranspose: (octaves: number): void => {
      audio.setBellTranspose(octaves);
      settings.bellTranspose = octaves;
      persist();
    },
    onBellModRatio: (ratio: number): void => {
      audio.setBellModRatio(ratio);
      settings.bellModRatio = ratio;
      persist();
    },
    onBellModIndex: (index: number): void => {
      audio.setBellModIndex(index);
      settings.bellModIndex = index;
      persist();
    },
    onBellLevel: (level: number): void => {
      audio.setBellLevel(level);
      settings.bellLevel = level;
      persist();
    },
    onDensityLow: (rate: number): void => {
      audio.setDensityLow(rate);
      settings.densityLow = rate;
      persist();
    },
    onDensityHigh: (rate: number): void => {
      audio.setDensityHigh(rate);
      settings.densityHigh = rate;
      persist();
    },
    onDensityTransient: (amount: number): void => {
      audio.setDensityTransient(amount);
      settings.densityTransient = amount;
      persist();
    },
    onDensityDetune: (cents: number): void => {
      audio.setDensityDetune(cents);
      settings.densityDetune = cents;
      persist();
    },
    onDensityVoiceGain: (floor: number): void => {
      audio.setDensityVoiceGain(floor);
      settings.densityVoiceGain = floor;
      persist();
    },
    onDensityDuck: (floor: number): void => {
      audio.setDensityDuck(floor);
      settings.densityDuck = floor;
      persist();
    },
    onDensityReverb: (amount: number): void => {
      audio.setReverbAmount(amount);
      settings.densityReverb = amount;
      persist();
    },
    onEmitterRate: (rate: number): void =>
      editEmitter("emRate", { rate }, (): void => {
        emitterSpawn.rate = rate;
      }),
    onEmitterBounce: (value: number): void =>
      editEmitter("emBounce", { bounce: value }, (): void => {
        emitterSpawn.bounce = value;
      }),
    onEmitterFriction: (value: number): void =>
      editEmitter("emFriction", { friction: value }, (): void => {
        emitterSpawn.friction = value;
      }),
    onEmitterDrag: (value: number): void =>
      editEmitter("emDrag", { drag: value }, (): void => {
        emitterSpawn.drag = value;
      }),
    onEmitterCone: (deg: number): void =>
      editEmitter("emCone", { coneDeg: deg }, (): void => {
        emitterSpawn.coneDeg = deg;
      }),
    onEmitterSpeed: (min: number, max: number): void =>
      editEmitter("emSpeed", { speedMin: min, speedMax: max }, (): void => {
        emitterSpawn.speedMin = min;
        emitterSpawn.speedMax = max;
      }),
    onEmitterLife: (min: number, max: number): void =>
      editEmitter("emLife", { lifeMin: min, lifeMax: max }, (): void => {
        emitterSpawn.lifeMin = min;
        emitterSpawn.lifeMax = max;
      }),
    onEmitterPulseSpeed: (enabled: boolean): void =>
      editEmitter("emPulseSpeed", { pulseSpeedFromSettings: enabled }, (): void => {
        emitterSpawn.pulseSpeedFromSettings = enabled;
      }),
    onEmitterMeter: (meter: Meter): void => {
      const target = emitterField.selectedEmitter() ?? emitterSpawn;
      const num = clampSeqNum(target.num, meter);
      const seqSteps = target.seqSteps.filter((s: number): boolean => s < num);
      editEmitter("emMeter", { meter, num, seqSteps }, (): void => {
        emitterSpawn.meter = meter;
        emitterSpawn.num = num;
        emitterSpawn.seqSteps = seqSteps;
      });
    },
    onEmitterNum: (num: number): void => {
      const target = emitterField.selectedEmitter() ?? emitterSpawn;
      const seqSteps = target.seqSteps.filter((s: number): boolean => s < num);
      editEmitter("emNum", { num, seqSteps }, (): void => {
        emitterSpawn.num = num;
        emitterSpawn.seqSteps = seqSteps;
      });
    },
    onPointSize: (size: number): void => {
      particles.setPointSize(size);
      field.setParticleRadius(size / 2);
      settings.pointSize = size;
      persist();
    },
    onGravityY: (value: number): void => {
      const stored: number = -value;
      if (settings.gravityY === stored) {
        return;
      }
      beginEdit("gravityY");
      particles.setGravityY(stored);
      settings.gravityY = stored;
      persist();
    },
    onFallKillDist: (value: number): void => {
      particles.setFallKillDistance(value);
      settings.fallKillDistance = value;
      persist();
    },
    onFallKillLifeDiv: (value: number): void => {
      particles.setFallKillLifeDivisor(value);
      settings.fallKillLifeDivisor = value;
      persist();
    },
    onClockPeriod: (seconds: number): void => {
      if (settings.clockPeriod === seconds) {
        return;
      }
      beginEdit("clockPeriod");
      particles.setClockPeriod(seconds);
      settings.clockPeriod = seconds;
      reconcileOrbitClockSync();
      persist();
    },
    onClockReset: (): void => {
      requestClockReset();
    },
    onEditSnap: (snap: EditSnap): void => {
      settings.editSnap = snap;
      persist();
    },
    onCommunitySceneCameraEnabled: (on: boolean): void => {
      settings.communitySceneCameraEnabled = on;
      persist();
    },
    onCommunitySceneObjectPlacement: (on: boolean): void => {
      settings.communitySceneObjectPlacement = on;
      persist();
    },
    onShowNoteName: (show: boolean): void => {
      settings.showNoteName = show;
      persist();
      bindPanel();
    },
    onLanguage: (lang: Language): void => {
      settings.language = lang;
      persist();
      persistObjects();
      persistConnections();
      syncEmitters();
      markSkipTitleOnce();
      location.reload();
    },
    onFoldout: (title: string, collapsed: boolean): void => {
      settings.foldouts = { ...settings.foldouts, [title]: collapsed };
      persist();
    },
    onDebugRender: (state: DebugRenderState): void => {
      setDebugRender(state);
      particles.updatePointScale();
    },
    onDebugSim: (running: boolean): void => particles.setDebugSimPaused(!running),
    onDebugReadback: (running: boolean): void => particles.setDebugReadbackOff(!running),
    onDebugGrid: (useGrid: boolean): void => particles.setUseGrid(useGrid),
    onWallTile: (tiles: number): void => backgroundWall.setTiling(tiles),
    onWallNormalScale: (scale: number): void => backgroundWall.setNormalScale(scale),
    onWallResolution: (w: number, h: number): void => backgroundWall.setResolution(w, h),
    onWallZ: (z: number): void => backgroundWall.setZ(z),
    onWallLightDir: (x: number, y: number): void => {
      settings.wallLightX = x;
      settings.wallLightY = y;
      persist();
      setKeyLightDir(x, y);
    },
    onWallRegenerate: (params: WallParams): void => {
      backgroundWall.regenerate(params);
      saveEnv(params);
      roomNotify();
    },
    onWallShadowRes: (size: number): void => {
      settings.wallShadowRes = size;
      persist();
      setShadowMapSize(size);
    },
    onFeature: (category: FeatureCategory, on: boolean): void => {
      setFeatureEnabled(category, on);
    },
    onSimpleMode: (on: boolean): void => {
      if (on && hasNonPentatonicPitched()) {
        const applySimpleModeSwitch = (): void => {
          pushUndo();
          snapAllPitchedToPentatonic();
          if (!settings.objectPlacement) {
            clearTouchPlay();
          }
          settings.simpleMode = true;
          persist();
          applySimpleMode();
        };
        if (settings.skipSimpleModeConfirm) {
          applySimpleModeSwitch();
          return;
        }
        showActionModal<boolean>(
          t("simpleMode.confirmTitle"),
          t("simpleMode.confirmMessage"),
          [
            { label: t("common.continue"), value: true },
            { label: t("common.cancel"), value: false, subtle: true },
          ],
          (proceed: boolean, checked: boolean): void => {
            if (!proceed) {
              toolbar.setSimpleChecked(false);
              return;
            }
            if (checked) {
              settings.skipSimpleModeConfirm = true;
            }
            applySimpleModeSwitch();
          },
          { checkbox: { label: t("common.dontAskAgain") } },
        );
        return;
      }
      if (on && !settings.objectPlacement) {
        clearTouchPlay();
      }
      settings.simpleMode = on;
      persist();
      applySimpleMode();
      if (!on) {
        const targets = pitchMaskSnapTargets();
        if (targets.length > 0) {
          pushUndo();
          applyPitchMaskSnap(targets);
        }
      }
    },
    onCameraEnabled: (on: boolean): void => {
      settings.cameraEnabled = on;
      persist();
      rig.setCameraEnabled(on);
    },
    onTouchPlayToggle: (): void => {
      setObjectPlacement(!settings.objectPlacement);
    },
    onCreateRoom: (): void => {
      if (roomActive) {
        if (activeRoomId !== null) {
          showRoomShareModal(roomShareUrl(activeRoomId));
        }
        return;
      }
      const createMessage = t("multiplayer.createPrivateMessage");
      showActionModal<"create" | "cancel">(
        t("multiplayer.createTitle"),
        createMessage,
        [
          { label: t("multiplayer.createConfirm"), value: "create" },
          { label: t("common.cancel"), value: "cancel", subtle: true },
        ],
        (value: "create" | "cancel", _checked: boolean, name: string, checks: boolean[], capacity: number): void => {
          if (value !== "create") {
            return;
          }
          const roomId = newRoomId();
          const shareUrl = roomShareUrl(roomId);
          const barUrl = new URL(location.href);
          barUrl.searchParams.set("room", roomId);
          history.replaceState(null, "", barUrl.toString());
          const trimmed = name.trim();
          const scene: RoomSceneMeta | null =
            currentProvenance === null
              ? null
              : {
                  id: currentProvenance.meta.id,
                  title: currentProvenance.meta.title,
                  ownerName: currentProvenance.meta.ownerName,
                  owner: currentProvenance.meta.owner,
                };
          void startRoom(roomId, {
            public: false,
            name: trimmed === "" ? undefined : trimmed,
            gateAnon: checks[0] !== true,
            capacity,
            scene,
          });
          if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl).catch((): void => {});
          }
          showRoomShareModal(shareUrl);
        },
        {
          numberInput: { label: t("multiplayer.roomCapacityLabel"), value: DEFAULT_ROOM_CAPACITY, min: MIN_ROOM_CAPACITY, max: MAX_ROOM_CAPACITY },
          checkbox: [
            {
              label: t("multiplayer.allowAnon"),
              checked: true,
              disabled: !myIsRealCached,
              title: myIsRealCached ? undefined : t("multiplayer.allowAnonHint"),
            },
          ],
          textInput: { label: t("multiplayer.roomNameLabel"), placeholder: t("multiplayer.roomNamePlaceholder") },
        },
      );
    },
    onLeaveRoom: (): void => {
      restoreRoomStopOnBlur();
      const url = new URL(location.href);
      url.searchParams.delete("room");
      url.searchParams.delete("uid");
      location.href = url.toString();
    },
    onBrowsePublic: (): void => {
      void browsePublic();
    },
    onShowTutorial: (): void => showTutorial(),
    onStopOnBlur: (on: boolean): void => {
      if (roomActive) {
        return;
      }
      settings.stopOnBlur = on;
      persist();
    },
    onPaletteDragStart: (tool: Tool, ev: PointerEvent): void => pointer.beginPaletteDrag(tool, ev),
    onSubmenuToolDragStart: (tool: Tool, ev: PointerEvent): void => pointer.beginPaletteDrag(tool, ev, true),
    onNodeMenuDragStart: (ev: PointerEvent, wasOpen: boolean): void => pointer.beginNodeMenuDrag(ev, wasOpen),
    onCopy: (): void => {
      const sel = field.selectedObject() ?? emitterField.selectedEmitter();
      if (!sel) {
        return;
      }
      clipboard = buildClipboard(sel.id, graph, field, emitterField);
      syncCopyPasteButtons();
    },
    onPasteDragStart: (ev: PointerEvent): void => pointer.beginPasteDrag(ev),
    onRadialPlaceTool: (tool: Tool, clientX: number, clientY: number): void => {
      if (!pointer.placeToolAt(tool, clientX, clientY)) {
        pointer.seedToolAtAnchor(tool, clientX, clientY);
      }
    },
    onRadialPaste: (clientX: number, clientY: number): void => {
      pointer.pasteAt(clientX, clientY);
    },
    onRadialSuspendControls: (): void => rig.suspendControls(),
    onRadialResumeControls: (): void => rig.resumeControls(),
    onExportVideo: async (cycles: number, onProgress: (text: string) => void): Promise<string | null> => {
      if (exportRunning) {
        return "export already running";
      }
      exportRunning = true;
      audio.releaseAllRailVoices();
      const cycleSec = performanceCycleSec(sequencerNums(emitterField.list()), particles.clockPeriodSeconds());
      particles.reset();
      try {
        await exportLoopVideo(
          {
            stepSimFrame,
            renderFrame: (): void => {
              backgroundWall.followCamera(rig.activeCamera);
              render(rig.activeCamera);
            },
            canvas,
            audioSampleRate: (): number => audio.sampleRate(),
            createOfflineAudio: (offlineCtx: BaseAudioContext, timeSource: () => number): AudioEngine => {
              const off = new AudioEngine(offlineCtx, timeSource, audio.decodedSamples());
              off.setMicSource(micStore);
              off.setMasterVolume(settings.volume);
              off.setWoodDecay(settings.woodDecayS);
              off.setBellTranspose(settings.bellTranspose);
              off.setBellModRatio(settings.bellModRatio);
              off.setBellModIndex(settings.bellModIndex);
              off.setBellLevel(settings.bellLevel);
              off.setDensityLow(settings.densityLow);
              off.setDensityHigh(settings.densityHigh);
              off.setDensityTransient(settings.densityTransient);
              off.setDensityDetune(settings.densityDetune);
              off.setDensityVoiceGain(settings.densityVoiceGain);
              off.setDensityDuck(settings.densityDuck);
              off.setReverbAmount(settings.densityReverb);
              off.setSceneTimbreOverride(settings.simpleMode ? "bell" : null);
              for (const t of timbreIds()) {
                off.setTimbreVolume(t, audio.timbreVolume(t));
                off.setTimbreTranspose(t, field.timbreTransposeFor(t));
              }
              for (const t of SUSTAIN_TIMBRES) {
                off.setSustainValue(t, audio.sustainValue(t));
              }
              activeAudio = off;
              return off;
            },
          },
          { cycles, cycleSec, onProgress },
        );
        return null;
      } catch (e) {
        return `export failed: ${(e as Error).message}`;
      } finally {
        activeAudio = audio;
        particles.reset();
        exportRunning = false;
      }
    },
    onCycleSec: (): number => performanceCycleSec(sequencerNums(emitterField.list()), particles.clockPeriodSeconds()),
    onSaveSlot: (name: string): string | null => {
      if (currentProvenance !== null && currentProvenance.meta.owner !== myUidCached) {
        return t("scene.foreignLocked");
      }
      return saveSlot(name, sceneBundle(), loadThumb()?.dataUrl);
    },
    onExportScene: (): string => JSON.stringify(sceneBundle()),
    onLoadSlot: (scene: unknown): string | null => {
      if (roomActive) {
        return null;
      }
      return applyImportedScene(scene);
    },
    onResetScene: (): void => resetSceneToBlankInPlace(),
    onApplySceneInPlace: (scene: unknown): string | null => applySceneInPlace(scene, false, true),
    onCommunityEnter: (): void => {
      stashedScene = sceneBundle();
      stashedProvenance = currentProvenance;
      document.body.classList.add("feed-open");
      renderTopChips();
      css2dRenderer.domElement.style.display = "none";
      setFeedActive(true);
      backgroundWall.prebakeAll();
      rig.suspendControls();
    },
    onFeedSettle: async (scene: unknown, meta: SceneListItem): Promise<void> => {
      const manifest = (scene as Record<string, unknown>).micManifest ?? null;
      setProvenance({ meta, micManifest: manifest });
      const epoch = ++focusEpoch;
      micLoading = parseMicManifest(manifest).length > 0 && r2Configured();
      if (micLoading) {
        micFrameRendered = false;
      }
      await applySceneMic(manifest, meta.owner, (): boolean => epoch === focusEpoch);
      if (epoch === focusEpoch) {
        micLoading = false;
      }
    },
    onPrefetchMic: (scene: unknown, owner: string): void => {
      const manifest = (scene as Record<string, unknown>).micManifest ?? null;
      void applySceneMic(manifest, owner, (): boolean => false);
    },
    onRecordSimPause: (open: boolean): void => {
      recordModalOpen = open;
      audio.setWindowMuted(open);
      if (open) {
        audio.releaseAllRailVoices();
      }
    },
    onCommunityExit: (): void => {
      feedViewportEl = null;
      setFeedActive(false);
      particles.setFeedHeightPx(null);
      document.body.classList.remove("feed-open");
      css2dRenderer.domElement.style.display = "";
      focusEpoch++;
      micLoading = false;
      if (stashedScene !== null) {
        const restored = stashedScene;
        applySceneInPlace(restored, true);
        const prov = stashedProvenance;
        if (prov === null) {
          saveMicImport(null);
          void applySceneMic(restored.micManifest ?? null, null).then((): void => micStore.evictInactiveImported());
          setProvenance(null);
        } else {
          saveMicImport(prov.micManifest === null ? null : { owner: prov.meta.owner, manifest: prov.micManifest });
          void applySceneMic(prov.micManifest, prov.meta.owner).then((): void => micStore.evictInactiveImported());
          setProvenance(prov);
        }
        stashedScene = null;
        stashedProvenance = null;
      }
      rig.resumeControls();
    },
    onCommunityKeepScene: (scene: unknown, meta: SceneListItem): void => {
      feedViewportEl = null;
      setFeedActive(false);
      particles.setFeedHeightPx(null);
      document.body.classList.remove("feed-open");
      css2dRenderer.domElement.style.display = "";
      focusEpoch++;
      micLoading = false;
      const valid = validScene(scene);
      if (valid !== null) {
        persistScene(valid);
        applySceneInPlace(valid, false, true);
        const manifest = valid.micManifest ?? null;
        saveMicImport(manifest === null ? null : { owner: meta.owner, manifest });
        void applySceneMic(manifest, meta.owner).then((): void => micStore.evictInactiveImported());
        setProvenance({ meta, micManifest: manifest });
        saveProvenance({ meta, micManifest: manifest });
      }
      stashedScene = null;
      stashedProvenance = null;
      rig.resumeControls();
    },
    onSetFeedViewport: (el: HTMLElement | null): void => {
      feedViewportEl = el;
      if (el === null) {
        setFeedRect(null);
        particles.setFeedHeightPx(null);
        if (feedCapture !== null) {
          feedCapture.resolve(null);
          feedCapture = null;
        }
      }
    },
    onCaptureFeedPreview: (id: string): Promise<string | null> =>
      new Promise((resolve: (dataUrl: string | null) => void): void => {
        if (feedCapture !== null) {
          feedCapture.resolve(null);
        }
        feedCapture = { id, resolve };
      }),
    onFetchScene: (id: string): Promise<{ scene: unknown } | { error: string }> => fetchScene(id),
    onQueuePlay: (id: string): void => queuePlay(id),
    onFlushPlays: (): void => flushPlays(),
    onGetSlots: (): SlotEntry[] => loadSlots(),
    onDeleteSlot: (name: string): void => deleteSlot(name),
    onCaptureThumb: (cb: (dataUrl: string) => void): void => {
      captureRequested = true;
      captureHiRes = false;
      captureCallback = cb;
    },
    onCaptureUploadPreview: (): Promise<string> =>
      new Promise((resolve: (dataUrl: string) => void): void => {
        captureRequested = true;
        captureHiRes = true;
        captureCallback = resolve;
      }),
    shareEnabled: (): boolean => shareEnabled(),
    onUploadSummary: (): UploadFieldSummary => ({
      gravityY: settings.gravityY,
      clockPeriod: settings.clockPeriod,
      features: FEATURE_CATEGORIES.filter((cat: FeatureCategory): boolean => settings.features[cat]),
      simpleMode: settings.simpleMode,
      cameraEnabled: settings.cameraEnabled,
      viewMode: settings.viewMode,
      wallPreset: wallParamsToPreset(backgroundWall.getParams()),
      usesMic: sceneUsesMic(snapshot().objects),
    }),
    onUploadScene: async (title: string): Promise<UploadResult> => {
      if (currentProvenance !== null && currentProvenance.meta.owner !== (await refreshMyUid())) {
        return { kind: "error", message: t("scene.foreignLocked") };
      }
      const bundle = sceneBundle();
      const controller = new AbortController();
      const timeoutId = setTimeout((): void => controller.abort(), UPLOAD_TIMEOUT_MS);
      try {
        const micResult = await uploadSceneMicAssets(bundle.micManifest, controller.signal);
        if (micResult !== null) {
          return micResult;
        }
        return await uploadScene(title, bundle, controller.signal);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") {
          return { kind: "timeout" };
        }
        return { kind: "error", message: err instanceof Error ? err.message : String(err) };
      } finally {
        clearTimeout(timeoutId);
      }
    },
    onListScenes: (q: SceneQuery): Promise<{ items: SceneListItem[] } | { error: string }> => listScenes(q),
    onApplySceneTagOps: (sceneId: string, ops: SceneTagOps): Promise<TagMutationResult> => applySceneTagOps(sceneId, ops),
    onReportScene: (id: string, reason: string): Promise<string | null> => reportScene(id, reason),
    onDeleteScene: (id: string): Promise<string | null> => deleteScene(id),
    onCurrentUid: (): Promise<string | null> => refreshMyUid(),
    onSignInWithGoogle: async (): Promise<string | null> => {
      track("signin_start");
      const err = await signInWithGoogle();
      await refreshMyUid();
      return err;
    },
    onSignOut: async (): Promise<void> => {
      await signOut();
      await refreshMyUid();
    },
    onAuthStatus: (): Promise<AuthStatus> => authStatus(),
    onIsRealAccount: (): Promise<boolean> => isRealAccount(),
    onSetDisplayName: (name: string): Promise<string | null> => setDisplayName(name),
    onSubmitFeedback: (body: string, includeScene: boolean): Promise<string | null> => {
      if (!includeScene) {
        return submitFeedback(body);
      }
      const feedbackScene = sceneBundle();
      delete feedbackScene.micManifest;
      return submitFeedback(body, feedbackScene);
    },
    onListMicAssets: (): Promise<{ items: MicAsset[] } | { error: string }> => listMicAssets(),
    onDeleteMicAsset: (hash: string): Promise<string | null> => deleteMicAsset(hash),
    onLikeScene: async (id: string): Promise<string | null> => {
      const err = await likeScene(id);
      if (err === null) {
        track("like", { scene_id: id });
      }
      return err;
    },
    onUnlikeScene: (id: string): Promise<string | null> => unlikeScene(id),
    onHasLiked: (id: string): Promise<boolean> => hasLiked(id),
    onLikedSceneIds: (ids: string[]): Promise<string[]> => likedSceneIds(ids),
    onBookmarkedSceneIds: (ids: string[]): Promise<string[]> => bookmarkedSceneIds(ids),
    onAddBookmark: (sceneId: string, categoryId: string | null): Promise<string | null> => addBookmark(sceneId, categoryId),
    onRemoveBookmark: (sceneId: string): Promise<string | null> => removeBookmark(sceneId),
    onSetBookmarkCategory: (sceneId: string, categoryId: string | null): Promise<string | null> => setBookmarkCategory(sceneId, categoryId),
    onListBookmarks: (): Promise<{ items: BookmarkItem[] } | { error: string }> => listBookmarks(),
    onListCategories: (): Promise<{ items: BookmarkCategory[] } | { error: string }> => listCategories(),
    onAddCategory: (name: string): Promise<string | null> => addCategory(name),
    onRenameCategory: (id: string, name: string): Promise<string | null> => renameCategory(id, name),
    onDeleteCategory: (id: string): Promise<string | null> => deleteCategory(id),
  }, initialBloomMode);
  const statsHudAllowed = params.has("debug") || params.has("notutorial") || benchMode;
  stats.setVisible(!toolbar.uiMin && statsHudAllowed);

  objectPanelEl = document.querySelector(".panel-object");
  mainPanelEl = document.querySelector(".hover-panel-main");

  const panelLayout = initPanelLayout(ctx);
  const pitchButtons = initPitchButtons(ctx, panelLayout, {
    onPitch: applyPitch,
    onPitchEnd: applyPitchEnd,
    isClaimLocked: (id: string): boolean => isForeignClaimed(id),
  });

  const ports = initPorts(ctx, panelLayout, {
    objectPanelEl,
    mainPanelEl,
    pushUndo,
    popUndo: (): void => {
      discardLastUndo();
    },
    persistConnections,
    onConnect: (toPort: PortId): void => {
      if (toPort === "pitchQuantizeIn") {
        requantizePitchToMasks();
      }
    },
    bindPanel,
    settableParams,
    offeredParams,
    setNodeOperand,
    highlightWireTarget,
    nodeUiVisible: (): boolean => !settings.simpleMode && settings.features.node,
    hitTestIds: (ev: PointerEvent): string[] => pointer.hitTestIds(ev),
    entityLabel: entityExprLabel,
    createEntityAt: (tool: Tool, clientX: number, clientY: number): string | null => pointer.createEntityAt(tool, clientX, clientY),
    isClaimLocked: (id: string): boolean => isForeignClaimed(id),
    wireGuestLocked: (id: string): boolean => isRoomGuest() && objectById(id)?.type === "global",
  });

  field.restore(loadObjects());
  graph.restore(loadConnections());
  pruneLegacyCountValueIn(graph, (id: string): boolean => objectById(id)?.type === "count");
  const initialEnv = loadEnv();
  backgroundWall.regenerate(initialEnv);
  toolbar.syncWall(initialEnv);
  clearEmitInjection();
  primeSignalState();
  syncTimbreEngine();
  applyFeatureGates();
  reconcileOrbitClockSync();
  sequencerPanel = initSequencerPanel(ctx, {
    editEmitterSeqSteps,
    uiNoSeq: (): boolean => toolbar.uiNoSeq,
  });
  const pointer = initPointer(ctx, {
    canvas,
    ports,
    undo: undoApi,
    entities,
    tools,
    getActiveTool: (): Tool | null => activeTool,
    gizmoIs2D: (): boolean => gizmoIs2D,
    getClipboard: (): Clipboard | null => clipboard,
    setClipboard: (cb: Clipboard | null): void => {
      clipboard = cb;
    },
    uiView: (): boolean => toolbar.uiView,
    touchPlayActive: (): boolean => touchPlayActive(),
    showPaletteHint: (tool: Tool): void => toolbar.showPaletteHint(tool),
    nodeSubmenu: {
      toolAt: (x: number, y: number): Tool | null => toolbar.nodeSubmenu.toolAt(x, y),
      setHover: (tool: Tool | null): void => toolbar.nodeSubmenu.setHover(tool),
      close: (): void => toolbar.nodeSubmenu.close(),
    },
    bindPanel,
    refreshGizmo,
    lockPanelOnTouch,
    persistObjects,
    persistConnections,
    syncEmitters,
    settableParams,
    togglePitchQuantizeClass: (objId: string, pitchClass: number): void => {
      beginEdit(`pitchQuantize:${objId}:${pitchClass}`);
      field.togglePitchClass(objId, pitchClass);
      persistObjects();
      requantizePitchToMasks();
      bindPanel();
    },
    isClaimLocked: (id: string): boolean => isForeignClaimed(id),
    isRadialMode: (): boolean => toolbar.isRadialMode(),
    openRadial: (clientX: number, clientY: number): void => toolbar.radial.open(clientX, clientY),
    deletionLocked: (): boolean => tutorialDeleteLock,
    sendButton: (id: string, phase: "press" | "release"): void => roomButtonSend(id, phase),
    roomActive: (): boolean => roomActive,
    onSemiSelect: (rootId: string): void => onSemiSelect(rootId),
  });
  bindPanel();
  toolbar.setToolsEnabled(true);
  applySimpleMode();
  applyPlayModeChrome();
  toolbar.setTouchPlayToggleActive(settings.objectPlacement);
  toolbar.setDimensionToggleVisible(!settings.simpleMode && settings.features["3d"]);
  toolbar.setDimensionToggleActive(settings.viewMode === "3d");
  const tutorialSpawnPoint = (): { x: number; y: number } => ({ x: window.innerWidth * 0.5, y: window.innerHeight * 0.45 });

  const TUTORIAL_BOX_DEFAULT_HEIGHT = 2;

  /** Where the tutorial box belongs: just below the rhythm's particle stream. Uses the default box height until one exists. */
  const tutorialBoxDropPosition = (): THREE.Vector3 | null => {
    const rhythm = latestTutorialRhythm();
    if (rhythm === null) {
      return null;
    }
    const box = latestTutorialBox();
    const boxHeight = box === null ? TUTORIAL_BOX_DEFAULT_HEIGHT : Math.max(0.1, box.params.y * 2 * box.scale.y);
    return new THREE.Vector3(rhythm.position.x + 0.6, rhythm.position.y - 0.9 - boxHeight, rhythm.position.z);
  };

  /** Moves the newest tutorial box just below the rhythm's particle stream and selects it, so the contact step can sound without manual dragging. */
  const relocateTutorialBox = (): boolean => {
    const box = latestTutorialBox();
    if (box === null) {
      return false;
    }
    const drop = tutorialBoxDropPosition();
    if (drop !== null) {
      field.moveById(box.id, new THREE.Vector3(drop.x, drop.y, box.position.z));
      persistObjects();
    }
    field.select(box);
    bindPanel();
    return true;
  };
  const latestTutorialBox = (): PlacedObject | null => {
    const boxes = field.list().filter((o: PlacedObject): boolean => o.type === "box");
    return boxes[boxes.length - 1] ?? null;
  };
  const latestTutorialRhythm = (): Emitter | null => {
    const syncs = emitterField.list().filter((em: Emitter): boolean => em.kind === "sync");
    return syncs[syncs.length - 1] ?? null;
  };

  const tutorial = setupTutorial({
    ...toolbar.tutorialTargets,
    radial: {
      active: (): boolean => toolbar.isRadialMode(),
      isOpen: (): boolean => toolbar.radial.isOpen(),
      itemRect: (key: string): DOMRect | null => toolbar.radial.itemRect(key),
      backRect: (): DOMRect | null => toolbar.radial.backRect(),
      open: (clientX: number, clientY: number): void => toolbar.radial.open(clientX, clientY),
      selectItem: (key: string): void => toolbar.radial.selectItem(key),
      selectBack: (): void => toolbar.radial.selectBack(),
      openScenePanelSection: (sectionTitle: string): void => toolbar.tutorialOpenScenePanelSection(sectionTitle),
    },
  }, {
    onBegin: (): void => {
      const snap = sceneBundle();
      applySceneInPlace({ ...snap, objects: [], emitters: [], connections: [] }, true);
      setProvenance(null);
      toolbar.setHoverPanelsSuppressed(true);
      toolbar.setHoverLeaveDisabled(true);
      toolbar.setMasterVolumeUnmuted(0.5);
    },
    onEnd: (): void => {
      toolbar.setHoverPanelsSuppressed(false);
      toolbar.setHoverLeaveDisabled(false);
      settings.cameraEnabled = true;
      setObjectPlacement(true);
    },
    setDeleteLock: (locked: boolean): void => {
      tutorialDeleteLock = locked;
    },
    ensurePlacementOn: (): void => {
      if (!settings.objectPlacement) {
        setObjectPlacement(true);
      }
    },
    placeForTutorial: (tool: "emitter-sync" | "box"): boolean => {
      const point = tutorialSpawnPoint();
      const placedId = pointer.createEntityAt(tool, point.x, point.y);
      if (placedId === null) {
        return false;
      }
      if (tool === "box") {
        relocateTutorialBox();
      }
      return true;
    },
    relocateTutorialBox: (): boolean => relocateTutorialBox(),
    selectTutorialBox: (): boolean => {
      const box = latestTutorialBox();
      if (box === null) {
        return false;
      }
      field.select(box);
      bindPanel();
      return true;
    },
    deleteTutorialBox: (): boolean => {
      const box = latestTutorialBox();
      if (box === null) {
        return false;
      }
      field.select(box);
      field.deleteSelected();
      persistObjects();
      bindPanel();
      return true;
    },
    nudgeTutorialBoxPitch: (): boolean => {
      const box = latestTutorialBox();
      if (box === null) {
        return false;
      }
      field.select(box);
      field.editSelected({ pitchIndex: Math.min(PITCH_INDEX_MAX, box.pitchIndex + 1) });
      persistObjects();
      bindPanel();
      return true;
    },
    setTutorialBoxTimbre: (timbreId: string): boolean => {
      const box = latestTutorialBox();
      if (box === null) {
        return false;
      }
      field.select(box);
      field.editSelected({ timbreId });
      persistObjects();
      bindPanel();
      return true;
    },
    entityCount: (): number => field.list().length + emitterField.list().length,
    targetBoxRect: (): DOMRect | null => {
      const box = latestTutorialBox();
      if (box === null) {
        return null;
      }
      const ndc = box.position.clone().project(rig.activeCamera);
      const x = (ndc.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-ndc.y * 0.5 + 0.5) * window.innerHeight;
      return new DOMRect(x - 36, y - 36, 72, 72);
    },
    boxDropRect: (): DOMRect | null => {
      const drop = tutorialBoxDropPosition();
      if (drop === null) {
        return null;
      }
      const ndc = drop.project(rig.activeCamera);
      const x = (ndc.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-ndc.y * 0.5 + 0.5) * window.innerHeight;
      return new DOMRect(x - 60, y - 60, 120, 120);
    },
    boxSelected: (): boolean => field.selectedObject()?.type === "box",
    soundPlayedCount: (): number => soundPulseCount,
    boxPitch: (): number | null => field.selectedObject()?.pitchIndex ?? null,
    boxTimbre: (): string | null => field.selectedObject()?.timbreId ?? null,
  });
  showTutorial = (): void => tutorial.start();
  const tutorialSuppressed = new URLSearchParams(location.search).has("bench") || new URLSearchParams(location.search).has("notutorial");
  if (tutorial.shouldAutoShow() && !tutorialSuppressed) {
    titleGate.then((): void => tutorial.start());
  }

  window.addEventListener("pointerup", (): void => {
    resetEditGesture();
  });

  window.addEventListener("pointerdown", (): void => {
    audio.resume();
    sfx.resume();
  });

  document.addEventListener("visibilitychange", (): void => {
    if (document.hidden) {
      audio.releaseAllRailVoices();
      flushPlays();
    }
  });

  window.addEventListener("pagehide", (): void => {
    flushPlays();
  });

  let blurPaused = false;
  let blurPauseTimer: number | undefined;
  let exportRunning = false;
  let recordModalOpen = false;
  let micLoading = false;
  let micFrameRendered = false;

  window.addEventListener("blur", (): void => {
    if (!settings.stopOnBlur) {
      return;
    }
    audio.setWindowMuted(true);
    blurPauseTimer = window.setTimeout((): void => {
      blurPaused = true;
      audio.releaseAllRailVoices();
    }, BLUR_PAUSE_DELAY_MS);
  });

  window.addEventListener("focus", (): void => {
    if (blurPauseTimer !== undefined) {
      window.clearTimeout(blurPauseTimer);
      blurPauseTimer = undefined;
    }
    blurPaused = false;
    audio.setWindowMuted(false);
  });

  let prevViewportW = window.innerWidth;
  let prevViewportH = window.innerHeight;

  /**
   * World-axis bounding box of every visible placed object + emitter, or null
   * when nothing is placed. Used to decide and apply rotation reframing.
   */
  function sceneWorldBounds(): THREE.Box3 | null {
    const box = new THREE.Box3();
    const item = new THREE.Box3();
    for (const o of field.list()) {
      const mesh = field.meshById(o.id);
      if (!mesh || !mesh.visible) {
        continue;
      }
      item.setFromObject(mesh);
      if (!item.isEmpty()) {
        box.union(item);
      }
    }
    for (const e of emitterField.list()) {
      const mesh = emitterField.meshById(e.id);
      if (!mesh || !mesh.visible) {
        continue;
      }
      item.setFromObject(mesh);
      if (!item.isEmpty()) {
        box.union(item);
      }
    }
    return box.isEmpty() ? null : box;
  }

  /**
   * Whether all 8 corners of `box` project inside the active camera's frustum.
   * Must be called before resize() applies the new aspect, so the projection
   * reflects the pre-rotation viewport.
   */
  function boxFitsInView(box: THREE.Box3): boolean {
    const cam = rig.activeCamera;
    const corner = new THREE.Vector3();
    for (let c = 0; c < 8; c++) {
      corner.set(c & 1 ? box.max.x : box.min.x, c & 2 ? box.max.y : box.min.y, c & 4 ? box.max.z : box.min.z);
      corner.project(cam);
      if (Math.abs(corner.x) > CAMERA_FIT_DETECT_NDC || Math.abs(corner.y) > CAMERA_FIT_DETECT_NDC || corner.z < -1 || corner.z > 1) {
        return false;
      }
    }
    return true;
  }

  window.addEventListener("resize", (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const prevAspect = prevViewportW / prevViewportH;
    const newAspect = w / h;
    const aspectChanged = Math.abs(newAspect / prevAspect - 1) > CAMERA_FIT_ASPECT_THRESHOLD;
    const bounds = aspectChanged && rig.isSteady() ? sceneWorldBounds() : null;
    const shouldFit = bounds !== null && boxFitsInView(bounds);
    resize(w, h);
    css2dRenderer.setSize(w, h);
    selectionFrames.setResolution(w, h);
    gizmo.setResolution(w, h);
    particles.updatePointScale();
    sequencerPanel?.rebuild();
    if (shouldFit && bounds !== null) {
      rig.fitToBox(bounds, newAspect);
    }
    prevViewportW = w;
    prevViewportH = h;
  });

  const transformGraph = initTransformGraph(ctx, {
    objectById,
    emitterById,
    nodeGraphActive: (): boolean => true,
    flatten2d: (): boolean => settings.simpleMode || settings.viewMode === "2d",
  });

  const { updateNodeLabels } = initNodeLabels(ctx, {
    buildValueNodes,
    uiView: (): boolean => toolbar.uiView,
  });

  let captureRequested = false;
  let captureCallback: ((dataUrl: string) => void) | null = null;
  let captureHiRes = false;

  const THUMB_PREVIEW_SCALE = 4;

  function captureFrame(scale: number = 1, quality: number = 0.6): string {
    const src = renderer.domElement;
    const W = 192 * scale, H = 312 * scale;
    const offscreen = document.createElement("canvas");
    offscreen.width = W;
    offscreen.height = H;
    const ctx = offscreen.getContext("2d");
    if (!ctx) return "";
    const sw = src.width, sh = src.height;
    const srcAspect = sw / sh;
    const dstAspect = W / H;
    let sx = 0, sy = 0, sWidth = sw, sHeight = sh;
    if (srcAspect > dstAspect) {
      sWidth = Math.round(sh * dstAspect);
      sx = Math.round((sw - sWidth) / 2);
    } else {
      sHeight = Math.round(sw / dstAspect);
      sy = Math.round((sh - sHeight) / 2);
    }
    ctx.drawImage(src, sx, sy, sWidth, sHeight, 0, 0, W, H);
    return offscreen.toDataURL("image/jpeg", quality);
  }

  /**
   * Reads the focused card's rect out of the drawing buffer into an offscreen
   * canvas for the objects-only feed preview. preserveDrawingBuffer is false, so
   * this must run in the same frame the rect was rendered (caller renders with
   * particles hidden first). The rect is in CSS px; the buffer is device px, so
   * the source box is scaled by the renderer pixel ratio. drawImage reads the
   * canvas element upright, so no Y flip is needed.
   */
  function captureFeedRect(rect: DOMRect): string {
    const src = renderer.domElement;
    const dpr = renderer.getPixelRatio();
    const sx = Math.round(rect.left * dpr);
    const sy = Math.round(rect.top * dpr);
    const sWidth = Math.max(1, Math.round(rect.width * dpr));
    const sHeight = Math.max(1, Math.round(rect.height * dpr));
    const W = 320, H = 518;
    const offscreen = document.createElement("canvas");
    offscreen.width = W;
    offscreen.height = H;
    const ctx = offscreen.getContext("2d");
    if (!ctx) return "";
    ctx.drawImage(src, sx, sy, sWidth, sHeight, 0, 0, W, H);
    return offscreen.toDataURL("image/jpeg", 0.7);
  }

  /**
   * One simulation frame shared by the realtime loop and the loop-video export:
   * injection staging, transform pass, fixed-step particle update, contact
   * readback, audio triggering into the given sink, glow decay and signal
   * evaluation — everything that advances scene state, none of the per-frame
   * editor UI work. Returns whether a Global node's reset pulse-in fired this
   * frame, so the realtime caller can defer the clock reset to the frame boundary
   * (the export loop ignores it — a mid-export reset would corrupt the loop).
   */
  /**
   * Returns whether a global node's reset pulse-in fired this frame, so the caller
   * can run the clock reset at the frame boundary (never inside evaluateSignals,
   * whose re-entrant prime would corrupt the in-flight frame). The export loop
   * ignores the return — a mid-export reset would corrupt the loop.
   */
  function stepSimFrame(delta: number, audioSink: SimAudioSink): boolean {
    particles.setEmitInjection(injExternal, injDriven, injSpeed);
    transformGraph.applyTransforms(delta);
    if (pruneEmptyGroups()) {
      persistObjects();
      persistConnections();
      bindPanel();
    }
    updateGroupBoxes();
    const measure = !exportRunning && !toolbar.uiMin;
    if (measure) { stats.beginSim(); }
    particles.update(delta);
    if (measure) { stats.endSim(); }
    const readbackStart = performance.now();
    const contacts = particles.readContactCounts();
    lastReadbackMs = performance.now() - readbackStart;
    if (!runDownstream) {
      lastContactsNonZero = 0;
      lastDownstreamMs = 0;
      return false;
    }
    const downstreamStart = performance.now();
    let nonZero = 0;
    for (let i = 0; i < contacts.counts.length; i++) {
      if (contacts.counts[i] > 0) nonZero++;
    }
    lastContactsNonZero = nonZero;
    if (nonZero > 0) {
      soundPulseCount += 1;
    }
    const packed = field.packedColliders();
    const oneShots = pointer.consumeTouchPlayOneShots();
    audioSink.trigger(contacts.counts, contacts.impacts, packed, contacts.windowSim);
    if (oneShots.length > 0) {
      const nominalImpact = IMPACT_REF / IMPACT_MAX;
      for (const id of oneShots) {
        const idx = packed.findIndex((p: PlacedObject): boolean => p.id === id);
        if (idx >= 0 && idx < contacts.counts.length) {
          audioSink.playTap(packed[idx]);
          contacts.counts[idx] = Math.max(contacts.counts[idx], 1);
          contacts.impacts[idx] = Math.max(contacts.impacts[idx], nominalImpact);
        }
      }
    }
    const touchRails = pointer.getTouchPlayRailInputs();
    if (particles.substepsLastFrame() > 0 || touchRails.length > 0) {
      const railInputs: RailVoiceInput[] = [];
      for (let k = 0; k < packed.length; k++) {
        if (packed[k].type !== "rail") {
          continue;
        }
        const pCount = contacts.railCounts[k];
        const pAvgT = pCount > 0 ? contacts.railTSums[k] / pCount : 0;
        const touch = touchRails.find((r: TouchPlayRailInput): boolean => r.id === packed[k].id);
        const tCount = touch?.count ?? 0;
        const total = pCount + tCount;
        const avgT = total > 0 ? (pAvgT * pCount + (touch?.avgT ?? 0) * tCount) / total : 0;
        railInputs.push({
          id: packed[k].id,
          count: total,
          avgT,
          startPitchIndex: packed[k].pitchIndex,
          endPitchIndex: packed[k].pitchIndexEnd,
          volume: packed[k].volume * (touch?.volScale ?? 1),
          railTimbre: packed[k].railTimbre,
        });
      }
      audioSink.updateRailVoices(railInputs);
    }
    driveContactGlow(delta, contacts.counts, contacts.impacts);
    if (roomActive) {
      if (counterSync.reconcile(liveCountIds())) {
        onCounterOwnershipChange();
      }
    }
    const resetPulsed = evaluateSignals(packed, contacts.counts, contacts.impacts, delta);
    if (roomActive) {
      counterSync.flush();
    }
    lastDownstreamMs = performance.now() - downstreamStart;
    return resetPulsed;
  }

  let roomTick: (dt: number) => void = (): void => {};
  let roomAvatarTick: () => void = (): void => {};
  let lastTime = performance.now();
  let dtEma = 1 / 60;
  let lastReadbackMs = 0;
  let lastDownstreamMs = 0;
  let lastContactsNonZero = 0;
  let soundPulseCount = 0;
  /**
   * GPU spike diagnostics split the frame into two TIME_ELAPSED spans: the sim
   * span brackets particles.update only (pure GPGPU, set inside stepSimFrame),
   * the render span brackets renderWithGroupOutlineIsolation (composer:
   * bloom/outline/post). The contact readback is deliberately outside the sim
   * GPU span — its synchronous readRenderTargetPixels stall would otherwise
   * inflate the sim GPU number — and is timed on its own via readbackMs (CPU).
   * Both GPU readings are 1-2 frames behind — the timer query becomes available
   * only after the GPU drains — so a recorded sample's GPU ms pairs with snapshot
   * fields (drawCalls/outlineActive/substeps) held steady across a stable window,
   * not with the exact frame they were issued.
   *
   * micLoading freezes the sim while a Community card's author mic kit downloads,
   * but only after `micFrameRendered` — one full frame runs first so the
   * just-applied scene is drawn before the freeze, otherwise the card would stick
   * on the stale pre-settle frame until the download finished.
   */
  function frame(now: number): void {
    const delta = (now - lastTime) / 1000;
    lastTime = now;
    if (blurPaused || exportRunning || recordModalOpen) {
      requestAnimationFrame(frame);
      return;
    }
    if (micLoading && micFrameRendered) {
      requestAnimationFrame(frame);
      return;
    }
    dtEma = dtEma * 0.9 + delta * 0.1;
    roomTick(delta);
    adaptive?.update(now, delta * 1000, 1 / dtEma);
    const statsActive = !toolbar.uiMin;
    const disjoint = statsActive ? stats.pollFrame() : false;
    rig.update(delta);
    field.setSectionClip(rig.canPlace());
    grid.visible = !rig.canPlace();
    selPerf.gizmo += runTimed(perfSkip.gizmo, syncGizmoMode);
    pointer.syncGizmoHandles();
    selPerf.ports += runTimed(perfSkip.ports, (): void => ports.updateConnectionOverlay());
    particles.setZLock(rig.zLock());
    particles.setFocalDist(rig.focalDist());
    if (!isTitleActive() && !isLandingActive()) {
      if (stepSimFrame(delta, audio)) {
        requestClockReset();
      }
    }
    sequencerPanel?.updatePlayhead();
    if (paramPanelDirty) {
      paramPanelDirty = false;
      bindPanel();
    }
    if (!perfSkip.locks) { refreshParamLocks(); }
    updateObjectPanelSide();
    pitchLabels.update();
    pitchButtons.update();
    selPerf.labels += runTimed(perfSkip.labels, updateNodeLabels);
    renderer.info.reset();
    if (statsActive && !benchMode) { stats.beginRender(); }
    const cpuStart = performance.now();
    backgroundWall.followCamera(rig.activeCamera);
    if (feedViewportEl !== null) {
      const r = feedViewportEl.getBoundingClientRect();
      setFeedRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      particles.setFeedHeightPx(r.height * renderer.getPixelRatio());
      if (feedCapture !== null && r.width > 0 && r.height > 0) {
        const fullyOnScreen = r.top >= 0 && r.left >= 0 && r.bottom <= window.innerHeight && r.right <= window.innerWidth;
        const cap = feedCapture;
        feedCapture = null;
        if (!fullyOnScreen) {
          cap.resolve(null);
        } else {
          const prevVisible = particles.points.visible;
          particles.points.visible = false;
          render(rig.activeCamera);
          const dataUrl = captureFeedRect(r);
          particles.points.visible = prevVisible;
          cap.resolve(dataUrl);
        }
      }
    }
    if (captureRequested) {
      captureRequested = false;
      const prevVisible = particles.points.visible;
      particles.points.visible = false;
      render(rig.activeCamera);
      const dataUrl = captureHiRes ? captureFrame(THUMB_PREVIEW_SCALE, 0.85) : captureFrame();
      particles.points.visible = prevVisible;
      if (!captureHiRes) {
        saveThumb(dataUrl);
      }
      captureCallback?.(dataUrl);
      captureCallback = null;
      captureHiRes = false;
    }
    render(rig.activeCamera);
    const cpuMs = performance.now() - cpuStart;
    if (statsActive && !benchMode) { stats.endRender(); }
    if (feedViewportEl === null) {
      selPerf.css2d += runTimed(perfSkip.labels, (): void => css2dRenderer.render(scene, rig.activeCamera));
    }
    roomAvatarTick();
    if (perfInstrument) {
      selPerf.frames += 1;
      (window as unknown as { __selperf: object }).__selperf = {
        gizmoMs: selPerf.gizmo / selPerf.frames,
        portsMs: selPerf.ports / selPerf.frames,
        labelsMs: selPerf.labels / selPerf.frames,
        css2dMs: selPerf.css2d / selPerf.frames,
        frames: selPerf.frames,
      };
      (window as unknown as { __contacts: { nonZero: number; readbackMs: number; downstreamMs: number } }).__contacts = {
        nonZero: lastContactsNonZero,
        readbackMs: lastReadbackMs,
        downstreamMs: lastDownstreamMs,
      };
    }
    if (statsActive) {
      stats.recordFrame({
        t: now,
        gpuRenderMs: stats.gpuRenderMs,
        gpuSimMs: stats.gpuSimMs,
        readbackMs: lastReadbackMs,
        downstreamMs: lastDownstreamMs,
        cpuMs,
        drawCalls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
        substeps: particles.substepsLastFrame(),
        contactsNonZero: lastContactsNonZero,
        outlineActive: selectionFrames.count() > 0,
        disjoint,
      });
      stats.update(
        now,
        1 / dtEma,
        cpuMs,
        renderer.info.render.calls,
        renderer.info.render.triangles,
        formatVramMb(estimateVramBytes(renderer, scene)),
      );
    }
    if (benchController) {
      const gpuAvailable = statsActive && stats.gpuTimingAvailable;
      const sceneMs = stats.phaseMs(RENDER_PHASE_SCENE);
      const bloomMs = stats.phaseMs(RENDER_PHASE_BLOOM);
      const postMs = stats.phaseMs(RENDER_PHASE_POST);
      const renderTotal = gpuAvailable
        ? (sceneMs ?? 0) + (bloomMs ?? 0) + (postMs ?? 0)
        : null;
      const sample: BenchSample = {
        frameMs: delta * 1000,
        gpuSimMs: gpuAvailable ? stats.gpuSimMs : null,
        gpuRenderMs: renderTotal,
        phase: { bloom: bloomMs, post: postMs },
        readbackMs: lastReadbackMs,
        downstreamMs: lastDownstreamMs,
        disjoint,
      };
      if (benchController.onFrame(now, sample)) {
        benchController = null;
      }
    }
    if (micLoading) {
      micFrameRendered = true;
    }
    requestAnimationFrame(frame);
  }

  function serializeRoomScene(): SceneSerialized {
    return withParamBase((): SceneSerialized => ({
      objects: field.serialize() as Record<string, unknown>[],
      emitters: emitterField.serialize() as Record<string, unknown>[],
      connections: graph.serialize() as Record<string, unknown>[],
      settings: {
        ...roomSyncSettings(settings),
        env: { preset: wallParamsToPreset(backgroundWall.getParams()) },
      } as Record<string, unknown>,
    }));
  }

  function applyRoomSnapshot(snap: SnapshotMsg): void {
    const snapSettings = (snap.settings ?? {}) as Record<string, unknown>;
    applySceneInPlace(
      { objects: snap.objects, emitters: snap.emitters, connections: snap.connections, settings: snapSettings, env: snapSettings.env },
      true,
    );
    if (snap.clockSinceResetMs !== undefined) {
      particles.setClockTimeBase(snap.clockSinceResetMs / 1000);
    }
    if (snap.capacity !== undefined) {
      roomCapacityCached = snap.capacity;
    }
    const scene = snap.provenance ?? null;
    roomSceneFromSnapshot = scene !== null;
    setProvenance(scene === null ? null : provenanceFromRoomScene(scene));
    counterSync.markLegacy(liveCountIds());
    if (snap.counts !== undefined) {
      const resolveOwner = (slot: number): string | null => snap.roster[slot]?.uid ?? null;
      if (counterSync.applySnapshotCounts(snap.counts, resolveOwner)) {
        onCounterOwnershipChange();
      }
    }
    captureActivityBaseline();
  }

  /**
   * Reconstructs a full Provenance from the minimal scene meta a late joiner
   * receives on the room snapshot, so a guest can show the same scene-info chip
   * (title / author) as the host. Engagement counts are unknown to a joiner (they
   * never fetched the source card), so they seed to zero; the like/bookmark
   * controls still resolve live by scene id.
   */
  function provenanceFromRoomScene(scene: RoomSceneMeta): Provenance {
    return {
      meta: {
        id: scene.id,
        owner: scene.owner,
        ownerName: scene.ownerName,
        title: scene.title,
        playCount: 0,
        likeCount: 0,
        bookmarkCount: 0,
        status: "",
        tags: [],
        autoTags: [],
        lockedTags: [],
      },
      micManifest: null,
    };
  }

  /**
   * Applies one remote per-key delta to the live scene without a full reload:
   * the touched object/emitter/connection is replaced (or removed) in place and
   * its dependent engine state reconciled. The room client wraps this in its echo
   * guard, so the persist calls here do not bounce the change back to the room.
   */
  function applyRemoteOp(op: Op): void {
    runSuppressed((): void => {
      signalRuntime.forceParamReapply();
      if (op.kind === "obj") {
        if (op.action === "upsert") {
          const before = field.list().find((o: PlacedObject): boolean => o.id === op.id);
          const wasToggleOn = before?.type === "button" && before.buttonMode === "toggle" && before.buttonOn === true;
          field.updateFromSerialized(op.id, op.value);
          const after = field.list().find((o: PlacedObject): boolean => o.id === op.id);
          const nowToggleOn = after?.type === "button" && after.buttonMode === "toggle" && after.buttonOn === true;
          if (!wasToggleOn && nowToggleOn) {
            widgets.pulse(op.id);
          }
          if (after?.type === "count") {
            counterSync.noteRemoteCounter(op.id);
          }
        } else {
          field.removeById(op.id);
        }
        persistObjects();
        syncTimbreEngine();
        reconcileOrbitClockSync();
        bindPanel();
      } else if (op.kind === "emitter") {
        if (op.action === "upsert") {
          emitterField.updateFromSerialized(op.id, op.value);
        } else {
          emitterField.removeById(op.id);
        }
        syncEmitters();
        bindPanel();
      } else if (op.kind === "conn") {
        if (op.action === "upsert") {
          graph.upsertSerialized(op.value);
        } else {
          graph.removeById(op.id);
        }
        persistConnections();
        bindPanel();
      } else if (op.action === "upsert") {
        applyRoomSettings(op.value);
      }
    });
  }

  /**
   * Disconnects the live room and clears every runtime room flag so the app is
   * back to single-player. Used by the gated-join rejection path (a normal leave
   * still reloads via onLeaveRoom); idempotent so a double close is harmless.
   */
  function teardownRoom(): void {
    if (!roomActive) {
      return;
    }
    roomClientClose();
    roomActive = false;
    activeRoomId = null;
    roomUidOverride = null;
    myAdToken = null;
    roomClientClose = (): void => {};
    roomNotify = (): void => {};
    roomClaimSend = (): void => {};
    roomReleaseSend = (): void => {};
    roomResetSend = (): void => {};
    roomEmoteSend = (): void => {};
    roomKickSend = (): void => {};
    roomKickDoneSend = (): void => {};
    roomActivitySend = (): void => {};
    localRoomJoinAt = 0;
    clearActivityTimers();
    activitySettingsBaseline = null;
    activityLog.clear();
    roomButtonSend = (): void => {};
    roomCountSend = (): void => {};
    roomCountOwnerSend = (): void => {};
    counterSync.reset();
    emoteRadial.close();
    kickRadial.close();
    clearResetFailsafe();
    roomTick = (): void => {};
    roomAvatarTick = (): void => {};
    lastClaimedId = null;
    semiSelectedId = null;
    roomColors.clear();
    roomClaims.clear();
    roomNames.clear();
    playerAvatars.clear();
    roomOwnerUid = "";
    roomIsPublic = false;
    roomNameCached = null;
    roomCapacityCached = null;
    roomSceneFromSnapshot = false;
    toolbar.setRoomGuestLock(false);
    toolbar.setRoomModeLock(false);
    restoreRoomStopOnBlur();
    toolbar.setRoomActive(false);
    applyForeignLock();
    renderTopChips();
    refreshSelectionFrames();
    applyClaimEnforcement();
    refreshGizmo();
    if (import.meta.env.DEV) {
      delete window.__room;
    }
  }

  /**
   * The room DO refused or severed this connection: a gated-room join rejection
   * (anon/unverifiable) or a mid-session bot-detected kick. Tears the room down,
   * restores the visitor's pre-surf scene when the rejection happened mid-surf (a
   * direct ?room= join never replaced the local scene, so there is nothing to
   * restore there), drops the room from the URL, and shows the reason. Every reject
   * reason resolves to a modal + teardown here — none is silently swallowed.
   */
  function onRoomReject(reason: "anon-gate" | "verify-failed" | "bot-detected" | "room-full" | "ad-required" | "kicked" | "cooldown" | "banned", retryAfterMs?: number): void {
    if (reason === "kicked") {
      void revertSessionThenLeave();
      return;
    }
    finishReject(reason, retryAfterMs);
  }

  /**
   * Kicked from the room: roll the whole local session back so the per-key diff
   * sync broadcasts each removal/restore to the other participants, wait one
   * microtask for those revert ops to flush onto the still-open socket (the undo
   * persists coalesce into one queued `markSceneDirty` diff), tell the DO we are
   * done so it closes without waiting out KICK_GRACE_MS, then tear down + surface
   * the reason. Best-effort within the local undo stack — a reload-dropped or
   * peer-overwritten (LWW) edit cannot be reverted (see undoStack docstring).
   */
  async function revertSessionThenLeave(): Promise<void> {
    undoApi.undoAll();
    await Promise.resolve();
    roomKickDoneSend();
    finishReject("kicked");
  }

  function finishReject(reason: "anon-gate" | "verify-failed" | "bot-detected" | "room-full" | "ad-required" | "kicked" | "cooldown" | "banned", retryAfterMs?: number): void {
    const restore = surfReturnScene;
    const restoreProvenance = surfReturnProvenance;
    surfReturnScene = null;
    surfReturnProvenance = null;
    teardownRoom();
    const url = new URL(location.href);
    if (url.searchParams.has("room")) {
      url.searchParams.delete("room");
      url.searchParams.delete("uid");
      history.replaceState(null, "", url.toString());
    }
    if (restore !== null) {
      applySceneInPlace(restore, true);
      setProvenance(restoreProvenance);
      toolbar.setForeignSceneLock(false);
    }
    const message =
      reason === "anon-gate"
        ? t("multiplayer.rejectedAnon")
        : reason === "bot-detected"
          ? t("multiplayer.rejectedBot")
          : reason === "room-full"
            ? t("multiplayer.rejectedFull")
            : reason === "ad-required"
              ? t("multiplayer.rejectedAd")
              : reason === "kicked"
                ? t("multiplayer.rejectedKicked")
                : reason === "cooldown"
                  ? t("multiplayer.rejectedCooldown", { minutes: Math.max(1, Math.ceil((retryAfterMs ?? 0) / 60_000)) })
                  : reason === "banned"
                    ? t("multiplayer.rejectedBanned")
                    : t("multiplayer.rejectedVerify");
    showActionModal<"ok">(t("multiplayer.rejectedTitle"), message, [{ label: t("multiplayer.close"), value: "ok" }], (): void => {});
  }

  /**
   * Reward-ad gate shown to a joiner just before connecting. A stub: pressing the
   * single watch button resolves true immediately. The real ad SDK plays its ad
   * here and resolves true only on a completed view — a dismissed/blocked ad (or
   * the user cancelling) resolves false so the join is abandoned. The fixed proof
   * the caller then presents to the room DO is AD_TOKEN_STUB.
   */
  function requestAdGate(): Promise<boolean> {
    return new Promise<boolean>((resolve: (passed: boolean) => void): void => {
      showActionModal<"watch" | "cancel">(
        t("multiplayer.adGateTitle"),
        t("multiplayer.adGateBody"),
        [
          { label: t("common.cancel"), value: "cancel", subtle: true },
          { label: t("multiplayer.adGateWatch"), value: "watch" },
        ],
        (value: "watch" | "cancel"): void => {
          resolve(value === "watch");
        },
      );
    });
  }

  /**
   * Connects to a room, first making sure this device holds a session: room
   * presence / claims / colors / owner enforcement all key on a stable uid, so a
   * sessionless participant (a viewer who never uploaded or liked) must sign in
   * anonymously before the hello — otherwise its uid is empty and it both collides
   * with every other sessionless participant and is treated as a guest in its own
   * room. The DEV `?uid=` override supplies the uid directly, so auth is skipped
   * there. roomConnecting guards the await window against a double entry. A joiner
   * (`guestJoin`) must clear the reward-ad gate before connecting — both the feed
   * card commit and the `?room=` auto-join funnel through here, so the gate lives
   * at this one entry; the room creator's own start (no `guestJoin`) is never gated.
   */
  async function startRoom(
    roomId: string,
    opts?: {
      public?: boolean;
      name?: string;
      gateAnon?: boolean;
      capacity?: number;
      scene?: RoomSceneMeta | null;
      seed?: boolean;
      guestJoin?: boolean;
    },
  ): Promise<void> {
    if (!MULTIPLAYER_ENABLED) {
      return;
    }
    if (roomActive || roomConnecting) {
      return;
    }
    if (opts?.guestJoin === true && myAdToken === null) {
      const passed = await requestAdGate();
      if (!passed) {
        surfReturnScene = null;
        surfReturnProvenance = null;
        const url = new URL(location.href);
        if (url.searchParams.has("room")) {
          url.searchParams.delete("room");
          url.searchParams.delete("uid");
          history.replaceState(null, "", url.toString());
        }
        return;
      }
      myAdToken = AD_TOKEN_STUB;
    }
    roomConnecting = true;
    roomUidOverride = import.meta.env.DEV ? new URLSearchParams(location.search).get("uid") : null;
    try {
      if (roomUidOverride === null) {
        await ensureRoomUid();
        await refreshMyUid();
        myNameCached = (await authStatus()).name;
      } else {
        myNameCached = roomUidOverride;
      }
    } finally {
      roomConnecting = false;
    }
    if (roomActive) {
      return;
    }
    if (opts?.guestJoin === true) {
      resetSceneForGuestJoin();
    }
    activeRoomId = roomId;
    roomNameCached = opts?.name ?? null;
    roomCapacityCached = opts?.capacity ?? null;
    counterSync.reset();
    counterSync.markLegacy(liveCountIds());
    toolbar.setRoomActive(true);
    const interpTargets = new Map<string, InterpTarget>();
    const interpPos = new THREE.Vector3();
    const interpQuat = new THREE.Quaternion();
    let emitterMpMotionActive = false;
    const selBox = new THREE.Box3();
    const remoteAvatars: AvatarTarget[] = [];
    const remoteBoxPool = new Map<string, THREE.Box3>();
    const roomClient = connectRoom({
      host: import.meta.env.VITE_PARTYKIT_HOST ?? location.host,
      room: roomId,
      uid: (): string => myRoomUid(),
      token: (): string | null => myTokenCached,
      adToken: (): string | null => myAdToken,
      displayName: (): string | null => myNameCached,
      public: opts?.public,
      name: opts?.name,
      gateAnon: opts?.gateAnon,
      capacity: opts?.capacity,
      scene: opts?.scene ?? undefined,
      seed: opts?.seed,
      serializeScene: serializeRoomScene,
      applySnapshot: applyRoomSnapshot,
      applyOp: applyRemoteOp,
      setInterpTarget: (id: string, target: InterpTarget): void => {
        interpTargets.set(id, target);
      },
      clearInterpTarget: (id: string): void => {
        interpTargets.delete(id);
      },
      onPresence: onRoomPresence,
      onReject: onRoomReject,
      onReset: onRoomReset,
      onEmote: (uid: string, emoji: string): void => playerAvatars.playEmote(uid, emoji),
      onActivity: (uid: string, kind: ActivityKind, detail?: string): void => noteActivity(uid, kind, detail),
      onButton: (_uid: string, id: string, phase: "press" | "release"): void => {
        if (phase === "press") {
          widgets.press(id);
        } else {
          widgets.release(id);
        }
        field.setButtonPressed(id, phase === "press");
      },
      onCount: (id: string, owner: string, value: number): void => {
        if (counterSync.receiveCount(id, owner, value)) {
          onCounterOwnershipChange();
        }
      },
      onCountOwner: (id: string, owner: string): void => {
        if (counterSync.setOwner(id, owner)) {
          onCounterOwnershipChange();
        }
      },
    });
    forceRoomStopOnBlurOff();
    roomActive = true;
    localRoomJoinAt = Date.now();
    roomIsPublic = opts?.public === true;
    applyRoomPublicState();
    captureActivityBaseline();
    applyForeignLock();
    renderTopChips();
    roomClientClose = roomClient.close;
    roomClaimSend = roomClient.claim;
    roomReleaseSend = roomClient.release;
    roomResetSend = roomClient.reset;
    roomEmoteSend = roomClient.emote;
    roomKickSend = roomClient.kick;
    roomKickDoneSend = roomClient.kickDone;
    roomActivitySend = roomClient.activity;
    roomButtonSend = roomClient.button;
    roomCountSend = roomClient.countValue;
    roomCountOwnerSend = roomClient.countOwner;
    roomNotify = roomClient.markSceneDirty;
    if (import.meta.env.DEV) {
      window.__room = {
        uid: (): string => myRoomUid(),
        claims: (): Record<string, string> => Object.fromEntries(roomClaims),
        colors: (): Record<string, number> => Object.fromEntries(roomColors),
        selectedId: (): string | null => currentSelectedId(),
        foreignSelected: (): boolean => {
          const id = currentSelectedId();
          return id !== null && isForeignClaimed(id);
        },
        dragStep: (id: string, x: number, y: number, z: number): void => {
          field.moveById(id, new THREE.Vector3(x, y, z));
        },
        emitterDragStep: (id: string, x: number, y: number, z: number): void => {
          emitterField.moveById(id, new THREE.Vector3(x, y, z));
        },
        pokeKnob: (id: string, value: number): void => {
          const p = settableParams(id).find((q: NumericParam): boolean => q.name === "value");
          if (p) {
            p.set(value);
          }
        },
        paramValue: (id: string, name: string): number | null => {
          const p = settableParams(id).find((q: NumericParam): boolean => q.name === name);
          return p ? p.get() : null;
        },
        dragEnd: (): void => {
          persistObjects();
        },
        meshPos: (id: string): [number, number, number] | null => {
          const mesh = field.meshById(id) ?? emitterField.meshById(id);
          return mesh === null ? null : [mesh.position.x, mesh.position.y, mesh.position.z];
        },
        colliderPos: (id: string): [number, number, number] | null => {
          const pos = field.liveOrBasePosition(id);
          return pos === null ? null : [pos.x, pos.y, pos.z];
        },
        emitterOriginPos: (id: string): [number, number, number] | null => {
          const pos = emitterField.liveOrBasePosition(id);
          return pos === null ? null : [pos.x, pos.y, pos.z];
        },
        seqValues: (id: string): number[] | null => {
          const o = field.list().find((p: PlacedObject): boolean => p.id === id);
          return o ? [...o.seqValues] : null;
        },
        button: (id: string, phase: "press" | "release"): void => {
          if (phase === "press") {
            widgets.press(id);
          } else {
            widgets.release(id);
          }
          field.setButtonPressed(id, phase === "press");
          roomButtonSend(id, phase);
        },
        kick: (targetUid: string): void => roomKickSend(targetUid),
        isHeld: (id: string): boolean => widgets.isHeld(id),
        clockTimeSec: (): number => particles.clockTimeSec(),
        lampLevel: (id: string): number | null => {
          const mesh = field.meshById(id);
          const dome = mesh?.userData.lampDome as THREE.Mesh | undefined;
          if (!dome) {
            return null;
          }
          return (dome.material as THREE.MeshStandardMaterial).emissiveIntensity;
        },
        semiSelectedId: (): string | null => semiSelectedId,
        countOwner: (id: string): string | null => counterSync.ownerOf(id),
        screenOf: (id: string): [number, number] | null => {
          const mesh = field.meshById(id) ?? emitterField.meshById(id);
          if (mesh === null) {
            return null;
          }
          const v = new THREE.Vector3();
          mesh.getWorldPosition(v);
          v.project(rig.activeCamera);
          return [(v.x * 0.5 + 0.5) * window.innerWidth, (-v.y * 0.5 + 0.5) * window.innerHeight];
        },
      };
    }
    roomTick = (dt: number): void => {
      const alpha = 1 - Math.exp(-dt / ROOM_INTERP_TAU_S);
      let emitterMoved = false;
      for (const [id, target] of interpTargets) {
        const mesh = target.kind === "emitter" ? emitterField.meshById(id) : field.meshById(id);
        if (mesh === null) {
          interpTargets.delete(id);
          continue;
        }
        interpPos.set(target.x, target.y, target.z);
        interpQuat.set(target.qx, target.qy, target.qz, target.qw);
        mesh.position.lerp(interpPos, alpha);
        mesh.quaternion.slerp(interpQuat, alpha);
        if (target.kind === "obj") {
          field.setLivePose(id, mesh.position, mesh.quaternion);
        } else {
          emitterField.setLivePose(id, mesh.position, mesh.quaternion);
          emitterMoved = true;
        }
      }
      if (emitterMoved || emitterMpMotionActive) {
        particles.setEmitters(emitterField.liveList());
      }
      emitterMpMotionActive = emitterMoved;
      roomClient.tickTransient();
    };
    /**
     * Runs after the scene render, when every object's matrixWorld is final for the
     * frame, so an avatar measured from its object's box never lags a frame behind a
     * live drag (which the earlier roomTick-time measurement did, most visibly for
     * groups whose box spans many moving children).
     */
    roomAvatarTick = (): void => {
      const self = myRoomUid();
      remoteAvatars.length = 0;
      for (const [objId, holder] of roomClaims) {
        if (holder === self) {
          continue;
        }
        const mesh = field.meshById(objId) ?? emitterField.meshById(objId);
        if (mesh !== null) {
          let box = remoteBoxPool.get(holder);
          if (box === undefined) {
            box = new THREE.Box3();
            remoteBoxPool.set(holder, box);
          }
          box.setFromObject(mesh);
          remoteAvatars.push({ uid: holder, color: colorCssForUid(holder), name: roomNames.get(holder) ?? "", box });
        }
      }
      const selId = currentSelectedId() ?? semiSelectedId;
      const selMesh = selId !== null && !isForeignClaimed(selId) ? field.meshById(selId) ?? emitterField.meshById(selId) : null;
      const selfOccupiedBox = selMesh !== null ? selBox.setFromObject(selMesh) : null;
      playerAvatars.update({
        camera: rig.activeCamera,
        remotes: remoteAvatars,
        selfUid: self,
        selfColor: colorCssForUid(self),
        selfName: myNameCached ?? "",
        selfOccupiedBox,
      });
    };
  }

  /**
   * The http(s) origin of the partyserver worker for one-shot directory / peek
   * GETs. Production serves assets and parties on one origin (location.host); dev
   * points VITE_PARTYKIT_HOST at the wrangler dev server, which is plain http.
   */
  function partyHttpBase(): string {
    const host = import.meta.env.VITE_PARTYKIT_HOST ?? location.host;
    const insecure = host.startsWith("localhost") || host.startsWith("127.0.0.1");
    const proto = insecure ? "http:" : location.protocol === "https:" ? "https:" : "http:";
    return `${proto}//${host}`;
  }

  function snapshotEmpty(snap: SnapshotMsg): boolean {
    return (
      snap.objects.length === 0 &&
      snap.emitters.length === 0 &&
      snap.connections.length === 0 &&
      snap.settings === null
    );
  }

  async function peekRoom(id: string): Promise<SnapshotMsg | null> {
    try {
      const res = await fetch(`${partyHttpBase()}/parties/room/${id}`);
      if (!res.ok) {
        return null;
      }
      const data = (await res.json()) as SnapshotMsg;
      return data.t === "snapshot" ? data : null;
    } catch {
      return null;
    }
  }

  function createFeedPingAntennaSvg(): SVGSVGElement {
    const SVG_NS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "feed-ping-antenna");
    svg.setAttribute("viewBox", "0 0 14 12");
    svg.setAttribute("width", "12");
    svg.setAttribute("height", "12");
    svg.setAttribute("aria-hidden", "true");
    const bars: ReadonlyArray<{ x: number; y: number; w: number; h: number }> = [
      { x: 0, y: 7, w: 3, h: 5 },
      { x: 4, y: 4, w: 3, h: 8 },
      { x: 8, y: 0, w: 3, h: 12 },
    ];
    for (const bar of bars) {
      const rect = document.createElementNS(SVG_NS, "rect");
      rect.setAttribute("x", String(bar.x));
      rect.setAttribute("y", String(bar.y));
      rect.setAttribute("width", String(bar.w));
      rect.setAttribute("height", String(bar.h));
      rect.setAttribute("fill", "currentColor");
      svg.appendChild(rect);
    }
    return svg;
  }

  async function pingRoomRttMs(id: string): Promise<number | null> {
    const startAt = performance.now();
    try {
      const res = await fetch(`${partyHttpBase()}/parties/room/${id}?ping`, { cache: "no-store" });
      if (!res.ok) {
        return null;
      }
      return performance.now() - startAt;
    } catch {
      return null;
    }
  }

  async function browsePublic(): Promise<void> {
    if (roomActive) {
      return;
    }
    let pool: RoomDescriptor[] = [];
    try {
      const res = await fetch(`${partyHttpBase()}/parties/directory/main`);
      const data = (await res.json()) as DirectoryList;
      pool = Array.isArray(data.rooms) ? data.rooms : [];
    } catch {
      pool = [];
    }
    openRoomsFeed(pool);
  }

  /**
   * Orders the pool by interleaving a busy stream (occupancy desc) and a recent
   * stream (createdAt desc): each step picks the next not-yet-placed room from one
   * stream chosen with equal probability, falling back to the other stream once one
   * is exhausted. This weights both liveliness and freshness without the previous
   * fully-random shuffle.
   */
  function interleavePool(pool: RoomDescriptor[]): RoomDescriptor[] {
    const busy = [...pool].sort((a: RoomDescriptor, b: RoomDescriptor): number => b.count - a.count);
    const recent = [...pool].sort((a: RoomDescriptor, b: RoomDescriptor): number => b.createdAt - a.createdAt);
    const seen = new Set<string>();
    const out: RoomDescriptor[] = [];
    const advance = (arr: RoomDescriptor[], from: number): number => {
      let i = from;
      while (i < arr.length && seen.has(arr[i].id)) {
        i++;
      }
      return i;
    };
    let bi = 0;
    let ri = 0;
    while (out.length < pool.length) {
      bi = advance(busy, bi);
      ri = advance(recent, ri);
      const fromBusy = ri >= recent.length ? true : bi >= busy.length ? false : Math.random() < 0.5;
      const pick = fromBusy ? busy[bi] : recent[ri];
      if (pick === undefined) {
        break;
      }
      seen.add(pick.id);
      out.push(pick);
    }
    return out;
  }

  /**
   * Opens the public rooms as the second consumer of the feed core: each card peeks
   * the room's live scene read-only (no join, no seed), shows the room name + current
   * occupancy, and joins the room (live, seed:false) when the card is committed.
   * onEnter stashes the visitor's local scene and locks editing; onExit restores it;
   * a join keeps the room's scene instead. Index/search/count chrome is off.
   */
  function openRoomsFeed(descriptors: RoomDescriptor[]): void {
    const CREATE_PUBLIC_ROOM_ITEM_ID = "__create_public_room__";
    type RoomFeedItem = FeedCoreItem & {
      kind: "room";
      count: number;
      capacity: number;
      gateAnon: boolean;
      rttMs: number | null;
    };
    type CreateFeedItem = FeedCoreItem & {
      kind: "create";
      autoName: string;
      cardLabel: string;
      disabledReason: string | null;
    };
    type RoomItem = RoomFeedItem | CreateFeedItem;
    let stash: Record<string, unknown> | null = null;
    let stashProvenance: Provenance | null = null;
    openFeed<RoomItem>((api: FeedCoreApi<RoomItem>): FeedConsumer<RoomItem> => {
      const closeBtn = document.createElement("button");
      closeBtn.className = "rooms-feed-close icon-btn icon-btn-square";
      closeBtn.textContent = "✕";
      closeBtn.title = t("multiplayer.surfBack");
      closeBtn.addEventListener("click", (): void => api.close());

      function buildCardInfo(item: RoomItem): HTMLElement {
        const info = document.createElement("div");
        info.className = "feed-card-info";
        const main = document.createElement("div");
        main.className = "feed-card-info-main";
        const meta = document.createElement("div");
        meta.className = "feed-card-meta";
        if (item.kind === "create") {
          info.classList.add("feed-card-create-info");
          if (item.disabledReason !== null) {
            const hint = document.createElement("span");
            hint.className = "feed-card-occupancy";
            hint.textContent = item.disabledReason;
            meta.appendChild(hint);
          }
          if (meta.childElementCount > 0) {
            main.appendChild(meta);
          }
          info.appendChild(main);
          return info;
        } else {
          const title = document.createElement("div");
          title.className = "feed-card-title";
          title.textContent = item.title;
          const occ = document.createElement("span");
          occ.className = "feed-card-occupancy";
          occ.textContent = t("multiplayer.occupancyCap", { count: item.count, cap: item.capacity });
          meta.appendChild(occ);
          if (item.rttMs !== null) {
            const latency = document.createElement("span");
            latency.className = "feed-card-occupancy";
            latency.appendChild(createFeedPingAntennaSvg());
            latency.appendChild(document.createTextNode(` ${Math.round(item.rttMs)}ms`));
            meta.append(" ", latency);
          }
          if (!item.gateAnon) {
            const anonTag = document.createElement("span");
            anonTag.className = "tag-chip tag-chip-fixed";
            anonTag.textContent = t("multiplayer.anonEntryOk");
            meta.appendChild(anonTag);
          }
          main.append(title, meta);
          info.appendChild(main);
          return info;
        }
      }

      function buildCardStageOverlay(item: RoomItem): HTMLElement | null {
        if (item.kind !== "create") {
          return null;
        }
        const wrap = document.createElement("div");
        wrap.className = "feed-card-create-center";
        if (item.disabledReason !== null) {
          wrap.classList.add("feed-card-create-center-disabled");
        }
        const plus = document.createElement("div");
        plus.className = "feed-card-create-plus";
        plus.textContent = "+";
        const label = document.createElement("div");
        label.className = "feed-card-create-label";
        label.textContent = item.cardLabel;
        wrap.append(plus, label);
        return wrap;
      }

      function buildIndexRow(item: RoomItem): HTMLButtonElement {
        const row = document.createElement("button");
        row.className = "feed-index-row";
        row.textContent = item.title;
        return row;
      }

      async function loadItems(): Promise<FeedLoadResult<RoomItem>> {
        const ordered = interleavePool(descriptors);
        const publicCount = Math.min(descriptors.length, PUBLIC_ROOM_CAP);
        const rttByIndex = await Promise.all(ordered.map((r: RoomDescriptor): Promise<number | null> => pingRoomRttMs(r.id)));
        const items: RoomItem[] = ordered.map((r: RoomDescriptor, i: number): RoomItem => ({
          id: r.id,
          title: r.name.trim() === "" ? t("multiplayer.untitledRoom") : r.name,
          owner: "",
          ownerName: null,
          mine: false,
          status: "",
          kind: "room",
          count: r.count,
          capacity: r.capacity,
          gateAnon: r.gateAnon === true,
          rttMs: rttByIndex[i] ?? null,
        }));
        const createDisabledReason =
          !myIsRealCached
            ? t("multiplayer.createSignInNote")
            : descriptors.length >= PUBLIC_ROOM_CAP
              ? t("multiplayer.publicRoomCapReached", { cap: PUBLIC_ROOM_CAP })
              : null;
        items.push({
          id: CREATE_PUBLIC_ROOM_ITEM_ID,
          title: t("multiplayer.createPublicCardTitle"),
          owner: "",
          ownerName: null,
          mine: true,
          status: "",
          kind: "create",
          autoName: autoPublicRoomName(publicCount + 1),
          cardLabel: t("multiplayer.createPublicCardLabel", { count: publicCount, cap: PUBLIC_ROOM_CAP }),
          disabledReason: createDisabledReason,
        });
        return { items };
      }

      async function fetchScene(id: string): Promise<FeedSceneResult> {
        if (id === CREATE_PUBLIC_ROOM_ITEM_ID) {
          return { scene: stash ?? sceneBundle() };
        }
        const snap = await peekRoom(id);
        if (snap === null || snapshotEmpty(snap)) {
          return { error: t("multiplayer.roomGone") };
        }
        return { scene: { objects: snap.objects, emitters: snap.emitters, connections: snap.connections, settings: snap.settings ?? {} } };
      }

      return {
        title: t("multiplayer.surfTitle"),
        chrome: { search: false, index: false, count: false },
        searchControls: null,
        strings: {
          loading: t("multiplayer.surfLoading"),
          empty: t("multiplayer.surfEmpty"),
          loadFailed: t("multiplayer.roomGone"),
          indexToggleAria: "",
          indexCount: (): string => "",
        },
        onEnter: (): void => {
          stash = sceneBundle();
          stashProvenance = currentProvenance;
          document.querySelector(".top-controls")?.prepend(closeBtn);
          document.body.classList.add("feed-open");
          renderTopChips();
          css2dRenderer.domElement.style.display = "none";
          setFeedActive(true);
          backgroundWall.prebakeAll();
          rig.suspendControls();
        },
        onExit: (): void => {
          if (stash !== null) {
            applySceneInPlace(stash, true);
            stash = null;
          }
          setProvenance(stashProvenance);
          stashProvenance = null;
          toolbar.setForeignSceneLock(false);
        },
        onClose: (): void => {
          closeBtn.remove();
          feedViewportEl = null;
          setFeedActive(false);
          particles.setFeedHeightPx(null);
          document.body.classList.remove("feed-open");
          css2dRenderer.domElement.style.display = "";
          rig.resumeControls();
        },
        loadItems,
        fetchScene,
        applySceneInPlace: (scene: unknown): string | null => {
          const err = applySceneInPlace(scene, false);
          if (err === null) {
            toolbar.setForeignSceneLock(true);
          }
          return err;
        },
        setViewport: (stage: HTMLElement | null): void => {
          feedViewportEl = stage;
          if (stage === null) {
            setFeedRect(null);
            particles.setFeedHeightPx(null);
            if (feedCapture !== null) {
              feedCapture.resolve(null);
              feedCapture = null;
            }
          }
        },
        capturePreview: (id: string): Promise<string | null> =>
          new Promise((resolve: (dataUrl: string | null) => void): void => {
            if (feedCapture !== null) {
              feedCapture.resolve(null);
            }
            feedCapture = { id, resolve };
          }),
        buildCardStageOverlay,
        cardClassName: (item: RoomItem): string | null => (item.kind === "create" ? "feed-card-create-action" : null),
        buildCardInfo,
        buildIndexRow,
        guardCommit: (item: RoomItem): string | null => {
          if (item.kind === "create") {
            return item.disabledReason;
          }
          return item.gateAnon && !myIsRealCached ? t("multiplayer.signInToJoin") : null;
        },
        onCommit: (item: RoomItem): void => {
          if (item.kind === "create") {
            toolbar.setForeignSceneLock(false);
            resetSceneToBlankInPlace();
            const roomId = newRoomId();
            const shareUrl = roomShareUrl(roomId);
            const barUrl = new URL(location.href);
            barUrl.searchParams.set("room", roomId);
            history.replaceState(null, "", barUrl.toString());
            void startRoom(roomId, {
              public: true,
              name: item.autoName,
              gateAnon: false,
              capacity: DEFAULT_ROOM_CAPACITY,
              scene: null,
            });
            if (navigator.clipboard) {
              navigator.clipboard.writeText(shareUrl).catch((): void => {});
            }
            showRoomShareModal(shareUrl);
            return;
          }
          toolbar.setForeignSceneLock(false);
          surfReturnScene = stash;
          surfReturnProvenance = stashProvenance;
          const barUrl = new URL(location.href);
          barUrl.searchParams.set("room", item.id);
          history.replaceState(null, "", barUrl.toString());
          void startRoom(item.id, { seed: false, name: item.title, guestJoin: true });
        },
      };
    });
  }

  const initialRoomId = MULTIPLAYER_ENABLED ? new URLSearchParams(location.search).get("room") : null;
  if (initialRoomId !== null && initialRoomId !== "") {
    void startRoom(initialRoomId, { guestJoin: true });
  }

  requestAnimationFrame(frame);
}

/**
 * Validate a parsed scene object and commit it to localStorage, then reload so
 * the app boots from the imported state. Shared by textarea Import and Import
 * Default. Returns an error string on a malformed scene, null on success
 * (success never returns — the reload tears down the page). The features
 * category toggles are scene content (they gate node/force behavior, so the
 * scene's intended performance needs them); a legacy scene without them keeps
 * the importer's current values.
 */
function persistScene(scene: Record<string, unknown>): void {
  saveObjects(scene.objects);
  saveEmitters(scene.emitters);
  saveConnections(Array.isArray(scene.connections) ? scene.connections : []);
  saveSettings(applySceneSettings(loadSettings(), scene.settings));
  saveEnv(parseWallParams(scene.env) ?? { ...WALL_DEFAULT_PARAMS });
  savePendingCamera(scene.camera);
}

/** Whether any object in a serialized object array plays the `mic` timbre, so the scene must carry a micManifest. */
function sceneUsesMic(objects: unknown): boolean {
  return Array.isArray(objects) && objects.some((o: unknown): boolean => typeof o === "object" && o !== null && (o as Record<string, unknown>).timbreId === "mic");
}

const HEX64_RE = /^[0-9a-f]{64}$/;

/** Validates a scene's optional micManifest into well-formed entries, dropping malformed ones (an importer must not act on garbage hashes). */
function parseMicManifest(raw: unknown): MicManifestEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: MicManifestEntry[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) {
      continue;
    }
    const o = entry as Record<string, unknown>;
    if (
      typeof o.hash === "string" &&
      HEX64_RE.test(o.hash) &&
      typeof o.name === "string" &&
      typeof o.order === "number" &&
      typeof o.mime === "string" &&
      typeof o.durationMs === "number"
    ) {
      out.push({
        hash: o.hash,
        name: o.name,
        order: o.order,
        mime: o.mime,
        durationMs: o.durationMs,
        ...(typeof o.trimStart === "number" && typeof o.trimEnd === "number" ? { trimStart: o.trimStart, trimEnd: o.trimEnd } : {}),
      });
    }
  }
  return out;
}

/**
 * The author identity of the scene currently loaded into the running engine.
 * Null means this device's own scene (own recordings, no attribution). A non-null
 * value is a Community scene kept or focused in-place: it carries the author so
 * the right Layer B kit plays, the info chip stays pinned, and re-uploading
 * someone else's scene under your own name is refused. It is never serialized
 * into the scene JSON (forgeable + the upload Edge Function overwrites owner with
 * auth.uid()); it lives only at runtime.
 */
type Provenance = { meta: SceneListItem; micManifest: unknown };

/**
 * Rebuilds a Provenance from its persisted form (saved on keep, read at boot),
 * validating the fields the guards and chip depend on; returns null if the record
 * is malformed so a corrupt entry degrades to "own scene" rather than throwing.
 * Count / tag fields default since only owner identity drives the guards.
 */
function provenanceFromStored(rec: { meta: Record<string, unknown>; micManifest: unknown }): Provenance | null {
  const m = rec.meta;
  if (typeof m.owner !== "string" || typeof m.id !== "string" || typeof m.title !== "string") {
    return null;
  }
  const meta: SceneListItem = {
    id: m.id,
    owner: m.owner,
    ownerName: typeof m.ownerName === "string" ? m.ownerName : null,
    title: m.title,
    playCount: typeof m.playCount === "number" ? m.playCount : 0,
    likeCount: typeof m.likeCount === "number" ? m.likeCount : 0,
    bookmarkCount: typeof m.bookmarkCount === "number" ? m.bookmarkCount : 0,
    status: typeof m.status === "string" ? m.status : "",
    tags: Array.isArray(m.tags) ? (m.tags as string[]) : [],
    autoTags: Array.isArray(m.autoTags) ? (m.autoTags as string[]) : [],
    lockedTags: Array.isArray(m.lockedTags) ? (m.lockedTags as string[]) : [],
  };
  return { meta, micManifest: rec.micManifest };
}

/**
 * Switches the mic playback source for a freshly applied scene: an imported
 * Community scene (a manifest plus its author `owner`, with R2 configured) has
 * its recordings fetched from the author's R2 objects into Layer B and activated;
 * anything else returns to this device's own recordings (Layer A). A manifest
 * present but unresolvable (no owner / no R2 base) is logged and left silent
 * rather than substituting this device's recordings (`.claude/rules/ts.md`
 * fallback ban — an imported scene must never sound with the wrong audio).
 *
 * `shouldActivate` is the latest-wins gate, re-checked after the (slow) fetch and
 * before the (instant) activation: an in-place feed focus the user scrolled past
 * resolves into a stale epoch and must not clobber the layer the current focus
 * wants. Non-feed callers (boot, keep, exit) leave it at the always-true default.
 */
async function applySceneMic(manifestRaw: unknown, owner: string | null, shouldActivate: () => boolean = (): boolean => true): Promise<void> {
  const manifest = parseMicManifest(manifestRaw);
  if (manifest.length === 0 || owner === null || !r2Configured()) {
    if (shouldActivate()) {
      micStore.setActiveManifest(null);
    }
    if (manifest.length > 0 && (owner === null || !r2Configured())) {
      console.error("mic import skipped: missing scene owner or VITE_R2_PUBLIC_BASE");
    }
    return;
  }
  await micStore.resolveManifest(manifest, (e: MicManifestEntry): string => {
    const url = micPublicUrl(micStoragePath(owner, e.hash));
    if (url === null) {
      throw new Error("R2 public base unset");
    }
    return url;
  });
  if (!shouldActivate()) {
    return;
  }
  micStore.setActiveManifest(manifest);
}

function formatMB(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}

/**
 * Uploads the mic recordings a scene about to be posted depends on, before the
 * scene itself, so a Community import can fetch them. Returns null to proceed
 * with the scene upload (no mic, all recordings already in the cloud, or upload
 * succeeded), or an early UploadResult that blocks the post: a quota overflow
 * (reported with current usage) or a missing local blob. Recordings already
 * stored for this owner are skipped client-side; the Edge Function re-dedups and
 * enforces the 32-clip / 8 MB quota authoritatively.
 */
async function uploadSceneMicAssets(manifestRaw: unknown, signal?: AbortSignal): Promise<UploadResult | null> {
  const manifest = parseMicManifest(manifestRaw);
  if (manifest.length === 0) {
    return null;
  }
  const existing = await listMicHashes();
  const toUpload: MicAssetInput[] = [];
  for (const e of manifest) {
    if (existing.has(e.hash)) {
      continue;
    }
    const blob = await micStore.blobForHash(e.hash);
    if (blob === null) {
      return { kind: "error", message: t("mic.audioMissing") };
    }
    toUpload.push({ hash: e.hash, mime: e.mime, durationMs: e.durationMs, name: e.name, blob });
  }
  if (toUpload.length === 0) {
    return null;
  }
  const res = await uploadMicAssets(toUpload, signal);
  if (res.kind === "ok") {
    return null;
  }
  if (res.kind === "needs-signin") {
    return { kind: "needs-signin" };
  }
  if (res.kind === "quota") {
    return {
      kind: "reject",
      reason: t("mic.quotaExceeded", {
        count: String(res.used.count + res.adding.count),
        maxCount: String(res.max.count),
        mb: formatMB(res.used.bytes + res.adding.bytes),
        maxMb: formatMB(res.max.bytes),
      }),
    };
  }
  return { kind: "error", message: res.message };
}

function validScene(parsed: unknown): Record<string, unknown> | null {
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const scene = parsed as Record<string, unknown>;
  if (!Array.isArray(scene.objects) || !Array.isArray(scene.emitters) || typeof scene.settings !== "object" || scene.settings === null) {
    return null;
  }
  return scene;
}

function isFiniteTriple(v: unknown): v is [number, number, number] {
  return Array.isArray(v) && v.length === 3 && v.every((n: unknown): boolean => typeof n === "number" && Number.isFinite(n));
}

/** Validates an untrusted scene's optional `camera` field into a CameraPose, or null when absent / malformed (legacy scenes carry no camera). */
function parseCameraPose(raw: unknown): CameraPose | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const c = raw as Record<string, unknown>;
  if (c.mode !== "2d" && c.mode !== "3d") {
    return null;
  }
  const persp = c.persp as Record<string, unknown> | null;
  const ortho = c.ortho as Record<string, unknown> | null;
  if (typeof persp !== "object" || persp === null || typeof ortho !== "object" || ortho === null) {
    return null;
  }
  if (!isFiniteTriple(persp.position) || !isFiniteTriple(persp.target) || !isFiniteTriple(ortho.position) || !isFiniteTriple(ortho.target) || typeof ortho.zoom !== "number" || !Number.isFinite(ortho.zoom)) {
    return null;
  }
  return {
    mode: c.mode,
    persp: { position: persp.position, target: persp.target },
    ortho: { position: ortho.position, target: ortho.target, zoom: ortho.zoom },
  };
}

function applyImportedScene(parsed: unknown): string | null {
  const scene = validScene(parsed);
  if (scene === null) {
    return "invalid scene: missing objects / emitters / settings";
  }
  persistScene(scene);
  saveMicImport(null);
  saveProvenance(null);
  markSkipTitleOnce();
  location.reload();
  return null;
}

const SHARE_PARAM = "scene";

/**
 * A URL-safe room id from crypto random bytes (base36, no Math.random fallback).
 * The room DO is lazily created on first connect, so any unused id is valid and
 * collision probability at these lengths is negligible.
 */
function newRoomId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  let s = "";
  for (const b of bytes) {
    s += b.toString(36).padStart(2, "0");
  }
  return s.slice(0, 12);
}

function autoPublicRoomName(sequence: number): string {
  return t("multiplayer.publicRoomAutoName", { num: sequence });
}

/** A clean share URL carrying only `?room=<id>`, dropping work params (notutorial / bench / scene / uid). */
function roomShareUrl(roomId: string): string {
  const url = new URL(location.origin + location.pathname);
  url.searchParams.set("room", roomId);
  return url.toString();
}

function showRoomShareModal(url: string): void {
  showUploadSuccessModal({
    title: t("multiplayer.urlTitle"),
    urlLabel: t("multiplayer.urlLabel"),
    url,
    copyLabel: t("multiplayer.copy"),
    copiedLabel: t("multiplayer.copied"),
    closeLabel: t("multiplayer.close"),
    hideUrlLabel: true,
    hideCloseButton: true,
    dismissOnOverlayClick: true,
  });
}

function stripSceneParam(): void {
  const url = new URL(location.href);
  url.searchParams.delete(SHARE_PARAM);
  history.replaceState(null, "", url.toString());
}

function autoStashStamp(): string {
  const now = new Date();
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}`;
}

/**
 * When the page opens with ?scene=<id>, fetch the shared scene and commit it to
 * localStorage, but first stash the visitor's current scene into an auto slot so
 * their work is never destroyed. The URL param is removed before reload so the
 * fetch does not loop, and play count is bumped once per session per scene.
 * Returns true when a reload is pending (the caller must stop normal boot).
 */
async function loadSharedSceneIfRequested(): Promise<boolean> {
  const id = new URLSearchParams(location.search).get(SHARE_PARAM);
  if (id === null) {
    return false;
  }
  if (!shareEnabled()) {
    stripSceneParam();
    return false;
  }
  const result = await fetchScene(id);
  if ("error" in result) {
    stripSceneParam();
    return false;
  }
  const scene = validScene(result.scene);
  if (scene === null) {
    stripSceneParam();
    return false;
  }
  if (hasSavedScene()) {
    saveSlot(
      `(auto-stash ${autoStashStamp()})`,
      {
        objects: loadObjects(),
        emitters: loadEmitters(),
        connections: loadConnections(),
        settings: sceneSettings(loadSettings()),
      },
      loadThumb()?.dataUrl,
    );
  }
  persistScene(scene);
  saveProvenance(null);
  const playedKey = `resonella.played.${id}`;
  if (sessionStorage.getItem(playedKey) === null) {
    sessionStorage.setItem(playedKey, "1");
    incrementPlay(id);
  }
  stripSceneParam();
  markSkipTitleOnce();
  location.reload();
  return true;
}

const BENCH_FIXTURE_RE = /^[a-z0-9_-]+$/;

interface PerfSkip {
  gizmo: boolean;
  ports: boolean;
  labels: boolean;
  outline: boolean;
  locks: boolean;
}

type ReadbackTestMode = "full" | "noread" | "nodown" | "off";

/**
 * Parses ?rbmode into a contact-readback isolation lever for the mobile perf
 * split: full = readPixels + downstream consumption (production), noread = skip
 * the readPixels GPU sync but keep downstream (isolates the tiler flush cost),
 * nodown = pay readPixels but no-op downstream (isolates audio/glow/signal CPU),
 * off = neither. Anything else falls to full. Pairs with the downstreamMs timer
 * to attribute the per-frame cost between the GPU flush and the CPU consumers.
 */
function parseReadbackTestMode(raw: string | null): ReadbackTestMode {
  if (raw === "noread" || raw === "nodown" || raw === "off") {
    return raw;
  }
  return "full";
}

/**
 * Parses ?noperf=<csv> into per-element skip flags so each select-time UI cost
 * (gizmo sync/orient + group meshes, PORTS overlay reflow, node labels + CSS2D,
 * selection frames, refreshParamLocks) can be no-op'd independently for per-frame
 * cost attribution. Driven from the bench runner's --noperf flag.
 */
function parsePerfSkip(raw: string | null): PerfSkip {
  const set = new Set((raw ?? "").split(",").map((s: string): string => s.trim()).filter(Boolean));
  return {
    gizmo: set.has("gizmo"),
    ports: set.has("ports"),
    labels: set.has("labels"),
    outline: set.has("outline"),
    locks: set.has("locks"),
  };
}

/**
 * When the page opens with ?bench=<fixture>, fetch public/bench/<fixture>.json
 * and hand it to the scene loaders as a non-persistent override so the run uses
 * a fixed, deterministic scene without writing localStorage. The fixture name is
 * restricted to a safe charset (no path traversal). A missing / malformed file
 * leaves the override unset, so boot falls through to the normal saved scene.
 */
async function loadBenchFixtureIfRequested(): Promise<void> {
  const fixture = new URLSearchParams(location.search).get("bench");
  if (fixture === null || !BENCH_FIXTURE_RE.test(fixture)) {
    return;
  }
  let parsed: unknown;
  try {
    const res = await fetch(`bench/${fixture}.json`);
    if (!res.ok) {
      return;
    }
    parsed = await res.json();
  } catch {
    return;
  }
  const scene = validScene(parsed);
  if (scene === null) {
    return;
  }
  setBenchSceneOverride(scene);
}

let titleGate: Promise<void> = Promise.resolve();

async function bootstrap(): Promise<void> {
  const bootParams = new URLSearchParams(location.search);
  const titleSuppressed = shouldSuppressTitle(bootParams) || consumeSkipTitleOnce();
  if (titleSuppressed) {
    removeLanding();
  } else {
    applyLandingLanguage(loadSettings().language);
    preloadTitleFont();
  }
  if (await loadSharedSceneIfRequested()) {
    return;
  }
  await loadBenchFixtureIfRequested();
  if (titleSuppressed) {
    titleGate = Promise.resolve();
  } else {
    titleGate = showLanding()
      .then((): Promise<void> => runTitleScreen({ immediate: true, onReady: removeLanding }))
      .finally(removeLanding);
  }
  main();
}

bootstrap();
